import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { socketService } from "../../../services/socket";
import { useProjects } from "../../common/contexts/ProjectContext";
import { useProjectNavigation } from "../../../hooks/useProjectNavigation";
import { useAuth } from "../../auth/contexts/AuthContext";
import { useTags } from "../../common/contexts/TagsContext";
import { useSelection, SelectableItem } from "../../common/contexts/SelectionContext";
import AssemblyManager, { type AssemblyManagerRef } from "../components/AssemblyManager";
const Discovery = React.lazy(() =>
  import("../../discovery/views/Discovery").then((m) => ({
    default: m.Discovery,
  })),
);
import { FullscreenButton } from "../../common/components/FullscreenButton";
import { BugReportButton } from "../../common/components/BugReportButton";
import { LanguageToggle } from "../../common/components/LanguageToggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DownloadIcon } from "lucide-react";
import { ConnectionStatusWidget } from "../../common/components/ConnectionStatusWidget";
import { ParticipantBadge } from "../../common/components/ParticipantBadge";
import { PulseDashboard } from "../../common/components/PulseDashboard";
import { RoleBrandingHeader } from "../../common/components/RoleBrandingHeader";
import { UserMenu } from "../../common/components/UserMenu";
import { formatLogText } from "../../../utils/formatLogText";
import { TextSelector } from "../components/TextSelector";
import { LogCard } from "../components/LogCard";
import { RelayClipPlayer, type RelayClipPlayerHandle } from "../components/RelayClipPlayer";
import { VideoSearchPanel } from "../components/VideoSearchPanel";
import { getLogWallClock, resolveRelayChannel } from "../utils/relayClip";
import { getParticipantColor, isColorLight } from "../../../utils/participantColors";
import { offlineSyncService } from "../../../services/offlineSync";
import { supabase } from "../../../services/supabase";
import { CustomDialog } from "../../common/components/CustomDialog";
import DeleteCascadeModal from "../../common/components/DeleteCascadeModal";
import { ExportMarkersDialog } from "../components/ExportMarkersDialog";
import { SummaryFullView } from "../../executive/views/SummaryFullView";
import { StreamFeed } from "../../controller/views/StreamFeed";
import type { Story as DomainStory } from "../../common/types/domain";
import { compareLogs } from "../../../utils/logOrder";
import { parseLog } from "../schemas/logSchema";

interface LogEntry {
  id: string;
  sourceId?: string;
  tc: string;
  text: string;
  author?: string;
  rating?: number;
  participants?: string[];
  tags?: string[];
  cameras?: string[];
  directorId?: string;
  type?: string;
  originType?: "log" | "transcript" | "transcription";
  startAt?: string;
  endAt?: string;
  createdAt?: number;
  // Snapshots de metadata resueltos al armar el item de biblioteca/VTR
  // (round-trip por DB). Opcionales: no todos los logs los traen.
  entryTags?: string[];
  storyTags?: string[];
  streams?: string[];
  storyRating?: number;
  duration?: string;
  rundownName?: string;
  eventName?: string;
  storyName?: string;
  // Orden manual ajustado en Recent Logs del Logger; round-trip por DB vía
  // content.sort_order. La biblioteca lo respeta con compareLogs.
  sortOrder?: number;
  // Word-level timestamps y metadata de historia propagada (RESUMEN).
  words?: any[];
  storyParticipants?: string[];
  startOffset?: number | null;
  endOffset?: number | null;
}

interface TranscriptSegment {
  id: string;
  sourceId?: string;
  text: string;
  tc: string;
  startAt: number;
  endAt: number;
  speaker: string | null;
  storyId?: string;
  tags?: string[];
  rating?: number;
  participants?: string[];
  cameras?: string[];
  streams?: string[];
}

interface SharedNote {
  id: string;
  text: string;
  author: string;
  role: string;
  createdAt: string;
}

interface RundownItem {
  id: string;
  title: string;
  status?: string;
  summary?: string;
  producerNotes?: string;
  notesAuthor?: string;
  sharedNotes?: SharedNote[];
  scheduledAt?: string | number | null;
  plannedTime?: string | null;
  stats?: {
    stories: number;
    logs: number;
    transcripts?: number;
    otfs?: number;
  };
}

interface Rundown {
  id: string;
  name: string;
  date?: string;
  items: RundownItem[];
  projectId: string;
  summary?: string;
  stats?: {
    events: number;
    stories: number;
    logs: number;
    transcripts?: number;
    otfs?: number;
  };
}

interface Story {
  id: string;
  title: string;
  name?: string;
  status: string;
  author?: string;
  rundownId: string;
  tags?: string[];
  cameras?: string[];
  streams?: string[];
  eventName?: string;
  rundownName?: string;
  summary?: string;
  aiSummary?: string;
  producerNotes?: string;
  notesAuthor?: string;
  sharedNotes?: SharedNote[];
  logs?: LogEntry[];
  logCount?: number;
  transcriptCount?: number;
  otfCount?: number;
  createdAt?: number;
  rating?: number;
  participants?: string[];
  directorId?: string;
}

type ViewMode = "list" | "cards";
type DrillLevel = "rundowns" | "events" | "stories" | "logs" | "transcript-context";

interface BreadcrumbItem {
  level: DrillLevel;
  id: string;
  title: string;
}

const SharedNotesEditor: React.FC<{
  notes: SharedNote[];
  onChange: (notes: SharedNote[]) => void;
  currentUser: { id: string; name: string; role: string } | null;
  accentColor: string;
}> = ({ notes, onChange, currentUser, accentColor }) => {
  const [newNoteText, setNewNoteText] = useState("");

  const handleAddNote = () => {
    if (!newNoteText.trim() || !currentUser) return;
    const newNote: SharedNote = {
      id: crypto.randomUUID(),
      text: newNoteText.trim(),
      author: currentUser.name,
      role: currentUser.role,
      createdAt: new Date().toISOString(),
    };
    onChange([...notes, newNote]);
    setNewNoteText("");
  };

  const handleDeleteNote = (id: string) => {
    onChange(notes.filter((n) => n.id !== id));
  };

  const handleUpdateNote = (id: string, newText: string) => {
    onChange(notes.map((n) => (n.id === id ? { ...n, text: newText } : n)));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {notes.map((note) => {
        const isAuthor = currentUser?.name === note.author;
        return (
          <div
            key={note.id}
            style={{
              padding: "12px",
              background: "rgba(255,255,255,0.05)",
              borderRadius: "8px",
              borderLeft: `4px solid ${note.role === "Showrunner" ? "var(--color-role-showrunner)" : "var(--color-role-sp)"}`,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "8px",
              }}
            >
              <div style={{ fontSize: "0.75rem", color: "#888" }}>
                <strong style={{ color: "#ccc" }}>{note.author}</strong> ({note.role}) •{" "}
                {new Date(note.createdAt).toLocaleString()}
              </div>
              {isAuthor && (
                <button
                  onClick={() => handleDeleteNote(note.id)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#ff4444",
                    cursor: "pointer",
                    padding: "0 4px",
                  }}
                >
                  <i className="bx bx-trash"></i>
                </button>
              )}
            </div>
            {isAuthor ? (
              <textarea
                data-testid={`shared-note-edit-${note.id}`}
                value={note.text}
                onChange={(e) => handleUpdateNote(note.id, e.target.value)}
                style={{
                  width: "100%",
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#fff",
                  padding: "8px",
                  borderRadius: "4px",
                  resize: "vertical",
                  minHeight: "60px",
                }}
              />
            ) : (
              <div
                style={{
                  color: "#ddd",
                  whiteSpace: "pre-wrap",
                  fontSize: "0.9rem",
                }}
              >
                {note.text}
              </div>
            )}
          </div>
        );
      })}

      <div style={{ marginTop: "8px" }}>
        <textarea
          data-testid="shared-note-new-input"
          value={newNoteText}
          onChange={(e) => setNewNoteText(e.target.value)}
          placeholder="Add a new shared note..."
          style={{
            width: "100%",
            background: "#111",
            border: `1px solid ${accentColor}`,
            color: "#fff",
            padding: "12px",
            borderRadius: "8px",
            resize: "vertical",
            minHeight: "80px",
            boxSizing: "border-box",
          }}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "8px",
          }}
        >
          <button
            onClick={handleAddNote}
            disabled={!newNoteText.trim() || !currentUser}
            style={{
              padding: "8px 16px",
              background: newNoteText.trim() ? accentColor : "#333",
              color: newNoteText.trim() ? "#000" : "#888",
              border: "none",
              borderRadius: "4px",
              cursor: newNoteText.trim() ? "pointer" : "not-allowed",
              fontWeight: 600,
            }}
          >
            Add Note
          </button>
        </div>
      </div>
    </div>
  );
};

function sanitizeTxtField(value?: string | null) {
  if (value == null) return "";
  return String(value)
    .replace(/[\t\r\n]+/g, " ")
    .trim();
}

function buildStoryTxtFilename(title?: string) {
  const slug =
    (title || "story")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "") || "story";
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const hhmm = `${pad(now.getHours())}${pad(now.getMinutes())}`;
  const ymd = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  return `${slug}_${hhmm}_${ymd}.txt`;
}

// Fecha del corte en formato "25 Jul " (día + mes abreviado + un espacio final).
// Preferimos createdAt de la historia; si falta, el startAt del primer log.
function formatCutDate(story: Story, sorted: LogEntry[]): string {
  const startAt = sorted.find((l) => l.startAt)?.startAt;
  const ts = story.createdAt ?? (startAt ? Date.parse(startAt) : Number.NaN);
  const d = Number.isNaN(ts) ? new Date() : new Date(ts);
  return `${d.getDate()} ${d.toLocaleDateString("en-US", { month: "short" })} `;
}

function buildStoryTxtContent(story: Story, logs: LogEntry[]) {
  const sorted = [...logs].sort(compareLogs);
  const headerLines = [
    sanitizeTxtField(story.title) || "(sin título)",
    formatCutDate(story, sorted),
  ];
  const body = sorted
    .map((log: any) => {
      const tc = sanitizeTxtField(log.tc) || "00:00:00:00";
      const text = sanitizeTxtField(log.text) || "(sin texto)";
      return `${tc}\t${text}`;
    })
    .join("\n");
  return `${headerLines.join("\n")}\n${body}`.trimEnd();
}

function downloadTxt(content: string, filename: string) {
  const blob = new Blob([content], { type: "text/plain; charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 1000);
}

function downloadStoryTxt(story: Story, logs: LogEntry[]) {
  downloadTxt(`${buildStoryTxtContent(story, logs)}\n`, buildStoryTxtFilename(story.title));
}

export function buildSelectedStoriesTxt(
  storiesWithLogs: Array<{ story: Story; logs: LogEntry[] }>,
) {
  return `${storiesWithLogs
    .map(({ story, logs }) => buildStoryTxtContent(story, logs))
    .join("\n\n")}\n`;
}

function buildSelectedStoriesTxtFilename(count: number) {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const hhmm = `${pad(now.getHours())}${pad(now.getMinutes())}`;
  const ymd = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  return `historias-seleccionadas-${count}_${hhmm}_${ymd}.txt`;
}

function downloadSelectedStoriesTxt(storiesWithLogs: Array<{ story: Story; logs: LogEntry[] }>) {
  downloadTxt(
    buildSelectedStoriesTxt(storiesWithLogs),
    buildSelectedStoriesTxtFilename(storiesWithLogs.length),
  );
}

function formatRelayTc(ms: number): string {
  const date = new Date(ms);
  const timePart = date.toLocaleTimeString("en-GB", {
    hour12: false,
    timeZone: "America/Mexico_City",
  });
  const milliseconds = date.getMilliseconds();
  const frames = Math.floor((milliseconds / 1000) * 29.97);
  return `${timePart};${String(frames).padStart(2, "0")}`;
}

// Convierte un timecode \"HH:MM:SS;FF\" o \"HH:MM:SS:FF\" a UTC ms.
function parseRelayTcToMs(tc: string, baseDate: Date | null | undefined): number | null {
  if (!tc || !baseDate) return null;
  const parts = tc.split(/[:;]/);
  if (parts.length < 3) return null;
  const hh = parseInt(parts[0], 10);
  const mm = parseInt(parts[1], 10);
  const ss = parseInt(parts[2], 10);
  const ff = parts[3] ? parseInt(parts[3], 10) : 0;
  if (isNaN(hh) || isNaN(mm) || isNaN(ss) || isNaN(ff)) return null;

  try {
    const datePart = baseDate.toLocaleDateString("en-CA", { timeZone: "America/Mexico_City" }); // 'YYYY-MM-DD'
    const localDate = new Date(
      `${datePart}T${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}:${parts[2].padStart(2, "0")}`,
    );
    if (isNaN(localDate.getTime())) return null;

    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Mexico_City",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: false,
    });

    const formatted = formatter.format(localDate);
    const match = formatted.match(/,\s*(\d+):(\d+):(\d+)/);
    if (!match) return localDate.getTime() + Math.round((ff / 29.97) * 1000);

    const fH = parseInt(match[1], 10);
    const fM = parseInt(match[2], 10);
    const fS = parseInt(match[3], 10);

    const wantedSecs = hh * 3600 + mm * 60 + ss;
    const gotSecs = fH * 3600 + fM * 60 + fS;

    const diffSecs = wantedSecs - gotSecs;
    const frameMs = Math.round((ff / 29.97) * 1000);
    return localDate.getTime() + diffSecs * 1000 + frameMs;
  } catch (e) {
    console.error("Error parsing TC to MS:", e);
    return null;
  }
}

// El selector STREAM 1/2 del player mapea a los canales de video del relay.
function streamToChannel(stream: "1" | "2"): string {
  return stream === "2" ? "video-2" : "video-1";
}
function channelToStream(channel: string): "1" | "2" {
  return channel === "video-2" ? "2" : "1";
}

const kbdStyle: React.CSSProperties = {
  background: "#2c2c2e",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: "4px",
  color: "#fff",
  padding: "3px 8px",
  fontFamily: "monospace",
  fontSize: "0.8rem",
  boxShadow: "0 2px 0 rgba(0,0,0,0.3)",
  display: "inline-block",
};

export const StoryProducer: React.FC<{
  mode?: "producer" | "showrunner";
  isEmbedded?: boolean;
  externalShowAssembly?: boolean;
  forceReadOnly?: boolean;
}> = ({ mode = "producer", isEmbedded = false, externalShowAssembly, forceReadOnly = false }) => {
  const { t } = useTranslation();
  const { activeProject, clearActiveProject, isLoading: isProjectLoading } = useProjects();
  const { navigate } = useProjectNavigation();
  const { user } = useAuth();
  const {
    getTagLabel,
    getTagColor,
    getCameraName,
    categories,
    tags: allTagsData,
    directors: contextDirectors,
  } = useTags();
  const isShowrunner = mode === "showrunner";
  const isContentDirector = user?.role === "Content Director";
  const isReadOnly = forceReadOnly || user?.role === "Cliente";
  // zoom removed to favor natural responsive layout on smaller screens
  const _zoom = 1;
  const ACCENT_COLOR = isShowrunner
    ? "var(--color-role-showrunner)"
    : isContentDirector
      ? "var(--color-role-contentdirector)"
      : "var(--color-role-sp)";
  const NOTES_LABEL = isShowrunner
    ? t("storyproducer.showrunnerNotes")
    : t("storyproducer.producerNotes");
  const MODULE_TITLE = isShowrunner
    ? t("storyproducer.showRunner")
    : isContentDirector
      ? t("auth.roles.contentDirector")
      : t("storyproducer.storyProducer");
  const UNKNOWN_SPEAKER = t("storyproducer.unknown");
  const {
    isSelectionMode,
    toggleSelectionMode: _toggleSelectionMode,
    isSelected,
    toggleItem,
    selectedItems,
    navigateToSource,
    clearNavigateToSource,
    addItemsToProject,
    activeProjectId,
  } = useSelection();
  const [searchParams, setSearchParams] = useSearchParams();
  const [globalAlert, setGlobalAlert] = useState<{
    isOpen: boolean;
    message: string;
    title?: string;
  } | null>(null);

  // Data state — always starts empty; populated exclusively by server via sockets
  const [rundowns, setRundowns] = useState<Rundown[]>([]);
  const [stories, setStories] = useState<Story[]>([]);
  const [participants, setParticipants] = useState<any[]>([]);
  const [directors, setDirectors] = useState<any[]>([]);

  // Guard: prevents URL sync from acting on stale/empty data before init-data arrives
  const [dataReady, setDataReady] = useState(false);

  // Dedupe de peticiones get-event-stories en vuelo. NO es un caché de
  // frescura: las stories se vuelven a pedir al servidor en cada drill-down.
  const [loadingEventIds, setLoadingEventIds] = useState<Set<string>>(new Set());

  // UI state
  const [isExportMarkersOpen, setIsExportMarkersOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("cards");
  // Library "OTF MODE" switch — when true, every library card (log AND
  // transcription) renders with the OTF visual style (cyan, no italic) AND
  // drag-into-CUT as OTF, so anything promoted on-the-fly lands as a proper
  // OTF entry downstream.
  const [displayAsOtf, setDisplayAsOtf] = useState<boolean>(false);
  const resolveDragType = useCallback(
    (logType: string | undefined): "otf" | "transcript" | "log" => {
      if (logType === "otf") return "otf";
      if (displayAsOtf) return "otf";
      const isTr = logType === "transcript" || logType === "transcription";
      if (isTr) return "transcript";
      return "log";
    },
    [displayAsOtf],
  );
  // Sibling to resolveDragType — also surfaces `originType` so a card
  // promoted to OTF by OTF MODE still remembers whether it began life as a
  // log or a transcript. Always spread this into the drag/selection payload
  // alongside the rest of the SelectableItem fields.
  //
  // Si el log ya viene del logger con un originType persistido (porque OTF
  // MODE del logger lo promovió al crearlo), respetamos ese valor — no lo
  // sobrescribimos con `log.type` actual ("otf"), que perdería la traza del
  // origen real (transcript/log).
  const resolveDragMeta = useCallback(
    (logType: string | undefined, existingOrigin?: string) => ({
      type: resolveDragType(logType),
      originType: (existingOrigin ?? logType ?? undefined) as
        | "log"
        | "transcript"
        | "transcription"
        | "otf"
        | undefined,
    }),
    [resolveDragType],
  );

  // ── Relay clip player ──────────────────────────────────────────────────────
  // Al pulsar el timecode de una tarjeta, cargamos el video grabado del relay en
  // ese instante DENTRO del STREAM PLAYER de arriba (no en un modal). El canal se
  // deriva de los tags de stream/cámara del log (ver resolveRelayChannel) y se
  // refleja en el selector STREAM 1/2.
  const playerRef = useRef<RelayClipPlayerHandle>(null);
  const assemblyRef = useRef<AssemblyManagerRef | null>(null);
  const [activeFocus, setActiveFocus] = useState<"player" | "program">("player");
  const activeFocusRef = useRef<"player" | "program">("player");
  activeFocusRef.current = activeFocus;

  const handleVtrPlayStarted = useCallback(() => {
    playerRef.current?.pause();
  }, []);
  const [playerClip, setPlayerClip] = useState<{
    at: Date;
    channel: string;
    title: string;
  } | null>(null);
  // Posición actual del video como wall-clock (UTC ms); alimenta el TC y IN/OUT.
  const [playerWallMs, setPlayerWallMs] = useState<number | null>(null);
  // Progreso de la barra de seek del STREAM PLAYER: fracción [0..1] dentro de la
  // ventana ±30 min alrededor del momento del log. `ready` habilita el scrub.
  const [playerProgress, setPlayerProgress] = useState<{
    fraction: number;
    ready: boolean;
  }>({
    fraction: 0,
    ready: false,
  });
  const [playerLoading, setPlayerLoading] = useState<boolean>(false);
  const [playerError, setPlayerError] = useState<string | null>(null);
  const [playerPendingSeekFraction, setPlayerPendingSeekFraction] = useState<number | null>(null);
  // Timecode "HH:MM:SS" derivado de la posición real del video (formato log.tc).
  const playerTc = playerWallMs != null ? formatRelayTc(playerWallMs) : null;
  // Fracción [0..1] reproducida; alimenta el relleno y el thumb de la barra de seek.
  const playerSeekFraction = playerPendingSeekFraction ?? playerProgress.fraction;
  useEffect(() => {
    setPlayerPendingSeekFraction(null);
  }, [playerClip?.at, playerClip?.channel]);

  // Convierte la posición X de un puntero sobre la barra en una fracción [0..1].
  const fractionFromPointer = useCallback((clientX: number, el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return null;
    return Math.max(0, Math.min((clientX - rect.left) / rect.width, 1));
  }, []);
  const previewSeekFromPointer = useCallback(
    (clientX: number, el: HTMLElement) => {
      const fraction = fractionFromPointer(clientX, el);
      if (fraction == null) return;
      setPlayerPendingSeekFraction((current) =>
        current != null && Math.abs(current - fraction) < 0.001 ? current : fraction,
      );
    },
    [fractionFromPointer],
  );
  const commitSeekFromPointer = useCallback(
    (clientX: number, el: HTMLElement) => {
      const fraction = fractionFromPointer(clientX, el);
      setPlayerPendingSeekFraction(null);
      if (fraction == null) return;
      playerRef.current?.seekToFraction(fraction);
    },
    [fractionFromPointer],
  );
  const cancelPendingSeek = useCallback(() => {
    setPlayerPendingSeekFraction(null);
  }, []);

  // Mantené esta regex en sync con LogCard#isStreamOrLocationTag.
  const streamTagLabelsForLog = useCallback(
    (log: any): string[] => {
      const labels = new Set<string>();
      const add = (value: unknown) => {
        if (typeof value !== "string") return;
        const label = value.trim();
        if (label) labels.add(label);
      };

      // `streams` es la asignación específica de la entrada. Puede venir del
      // relay como "STREAM 2" o como un canal canónico ("video-2"), por lo
      // que debe ganar sobre la cámara general de la historia.
      (log?.streams || []).forEach(add);
      if (log?.relayChannel) add(log.relayChannel);

      for (const cameraId of log?.cameras || []) {
        add(getCameraName(cameraId));
      }

      for (const tagId of log?.tags || []) {
        const tag = allTagsData.find((tt: any) => tt.id === tagId);
        if (!tag) continue;
        const cat = categories.find((c: any) => c.id === tag.categoryId);
        const name = (cat as any)?.name || "";
        if (
          /stream|ubicaci|location|espacio|lugar|foro|canal|c[áa]m(?:ara|era)|\bset\b/i.test(name)
        ) {
          add(getTagLabel(tagId));
        }
      }

      return Array.from(labels);
    },
    [allTagsData, categories, getCameraName, getTagLabel],
  );

  const handleTimecodeClick = useCallback(
    (log: any) => {
      const at = getLogWallClock(log);
      if (!at) {
        setGlobalAlert({
          isOpen: true,
          title: t("storyproducer.relayClip.title", "Clip de video"),
          message: t(
            "storyproducer.relayClip.noTimestamp",
            "Este log no tiene una marca de tiempo para ubicar el video.",
          ),
        });
        return;
      }
      const channel = resolveRelayChannel(streamTagLabelsForLog(log));
      const tcLabel = log.tc || at.toLocaleTimeString("en-GB", { timeZone: "America/Mexico_City" });
      // Carga el clip en el STREAM PLAYER de arriba (lo expande si estaba colapsado)
      // y sincroniza el selector STREAM 1/2 con el canal derivado.
      setPlayerClip({ at, channel, title: tcLabel });
      setActiveStream(channelToStream(channel));
      setActiveLogRef(log);
      setIsMockPlayerCollapsed(false);
      assemblyRef.current?.pauseVtr();
    },
    [streamTagLabelsForLog, t],
  );

  const [drillLevel, setDrillLevel] = useState<DrillLevel>("rundowns");
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [selectedRundown, setSelectedRundown] = useState<Rundown | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<RundownItem | null>(null);
  const [selectedStory, setSelectedStory] = useState<Story | null>(null);
  const [isLoadingStoryLogs, setIsLoadingStoryLogs] = useState<boolean>(false);
  const [showShortcutsModal, setShowShortcutsModal] = useState<boolean>(false);
  const [storyAuthorFilter, setStoryAuthorFilter] = useState<string>("");
  const [selectedStoryExportIds, setSelectedStoryExportIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [isExportingSelectedStories, setIsExportingSelectedStories] = useState(false);
  React.useEffect(() => {
    setStoryAuthorFilter("");
    setSelectedStoryExportIds(new Set());
  }, [selectedEvent?.id]);
  const selectedEventStories = useMemo(
    () =>
      selectedEvent
        ? stories
            .filter((story) => story.rundownId === selectedEvent.id && story.status !== "archived")
            .sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0))
        : [],
    [selectedEvent?.id, stories],
  );
  const visibleSelectedEventStories = useMemo(
    () =>
      selectedEventStories.filter(
        (story) => !storyAuthorFilter || (story.author || "").trim() === storyAuthorFilter,
      ),
    [selectedEventStories, storyAuthorFilter],
  );
  useEffect(() => {
    const availableStoryIds = new Set(selectedEventStories.map((story) => story.id));
    setSelectedStoryExportIds((current) => {
      if ([...current].every((storyId) => availableStoryIds.has(storyId))) return current;
      return new Set([...current].filter((storyId) => availableStoryIds.has(storyId)));
    });
  }, [selectedEventStories]);
  const [editingStory, setEditingStory] = useState<Story | null>(null);
  const [editingEvent, setEditingEvent] = useState<RundownItem | null>(null);
  // STREAM PLAYER State (Biblioteca UX). El player ahora reproduce el video real
  // del relay (ver playerClip/playerRef arriba); estos estados son del chrome.
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isStreamPlayerFocused, setIsStreamPlayerFocused] = useState<boolean>(true);

  React.useEffect(() => {
    const checkFocus = () => {
      const active = (window as any).__activeBraindogPlayer || "stream";
      setIsStreamPlayerFocused(active === "stream");
    };
    window.addEventListener("braindog-active-player-change", checkFocus);
    return () => {
      window.removeEventListener("braindog-active-player-change", checkFocus);
    };
  }, []);
  const [activeStream, setActiveStream] = useState<"1" | "2">("1");
  const [isSummaryCollapsed, setIsSummaryCollapsed] = useState<boolean>(true);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [isMockPlayerCollapsed, setIsMockPlayerCollapsed] = useState<boolean>(false);
  // Mark IN/OUT states
  const [markInTc, setMarkInTc] = useState<string | null>(null);
  const [markOutTc, setMarkOutTc] = useState<string | null>(null);
  const [activeLogRef, setActiveLogRef] = useState<any>(null);

  // Estados y refs para transporte J-K-L profesional (library player)
  const [jklSpeed, setJklSpeed] = useState<number>(1);
  const [jklDirection, setJklDirection] = useState<"forward" | "backward" | "paused">("paused");
  const rewindIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Estados J-K-L para el program player (VTR shuttle)
  const [vtrJklSpeed, setVtrJklSpeed] = useState<number>(1);
  const [vtrJklDirection, setVtrJklDirection] = useState<"forward" | "backward" | "paused">(
    "paused",
  );
  const vtrRewindIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopVtrRewinding = useCallback(() => {
    if (vtrRewindIntervalRef.current) {
      clearInterval(vtrRewindIntervalRef.current);
      vtrRewindIntervalRef.current = null;
    }
  }, []);

  const stopRewinding = useCallback(() => {
    if (rewindIntervalRef.current) {
      clearInterval(rewindIntervalRef.current);
      rewindIntervalRef.current = null;
    }
  }, []);

  const handleJKL_PlayForward = useCallback(
    (speedMultiplier: number) => {
      stopRewinding();
      setJklDirection("forward");
      setJklSpeed(speedMultiplier);
      playerRef.current?.setRate(speedMultiplier);
      setPlaybackSpeed(speedMultiplier);
      playerRef.current?.play();
      assemblyRef.current?.pauseVtr();
    },
    [stopRewinding],
  );

  const handleJKL_Pause = useCallback(() => {
    stopRewinding();
    setJklDirection("paused");
    setJklSpeed(1);
    playerRef.current?.setRate(1);
    setPlaybackSpeed(1);
    playerRef.current?.pause();
  }, [stopRewinding]);

  const handleJKL_Rewind = useCallback(
    (speedMultiplier: number) => {
      stopRewinding();
      setJklDirection("backward");
      setJklSpeed(speedMultiplier);
      playerRef.current?.pause();

      const intervalMs = 200;
      const deltaSec = -speedMultiplier;

      rewindIntervalRef.current = setInterval(() => {
        playerRef.current?.seekBy(deltaSec);
      }, intervalMs);
    },
    [stopRewinding],
  );

  const navigateToLogByIndex = useCallback(
    (newIndex: number, logsList: any[]) => {
      if (!logsList || logsList.length === 0) return;
      const safeIndex = Math.max(0, Math.min(newIndex, logsList.length - 1));
      const targetLog = logsList[safeIndex];
      if (!targetLog) return;

      handleTimecodeClick(targetLog);

      // Scroll
      setTimeout(() => {
        const el = document.getElementById(`mock-log-${targetLog.id}`);
        if (el) {
          el.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 50);
    },
    [handleTimecodeClick],
  );
  const handleInsertClipToCut = useCallback(() => {
    if (!activeProjectId || !markInTc || !markOutTc) return;
    const log = activeLogRef;
    const parseTcMs = (tc: string): number => {
      if (tc.includes("T")) return new Date(tc).getTime();
      const parts = tc.split(":").map(Number);
      if (parts.length === 4)
        return (parts[0] * 3600 + parts[1] * 60 + parts[2]) * 1000 + parts[3] * 40;
      if (parts.length === 3) return (parts[0] * 3600 + parts[1] * 60 + parts[2]) * 1000;
      return 0;
    };
    const durMs = Math.abs(parseTcMs(markOutTc) - parseTcMs(markInTc));
    const durS = Math.floor(durMs / 1000) % 60;
    const durM = Math.floor(durMs / 60000) % 60;
    const durH = Math.floor(durMs / 3600000);
    const computedDuration = `${durH > 0 ? `${durH}:` : ""}${durM.toString().padStart(2, "0")}:${durS.toString().padStart(2, "0")}`;

    const item = {
      id: `log_io_${Date.now()}_${crypto.randomUUID().substring(0, 8)}`,
      type: (log?.type || "log") as any,
      title: log?.text || "Log",
      tc: markInTc,
      tcIn: markInTc,
      tcOut: markOutTc,
      author: log?.author,
      tags: log?.tags || [],
      rating: log?.rating || 0,
      participants: log?.participants || [],
      sourceId: selectedStory?.id || "",
      storyId: selectedStory?.id || "",
      duration: computedDuration,
      directorId: log?.directorId,
    };

    addItemsToProject(activeProjectId, [item as any]);
    setSendToCutFlash(true);
    setTimeout(() => setSendToCutFlash(false), 800);
    setMarkInTc(null);
    setMarkOutTc(null);
  }, [activeProjectId, markInTc, markOutTc, activeLogRef, selectedStory?.id]);

  useEffect(() => {
    return () => {
      stopRewinding();
    };
  }, [stopRewinding]);
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Ignorar si el usuario está escribiendo en cualquier input o editor editable
      const activeEl = document.activeElement;
      if (
        activeEl &&
        (activeEl.tagName === "INPUT" ||
          activeEl.tagName === "TEXTAREA" ||
          activeEl.getAttribute("contenteditable") === "true")
      ) {
        return;
      }

      // J-K-L transporte
      if (e.code === "KeyL") {
        e.preventDefault();
        if (activeFocus === "program") {
          // L en program → play forward, escalando velocidad: 1→1.5→2→4x
          if (!assemblyRef.current) return;
          stopVtrRewinding();
          const nextSpeed =
            vtrJklDirection === "forward"
              ? vtrJklSpeed === 1
                ? 1.5
                : vtrJklSpeed === 1.5
                  ? 2
                  : vtrJklSpeed === 2
                    ? 4
                    : 4
              : 1;
          setVtrJklDirection("forward");
          setVtrJklSpeed(nextSpeed);
          assemblyRef.current.setVtrSpeed(nextSpeed);
          if (!assemblyRef.current.isVtrPlaying) assemblyRef.current.toggleVtrPlay();
        } else {
          if (jklDirection === "forward") {
            const nextSpeed = jklSpeed === 1 ? 2 : jklSpeed === 2 ? 4 : 8;
            handleJKL_PlayForward(nextSpeed);
          } else {
            handleJKL_PlayForward(1);
          }
        }
        return;
      }
      if (e.code === "KeyK") {
        e.preventDefault();
        if (activeFocus === "program") {
          // K en program → pausa y resetea velocidad a 1x
          stopVtrRewinding();
          setVtrJklDirection("paused");
          setVtrJklSpeed(1);
          assemblyRef.current?.setVtrSpeed(1);
          assemblyRef.current?.pauseVtr();
        } else {
          handleJKL_Pause();
        }
        return;
      }
      if (e.code === "KeyJ") {
        e.preventDefault();
        if (activeFocus === "program") {
          // J en program → reproducción hacia atrás, escalando: 1→1.5→2→4x
          if (!assemblyRef.current) return;
          // Pausar reproducción normal primero
          if (assemblyRef.current.isVtrPlaying) {
            assemblyRef.current.setVtrSpeed(1);
            assemblyRef.current.pauseVtr();
          }
          stopVtrRewinding();
          const nextSpeed =
            vtrJklDirection === "backward"
              ? vtrJklSpeed === 1
                ? 1.5
                : vtrJklSpeed === 1.5
                  ? 2
                  : vtrJklSpeed === 2
                    ? 4
                    : 4
              : 1;
          setVtrJklDirection("backward");
          setVtrJklSpeed(nextSpeed);
          // Retroceso emulado: seek directo en el video (más fluido que seekVtrBy)
          const intervalMs = 150;
          const deltaSec = -(nextSpeed * 0.15); // desplazamiento por tick proporcional a la velocidad
          vtrRewindIntervalRef.current = setInterval(() => {
            assemblyRef.current?.seekVtrDirect(deltaSec);
          }, intervalMs);
        } else {
          if (jklDirection === "backward") {
            const nextSpeed = jklSpeed === 1 ? 2 : jklSpeed === 2 ? 4 : 8;
            handleJKL_Rewind(nextSpeed);
          } else {
            handleJKL_Rewind(1);
          }
        }
        return;
      }

      // Play/Pause alternativo con barra espaciadora
      if (e.code === "Space") {
        e.preventDefault();
        if (activeFocus === "program") {
          assemblyRef.current?.toggleVtrPlay();
        } else {
          if (jklDirection === "paused") {
            handleJKL_PlayForward(1);
          } else {
            handleJKL_Pause();
          }
        }
        return;
      }

      // Frame stepping con Flecha Izq / Der (o salto de 5s con Shift)
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (activeFocus === "program") {
          if (e.shiftKey) {
            // Shift+← : saltar 5s sin audio scrub
            assemblyRef.current?.seekVtrBy(-5);
          } else {
            // ← : frame atrás con audio scrub para ubicar palabras
            assemblyRef.current?.scrubVtrAudio(-1);
          }
        } else if (playerClip) {
          if (e.shiftKey) {
            playerRef.current?.seekBy(-5);
          } else {
            playerRef.current?.seekBy(-1 / 29.97);
          }
        }
        return;
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        if (activeFocus === "program") {
          if (e.shiftKey) {
            // Shift+→ : saltar 5s sin audio scrub
            assemblyRef.current?.seekVtrBy(5);
          } else {
            // → : frame adelante con audio scrub para ubicar palabras
            assemblyRef.current?.scrubVtrAudio(1);
          }
        } else if (playerClip) {
          if (e.shiftKey) {
            playerRef.current?.seekBy(5);
          } else {
            playerRef.current?.seekBy(1 / 29.97);
          }
        }
        return;
      }

      // Marcar In & Out (I / O)
      if (e.code === "KeyI" && !e.shiftKey) {
        e.preventDefault();
        if (playerTc) {
          setMarkInTc(playerTc);
        }
        return;
      }
      if (e.code === "KeyO" && !e.shiftKey) {
        e.preventDefault();
        if (playerTc) {
          setMarkOutTc(playerTc);
        }
        return;
      }

      // Ir a In / Out (Shift + I / Shift + O)
      if (e.shiftKey && e.code === "KeyI") {
        e.preventDefault();
        if (markInTc) {
          const wallMs = parseRelayTcToMs(markInTc, playerClip?.at);
          if (wallMs !== null) {
            playerRef.current?.seekToWallClock(wallMs);
          }
        }
        return;
      }
      if (e.shiftKey && e.code === "KeyO") {
        e.preventDefault();
        if (markOutTc) {
          const wallMs = parseRelayTcToMs(markOutTc, playerClip?.at);
          if (wallMs !== null) {
            playerRef.current?.seekToWallClock(wallMs);
          }
        }
        return;
      }

      // Insertar al corte (Tecla Coma)
      if (e.code === "Comma") {
        e.preventDefault();
        handleInsertClipToCut();
        return;
      }

      // Navegación de tarjetas de la historia o del programa
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        if (activeFocusRef.current === "program") {
          // Evitar que Slate/browser reciban este evento (scroll o cursor interno)
          e.preventDefault();
          e.stopImmediatePropagation();
          if (e.key === "ArrowDown") {
            assemblyRef.current?.handleNextClip();
          } else {
            assemblyRef.current?.handlePrevClip();
          }
        } else {
          e.preventDefault();
          if (!selectedStory?.logs || selectedStory.logs.length === 0) return;

          // Sort logs the same way as rendered
          const sortedLogs = [...selectedStory.logs].sort(compareLogs);

          const currentIndex = activeLogRef
            ? sortedLogs.findIndex((l) => l.id === activeLogRef.id)
            : -1;

          let nextIndex = 0;
          if (e.key === "ArrowDown") {
            nextIndex = currentIndex === -1 ? 0 : Math.min(currentIndex + 1, sortedLogs.length - 1);
          } else {
            nextIndex = currentIndex === -1 ? sortedLogs.length - 1 : Math.max(currentIndex - 1, 0);
          }

          navigateToLogByIndex(nextIndex, sortedLogs);
        }
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleGlobalKeyDown, true);
    };
  }, [
    selectedStory?.logs,
    activeLogRef,
    navigateToLogByIndex,
    playerClip,
    playerTc,
    markInTc,
    markOutTc,
    jklDirection,
    jklSpeed,
    handleJKL_PlayForward,
    handleJKL_Pause,
    handleJKL_Rewind,
    vtrJklDirection,
    vtrJklSpeed,
    stopVtrRewinding,
    handleInsertClipToCut,
  ]);
  const [sendToCutFlash, setSendToCutFlash] = useState(false);
  const [deleteCascadeModal, setDeleteCascadeModal] = React.useState<{
    isOpen: boolean;
    type: "log" | "story" | "event" | "rundown";
    id: string;
    name: string;
    counts: any;
    isLoading: boolean;
    isDeleting: boolean;
  }>({
    isOpen: false,
    type: "log",
    id: "",
    name: "",
    counts: null,
    isLoading: false,
    isDeleting: false,
  });
  const deleteCascadeModalRef = useRef(deleteCascadeModal);

  useEffect(() => {
    deleteCascadeModalRef.current = deleteCascadeModal;
  }, [deleteCascadeModal]);

  // Mount guard for async socket callbacks — prevents setState after unmount
  // when the user navigates away while a get-event-stories / get-story-* / etc.
  // response is still in flight.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Delete Cascade Socket Listeners
  useEffect(() => {
    const onCount = (d: any) => {
      if (d.success)
        setDeleteCascadeModal((p) =>
          p.id === d.id ? { ...p, counts: d.counts, isLoading: false } : p,
        );
    };
    const onDel = (d: any) => {
      if (d.success) {
        setDeleteCascadeModal((p) => ({ ...p, isOpen: false }));
        window.location.reload();
      } else setDeleteCascadeModal((p) => ({ ...p, isDeleting: false }));
    };
    socketService.on("admin:count-cascade-result", onCount);
    socketService.on("admin:delete-cascade-result", onDel);
    return () => {
      socketService.off("admin:count-cascade-result", onCount);
      socketService.off("admin:delete-cascade-result", onDel);
    };
  }, []);

  const handleDeleteClick = useCallback(
    (type: "log" | "story" | "event" | "rundown", id: string, name: string) => {
      setDeleteCascadeModal({
        isOpen: true,
        type,
        id,
        name,
        counts: null,
        isLoading: true,
        isDeleting: false,
      });
      socketService.emit("admin:count-cascade", { type, id });
    },
    [],
  );

  const handleDeleteConfirm = useCallback(() => {
    const modal = deleteCascadeModalRef.current;
    setDeleteCascadeModal((p) => ({ ...p, isDeleting: true }));
    socketService.emit("admin:delete-cascade", {
      type: modal.type,
      id: modal.id,
      name: modal.name,
    });
  }, []);

  const handleExportStoryTxt = useCallback((story: Story) => {
    if (story.logs && story.logs.length > 0) {
      downloadStoryTxt(story, story.logs);
      return;
    }
    socketService.emit("get-story-logs", { storyId: story.id }, (logs: LogEntry[]) => {
      const safeLogs = Array.isArray(logs) ? logs : [];
      if (safeLogs.length === 0) {
        // Still export an empty file with the header so the user has feedback.
        downloadStoryTxt(story, []);
        return;
      }
      setStories((prev) => prev.map((s) => (s.id === story.id ? { ...s, logs: safeLogs } : s)));
      downloadStoryTxt(story, safeLogs);
    });
  }, []);

  const toggleStoryForTxtExport = useCallback((storyId: string) => {
    setSelectedStoryExportIds((current) => {
      const next = new Set(current);
      if (next.has(storyId)) next.delete(storyId);
      else next.add(storyId);
      return next;
    });
  }, []);

  const toggleAllVisibleStoriesForTxtExport = useCallback(() => {
    setSelectedStoryExportIds((current) => {
      const next = new Set(current);
      const visibleIds = visibleSelectedEventStories.map((story) => story.id);
      const allVisibleSelected =
        visibleIds.length > 0 && visibleIds.every((storyId) => next.has(storyId));

      for (const storyId of visibleIds) {
        if (allVisibleSelected) next.delete(storyId);
        else next.add(storyId);
      }
      return next;
    });
  }, [visibleSelectedEventStories]);

  const handleExportSelectedStoriesTxt = useCallback(async () => {
    if (isExportingSelectedStories) return;

    const selectedStories = selectedEventStories.filter((story) =>
      selectedStoryExportIds.has(story.id),
    );
    if (selectedStories.length === 0) return;

    setIsExportingSelectedStories(true);
    try {
      const storiesWithLogs = await Promise.all(
        selectedStories.map(
          (story) =>
            new Promise<{ story: Story; logs: LogEntry[] }>((resolve) => {
              if (story.logs && story.logs.length > 0) {
                resolve({ story, logs: story.logs });
                return;
              }

              socketService.emit("get-story-logs", { storyId: story.id }, (logs: LogEntry[]) => {
                resolve({ story, logs: Array.isArray(logs) ? logs : [] });
              });
            }),
        ),
      );

      if (!isMountedRef.current) return;
      const logsByStoryId = new Map(
        storiesWithLogs.map(({ story, logs }) => [story.id, logs] as const),
      );
      setStories((current) =>
        current.map((story) =>
          logsByStoryId.has(story.id) ? { ...story, logs: logsByStoryId.get(story.id) } : story,
        ),
      );
      downloadSelectedStoriesTxt(storiesWithLogs);
    } finally {
      if (isMountedRef.current) setIsExportingSelectedStories(false);
    }
  }, [isExportingSelectedStories, selectedEventStories, selectedStoryExportIds]);

  // List view state (original)
  const [expandedRundownId, setExpandedRundownId] = useState<string | null>(null);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [expandedStoryId, setExpandedStoryId] = useState<string | null>(null);
  const [hoveredListItemId, setHoveredListItemId] = useState<string | null>(null);

  // Panel visibility — plain local state. Each header button is a tab: if its key
  // is in the set the column shows, otherwise it's hidden. That's the whole model.
  // CUT and VTR are mutually exclusive (same Assembly panel, different filter).
  // (No URL persistence: a missing ?panels= used to fall back to the defaults —
  // which include the library — so closing a panel re-opened it on the next render.)
  const defaultPanels = useMemo<Set<string>>(() => {
    const s = new Set<string>();
    if (isEmbedded) s.add("explore");
    else if (isContentDirector) s.add("vtr");
    else s.add("explore");
    return s;
  }, [isEmbedded, isContentDirector]);

  const [openPanels, setOpenPanels] = useState<Set<string>>(() => new Set(defaultPanels));

  const lastProjectRef = useRef<string | null>(null);

  // Guardar paneles activos en localStorage cuando cambien
  useEffect(() => {
    if (!openPanels) return;
    const currentProjId = activeProject?.id || "global";
    if (lastProjectRef.current !== currentProjId) return;

    const key = `braindog_open_panels_${currentProjId}`;
    localStorage.setItem(key, JSON.stringify(Array.from(openPanels)));
  }, [openPanels]);

  // Cargar paneles activos cuando el usuario o el proyecto cambien
  useEffect(() => {
    if (!user) return;
    const currentProjId = activeProject?.id || "global";
    if (lastProjectRef.current === currentProjId) return;
    lastProjectRef.current = currentProjId;

    const key = `braindog_open_panels_${currentProjId}`;
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const arr = JSON.parse(saved);
        if (Array.isArray(arr)) {
          setOpenPanels(new Set(arr));
          return;
        }
      } catch (e) {}
    }
    // Si no hay nada guardado, usar los por defecto
    setOpenPanels(new Set(defaultPanels));
  }, [user, defaultPanels, activeProject?.id]);

  const batchPanels = (mutate: (p: Set<string>) => void) => {
    setOpenPanels((prev) => {
      const next = new Set(prev);
      mutate(next);
      return next;
    });
  };

  const togglePanel = (key: string, value?: boolean | ((prev: boolean) => boolean)) => {
    batchPanels((p) => {
      const cur = p.has(key);
      const next = value === undefined ? !cur : typeof value === "function" ? value(cur) : value;
      if (next) {
        p.add(key);
        if (key === "cut") p.delete("vtr");
        if (key === "vtr") p.delete("cut");
      } else {
        p.delete(key);
      }
    });
  };

  const _localShowAssembly = openPanels.has("cut") || openPanels.has("vtr");
  const showAssembly =
    isEmbedded && externalShowAssembly !== undefined ? externalShowAssembly : _localShowAssembly;
  const showDiscover = openPanels.has("brain");
  // When embedded (Controller "explore" tab) there is no header toggle to reopen
  // the library, so a persisted set without "explore" (saved by a standalone
  // session that closed it) would leave the center blank forever. Force it on.
  const showExplore = isEmbedded || openPanels.has("explore");
  const showPulse = openPanels.has("pulse");
  const assemblyFilter: "all" | "approved" = openPanels.has("vtr") ? "approved" : "all";
  const showSummaries = openPanels.has("summaries");
  const showFeed = openPanels.has("feed");
  // The VIDEO panel is a StoryProducer-only feature. When embedded (Content
  // Director "Library" tab) its header toggle is hidden, so a persisted
  // "videosearch" (saved by a standalone session) would leave the panel open
  // with no way to close it. Never show it while embedded.
  const showVideoSearch = !isEmbedded && openPanels.has("videosearch");

  const setShowDiscover = (v: boolean | ((prev: boolean) => boolean)) => togglePanel("brain", v);
  const setShowExplore = (v: boolean | ((prev: boolean) => boolean)) => togglePanel("explore", v);
  const setShowPulse = (v: boolean | ((prev: boolean) => boolean)) => togglePanel("pulse", v);
  const setShowSummaries = (v: boolean | ((prev: boolean) => boolean)) =>
    togglePanel("summaries", v);
  const setShowFeed = (v: boolean | ((prev: boolean) => boolean)) => togglePanel("feed", v);
  const setShowVideoSearch = (v: boolean | ((prev: boolean) => boolean)) =>
    togglePanel("videosearch", v);

  // Restaurar estado de navegación al cargar los datos
  const didRestoreNavigation = useRef<string | null>(null);

  // Guardar estado de navegación en localStorage cuando cambie
  useEffect(() => {
    if (!user) return;
    const projId = activeProject?.id || "global";
    if (didRestoreNavigation.current !== projId) return;

    localStorage.setItem(`storyproducer_drill_level_${projId}`, drillLevel);
    localStorage.setItem(`storyproducer_selected_rundown_id_${projId}`, selectedRundown?.id || "");
    localStorage.setItem(`storyproducer_selected_event_id_${projId}`, selectedEvent?.id || "");
    localStorage.setItem(`storyproducer_selected_story_id_${projId}`, selectedStory?.id || "");
  }, [drillLevel, selectedRundown, selectedEvent, selectedStory, user]);

  useEffect(() => {
    const currentProjId = activeProject?.id || "global";
    if (!dataReady || rundowns.length === 0 || didRestoreNavigation.current === currentProjId)
      return;
    didRestoreNavigation.current = currentProjId;

    const savedDrillLevel = localStorage.getItem(
      `storyproducer_drill_level_${currentProjId}`,
    ) as DrillLevel | null;
    const savedRundownId = localStorage.getItem(
      `storyproducer_selected_rundown_id_${currentProjId}`,
    );
    const savedEventId = localStorage.getItem(`storyproducer_selected_event_id_${currentProjId}`);
    const savedStoryId = localStorage.getItem(`storyproducer_selected_story_id_${currentProjId}`);

    if (!savedDrillLevel) return;

    let rundownObj: Rundown | null = null;
    let eventObj: RundownItem | null = null;
    let storyObj: Story | null = null;

    // 1. Encontrar Rundown
    if (savedRundownId) {
      rundownObj = rundowns.find((r) => r.id === savedRundownId) || null;
    }

    // 2. Encontrar Evento
    if (rundownObj && savedEventId) {
      eventObj = rundownObj.items?.find((e) => e.id === savedEventId) || null;
    }

    // 3. Encontrar Historia
    if (savedStoryId) {
      storyObj = stories.find((s) => s.id === savedStoryId) || null;
    }

    // Restaurar en cascada
    if (savedDrillLevel === "rundowns") {
      setDrillLevel("rundowns");
      setSelectedRundown(null);
      setSelectedEvent(null);
      setSelectedStory(null);
      setBreadcrumbs([]);
    } else if (savedDrillLevel === "events" && rundownObj) {
      setDrillLevel("events");
      setSelectedRundown(rundownObj);
      setSelectedEvent(null);
      setSelectedStory(null);
      setBreadcrumbs([{ level: "rundowns", id: rundownObj.id, title: rundownObj.name }]);
    } else if (savedDrillLevel === "stories" && rundownObj && eventObj) {
      setDrillLevel("stories");
      setSelectedRundown(rundownObj);
      setSelectedEvent(eventObj);
      setSelectedStory(null);
      setBreadcrumbs([
        { level: "rundowns", id: rundownObj.id, title: rundownObj.name },
        { level: "events", id: eventObj.id, title: eventObj.title },
      ]);
    } else if (savedDrillLevel === "logs" && storyObj) {
      let resolvedRundown = rundownObj;
      let resolvedEvent = eventObj;
      if (!resolvedRundown || !resolvedEvent) {
        for (const r of rundowns) {
          const item = r.items?.find((it) => it.id === storyObj?.rundownId);
          if (item) {
            resolvedRundown = r;
            resolvedEvent = item;
            break;
          }
        }
      }

      setDrillLevel("logs");
      setSelectedStory(storyObj);
      if (resolvedRundown && resolvedEvent) {
        setSelectedRundown(resolvedRundown);
        setSelectedEvent(resolvedEvent);
        setBreadcrumbs([
          { level: "rundowns", id: resolvedRundown.id, title: resolvedRundown.name },
          { level: "events", id: resolvedEvent.id, title: resolvedEvent.title },
          { level: "stories", id: storyObj.id, title: storyObj.title },
        ]);
      } else {
        setBreadcrumbs([{ level: "stories", id: storyObj.id, title: storyObj.title }]);
      }

      const currentStoryId = storyObj.id;
      setIsLoadingStoryLogs(true);
      socketService.emit("get-story-logs", { storyId: currentStoryId }, (logs: LogEntry[]) => {
        const updatedStory = { ...storyObj, logs };
        setSelectedStory((prev) => (prev && prev.id === currentStoryId ? updatedStory : prev));
        setStories((prev) => prev.map((s) => (s.id === currentStoryId ? updatedStory : s)));
        setIsLoadingStoryLogs(false);
      });
      loadTranscriptsForStory(currentStoryId);
    }
  }, [dataReady, rundowns, stories, activeProject?.id]);

  // Restaurar la posición de scroll al cambiar de historia o nivel
  useEffect(() => {
    if (!dataReady) return;
    const storyId = selectedStory?.id || "root";
    const savedScroll = localStorage.getItem(`explore_scroll_${storyId}`);
    if (savedScroll) {
      const scrollVal = parseInt(savedScroll, 10);
      if (!isNaN(scrollVal)) {
        const timer = setTimeout(() => {
          const el = document.getElementById("explore-main-area");
          if (el) el.scrollTop = scrollVal;
        }, 100);
        return () => clearTimeout(timer);
      }
    } else {
      const el = document.getElementById("explore-main-area");
      if (el) el.scrollTop = 0;
    }
  }, [selectedStory?.id, drillLevel, dataReady]);

  // Offline Sync State
  // Leemos el valor inicial de offlineSyncService
  const [offlineCount, setOfflineCount] = useState(() => offlineSyncService.getPendingCount());
  // Initial connection state
  const [isOnline, setIsOnline] = useState(socketService.connected);

  // Sidebar panels count (Discovery left, Assembly right)
  const sidebarPanelCount = [showDiscover, showAssembly].filter(Boolean).length;
  // Explorar gets remaining space, sidebars get fixed width
  const _sidebarWidth = sidebarPanelCount > 0 ? "350px" : "0";

  // State for highlighting text in logs when navigating from Discovery
  const [highlightText, setHighlightText] = useState<string | null>(null);
  const [highlightLogId, setHighlightLogId] = useState<string | null>(null);

  // State for transcript context view (TRANS citation navigation)
  const [transcriptContext, setTranscriptContext] = useState<{
    targetId: string;
    context: TranscriptSegment[];
  } | null>(null);
  const [tcExpandBefore, setTcExpandBefore] = useState(5);
  const [tcExpandAfter, setTcExpandAfter] = useState(5);
  const [tcSelectedSpeakers, setTcSelectedSpeakers] = useState<Set<string>>(new Set());
  // State for segment selection in transcript-context (always active, independent of CUT panel)
  const [tcCtxSelectedIds, setTcCtxSelectedIds] = useState<Set<string>>(new Set());
  const [tcCtxLastClickedIdx, setTcCtxLastClickedIdx] = useState<number>(-1);
  const [tcCtxAddedFeedback, setTcCtxAddedFeedback] = useState(false);
  // State for text highlight drag-to-CUT tooltip
  const [tcCtxHighlight, setTcCtxHighlight] = useState<{
    text: string;
    segId: string;
    x: number;
    y: number;
  } | null>(null);
  const tcCtxContainerRef = useRef<HTMLDivElement>(null);

  const [exploreSelectedSpeakers, setExploreSelectedSpeakers] = useState<Set<string>>(new Set());
  const [exploreExpandCount, setExploreExpandCount] = useState(20);
  const [listStoryContentView, setListStoryContentView] = useState<
    Record<string, "logs" | "transcripts">
  >({});
  const [listExploreSelectedSpeakers, setListExploreSelectedSpeakers] = useState<
    Record<string, Set<string>>
  >({});
  const [listExploreExpandCount, setListExploreExpandCount] = useState<Record<string, number>>({});

  // State for inline transcriptions
  const [storyTranscripts, setStoryTranscripts] = useState<Record<string, TranscriptSegment[]>>({});
  const [totalSegments, setTotalSegments] = useState<number>(0);
  const [expandedTranscriptLogIds, setExpandedTranscriptLogIds] = useState<Set<string>>(new Set());
  const [expandedSegmentIds, setExpandedSegmentIds] = useState<Set<string>>(new Set());

  // Helper: parse TC string "HH:MM:SS" to seconds
  const tcToSeconds = useCallback((tc: string): number => {
    if (!tc) return 0;
    const parts = tc.split(":").map(Number);
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return parts[0] || 0;
  }, []);

  // Helper: get transcripts temporally near a log (±60 seconds)
  const getTranscriptsForLog = useCallback(
    (logTc: string, storyId: string): TranscriptSegment[] => {
      const transcripts = storyTranscripts[storyId];
      if (!transcripts || transcripts.length === 0) return [];
      const logSeconds = tcToSeconds(logTc);
      const WINDOW = 60; // seconds
      return transcripts.filter((t) => {
        // t.tc ya viene como "HH:MM:SS" del backend
        const segSeconds = tcToSeconds(t.tc);
        return Math.abs(segSeconds - logSeconds) <= WINDOW;
      });
    },
    [storyTranscripts, tcToSeconds],
  );

  // Tracks in-flight get-story-transcripts requests to dedupe concurrent calls
  // for the same storyId (happens when URL-sync + autoload race each other).
  const transcriptsInFlightRef = useRef<Set<string>>(new Set());

  // Helper: load transcripts for a story. SIEMPRE pide al servidor (por
  // historia) en cada drill-down — sin caché de sesión. `transcriptsInFlightRef`
  // solo deduplica peticiones concurrentes idénticas (no decide frescura).
  const loadTranscriptsForStory = useCallback((storyId: string) => {
    if (transcriptsInFlightRef.current.has(storyId)) return; // dedupe concurrent fetch
    transcriptsInFlightRef.current.add(storyId);
    socketService.emit("get-story-transcripts", { storyId }, (transcripts: TranscriptSegment[]) => {
      transcriptsInFlightRef.current.delete(storyId);
      if (!isMountedRef.current) return;
      setStoryTranscripts((prev) => ({
        ...prev,
        [storyId]: transcripts || [],
      }));
    });
  }, []);

  // Carga las stories de un evento SIEMPRE desde el servidor (sin caché de
  // sesión): cada drill-down vuelve a pedirlas para que la biblioteca nunca
  // muestre un snapshot viejo. `loadingEventIds` solo deduplica peticiones
  // concurrentes idénticas; no decide frescura.
  const loadEventStories = useCallback(
    (eventId: string) => {
      if (!eventId) return;
      if (loadingEventIds.has(eventId)) return; // dedupe concurrent fetch
      setLoadingEventIds((prev) => new Set([...prev, eventId]));
      socketService.emit("get-event-stories", eventId, (response: any) => {
        if (!isMountedRef.current) return;
        if (response?.success && response.stories) {
          setStories((prev) => {
            const map = new Map(prev.map((s) => [s.id, s]));
            response.stories.forEach((s: any) => {
              // get-event-stories siempre devuelve logs:[] (los logs se cargan
              // aparte). Conservá los logs ya cargados para no vaciar una
              // historia abierta al re-consultar la lista del evento.
              const existing = map.get(s.id);
              map.set(s.id, {
                ...s,
                logs: s.logs && s.logs.length > 0 ? s.logs : (existing?.logs ?? []),
              });
            });
            return Array.from(map.values());
          });
        }
        setLoadingEventIds((prev) => {
          const next = new Set(prev);
          next.delete(eventId);
          return next;
        });
      });
    },
    [loadingEventIds],
  );

  // Toggle transcript expansion for a log
  const toggleTranscriptExpand = useCallback((logId: string) => {
    setExpandedTranscriptLogIds((prev) => {
      const next = new Set(prev);
      if (next.has(logId)) next.delete(logId);
      else next.add(logId);
      return next;
    });
  }, []);

  // Function to clear highlight when user clicks elsewhere
  const clearHighlight = () => {
    if (highlightLogId || highlightText) {
      setHighlightText(null);
      setHighlightLogId(null);
    }
  };

  // Token that invalidates in-flight scroll retry chains when a new
  // citation click arrives — otherwise stacked tryScroll() chains from
  // previous clicks keep firing scrollIntoView and the page cycles
  // between targets until every chain exhausts its 5 retries.
  const scrollNavTokenRef = useRef(0);

  // Centralised: pick the VISIBLE [data-log-id] element (the same log may be
  // rendered in multiple sections — list/cards/etc. — but only one is on
  // screen) and scroll it into view. First attempt waits a frame so React has
  // committed the new highlight; later attempts back off if logs are still
  // hydrating from the server.
  const scrollToLogId = (logId: string) => {
    const navToken = scrollNavTokenRef.current;

    // The user's own wheel/touch/keyboard scroll cancels pending programmatic
    // scrolls — otherwise a delayed scrollIntoView lands while the user is
    // mid-scroll and yanks them back to the card.
    const cancel = () => {
      if (scrollNavTokenRef.current === navToken) scrollNavTokenRef.current += 1;
      cleanup();
    };
    const onKey = (e: KeyboardEvent) => {
      if (["PageUp", "PageDown", "ArrowUp", "ArrowDown", "Home", "End", " "].includes(e.key)) {
        cancel();
      }
    };
    const cleanup = () => {
      window.removeEventListener("wheel", cancel, true);
      window.removeEventListener("touchmove", cancel, true);
      window.removeEventListener("keydown", onKey, true);
    };
    window.addEventListener("wheel", cancel, { passive: true, capture: true });
    window.addEventListener("touchmove", cancel, {
      passive: true,
      capture: true,
    });
    window.addEventListener("keydown", onKey, true);
    setTimeout(cleanup, 2500);

    const findVisible = () => {
      const candidates = document.querySelectorAll(`[data-log-id="${logId}"]`);
      return (Array.from(candidates).find((el) => (el as HTMLElement).offsetParent !== null) ??
        candidates[0]) as HTMLElement | undefined;
    };
    const attempt = (left: number, delay: number) => {
      const run = () => {
        if (scrollNavTokenRef.current !== navToken) return;
        const el = findVisible();
        if (el) {
          el.scrollIntoView({ behavior: "auto", block: "center" });
        } else if (left > 0) {
          attempt(left - 1, delay * 1.5);
        }
      };
      if (delay === 0) requestAnimationFrame(run);
      else setTimeout(run, delay);
    };
    attempt(5, 0);
    // Second pass after layout settles — covers cases where the first scroll
    // landed on a stale layout (e.g. story logs just finished re-rendering
    // when navigating between cards within the same story).
    setTimeout(() => {
      if (scrollNavTokenRef.current !== navToken) return;
      const el = findVisible();
      if (el) el.scrollIntoView({ behavior: "auto", block: "center" });
    }, 150);
  };

  // Function to navigate Explorar to a specific log (called from Discovery)
  const handleNavigateToLog = (
    logId: string,
    storyId: string,
    textToHighlight?: string,
    citationType?: string,
    _citationTc?: string,
  ) => {
    // Invalidate any pending scroll retry chains AND in-flight socket callbacks
    // from previous citation clicks — otherwise an older click's `get-story-detail`
    // or `get-story-logs` callback can resolve after the newer click and stomp
    // back onto highlightLogId, making the previous marker re-light during the
    // 0.2s CSS transition.
    scrollNavTokenRef.current += 1;
    const navToken = scrollNavTokenRef.current;

    // ALWAYS switch to Explore view when navigating
    if (isShowrunner) {
      batchPanels((p) => {
        p.delete("pulse");
        p.add("explore");
      });
    } else {
      setShowExplore(true);
    }

    // Normalize citationType to lowercase (SSE sends 'TRANS', full-context sends 'transcript')
    const normalizedType = citationType?.toLowerCase();

    // TRANS citations: show transcript context view in Explorer
    if (normalizedType === "trans" || normalizedType === "transcript") {
      socketService.emit("get-transcript-context", { segmentId: logId }, (result: any) => {
        if (scrollNavTokenRef.current !== navToken) return;
        if (result && result.context) {
          setTranscriptContext({
            targetId: result.targetId,
            context: result.context,
          });
          setTcExpandBefore(5);
          setTcExpandAfter(5);
          setTcSelectedSpeakers(new Set());
          setDrillLevel("transcript-context");
          setBreadcrumbs([]);
        } else {
          // No transcript context returned for segment
        }
      });
      return;
    }

    const doNavigate = (story: any) => {
      // Find the rundown/event for this story
      let rundown = rundowns.find((r) => r.items?.some((item: any) => item.id === story.rundownId));
      let event: any = null;

      if (rundown) {
        event = rundown.items.find((item: any) => item.id === story.rundownId);
      } else {
        // Fallback: search through ALL items in ALL rundowns
        for (const r of rundowns) {
          for (const item of r.items || []) {
            if (item.id === story.rundownId) {
              rundown = r;
              event = item;
              break;
            }
          }
          if (rundown) break;
        }
      }

      if (!rundown || !event) {
        // Rundown/event not found — fall back to first rundown with matching story
        setDrillLevel("logs");
        setSelectedStory(story);
        setExpandedStoryId(story.id);
        setListStoryContentView((prev) => ({ ...prev, [story.id]: "logs" }));
        setHighlightLogId(logId);
        setHighlightText(textToHighlight ? textToHighlight.substring(0, 80) : null);

        // Even without a rundown match, surface a story-level breadcrumb so the
        // user can navigate back instead of being stuck on a logs view.
        setBreadcrumbs([{ level: "stories", id: story.id, title: story.title }]);

        setSearchParams(
          (prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.delete("rId");
            newParams.delete("eId");
            newParams.set("sId", story.id);
            // Drop the transient citation params now that we've resolved the
            // location — otherwise they linger and the URL-consume effect
            // re-navigates on every render (infinite get-story-detail loop).
            newParams.delete("storyId");
            newParams.delete("logId");
            newParams.delete("type");
            newParams.delete("tc");
            return newParams;
          },
          { replace: true },
        );

        const scrollToLogFallback = () => scrollToLogId(logId);

        // SIEMPRE pedir los logs al servidor (sin caché de sesión).
        socketService.emit("get-story-logs", { storyId: story.id }, (logs: LogEntry[]) => {
          if (scrollNavTokenRef.current !== navToken) return;
          const updatedStory = { ...story, logs };
          setSelectedStory(updatedStory);
          setStories((prev) => prev.map((s) => (s.id === story.id ? updatedStory : s)));
          setIsLoadingStoryLogs(false);
          scrollToLogFallback();
        });
        loadTranscriptsForStory(story.id);
        return;
      }

      // For Card View
      setSelectedRundown(rundown);
      setSelectedEvent(event);
      setDrillLevel("logs");
      setBreadcrumbs([
        { level: "rundowns", id: rundown.id, title: rundown.name },
        { level: "events", id: event.id, title: event.title },
        { level: "stories", id: story.id, title: story.title },
      ]);

      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("rId", rundown.id);
          newParams.set("eId", event.id);
          newParams.set("sId", story.id);
          // Drop the transient citation params now that we've resolved the
          // location — otherwise they linger and the URL-consume effect
          // re-navigates on every render (infinite get-story-detail loop).
          newParams.delete("storyId");
          newParams.delete("logId");
          newParams.delete("type");
          newParams.delete("tc");
          return newParams;
        },
        { replace: true },
      );

      // For List View - expand hierarchy
      setExpandedRundownId(rundown.id);
      setExpandedEventId(event.id);
      setExpandedStoryId(story.id);
      setListStoryContentView((prev) => ({ ...prev, [story.id]: "logs" }));

      // Set highlight
      setHighlightLogId(logId);
      setHighlightText(textToHighlight ? textToHighlight.substring(0, 80) : null);

      // Scroll to log
      const scrollToLog = () => scrollToLogId(logId);

      // SIEMPRE pedir los logs al servidor (sin caché de sesión). Mostramos el
      // shell de la story de inmediato y la rellenamos cuando llegan los logs.
      setSelectedStory(story);
      socketService.emit("get-story-logs", { storyId: story.id }, (logs: LogEntry[]) => {
        if (scrollNavTokenRef.current !== navToken) return;
        const updatedStory = { ...story, logs };
        setSelectedStory(updatedStory);
        setStories((prev) => prev.map((s) => (s.id === story.id ? updatedStory : s)));
        setIsLoadingStoryLogs(false);
        scrollToLog();
      });
      loadTranscriptsForStory(story.id);
    };

    // ALWAYS fetch from server to guarantee freshest data
    setIsLoadingStoryLogs(true);
    socketService.emit("get-story-detail", { storyId }, (storyData: any) => {
      if (scrollNavTokenRef.current !== navToken) return;
      if (storyData) {
        // Add or update in local stories
        setStories((prev) => {
          const index = prev.findIndex((s) => s.id === storyData.id);
          if (index !== -1) {
            const newStories = [...prev];
            newStories[index] = storyData;
            return newStories;
          }
          return [...prev, storyData];
        });
        doNavigate(storyData);
      } else {
        console.error("❌ Story not found on server:", storyId);
        setIsLoadingStoryLogs(false);
      }
    });
  };

  // Format summary helper

  // Listen for navigate-to-source requests from CUT module
  React.useEffect(() => {
    if (navigateToSource) {
      const { storyId, itemId, itemType } = navigateToSource;
      handleNavigateToLog(
        itemId,
        storyId,
        undefined,
        itemType === "transcript" ? "trans" : undefined,
      );
      clearNavigateToSource();
    }
  }, [navigateToSource]);

  // Handle cross-module navigation from Discovery via URL query params
  // e.g., /storyproducer?storyId=xxx&logId=yyy&type=log&tc=08:30:15
  // Uses a ref to prevent re-processing the same navigation (avoids infinite loop
  // since handleNavigateToLog modifies stories which is in the dep array).
  const lastConsumedNavRef = useRef<string | null>(null);
  useEffect(() => {
    const storyId = searchParams.get("storyId");
    const logId = searchParams.get("logId");
    if (!storyId || !logId) {
      // Params cleared (or never set): reset the dedup guard so clicking the
      // same reference again re-triggers navigation instead of silently no-op'ing.
      lastConsumedNavRef.current = null;
      return;
    }
    // Wait until rundowns have actually loaded — otherwise doNavigate's
    // rundown lookup fails and we land in the no-breadcrumb fallback.
    if (!dataReady || rundowns.length === 0) return;

    // Prevent re-processing the same navigation params
    const navKey = `${storyId}::${logId}`;
    if (lastConsumedNavRef.current === navKey) return;
    lastConsumedNavRef.current = navKey;

    const citationType = searchParams.get("type") || undefined;
    const citationTc = searchParams.get("tc") || undefined;

    handleNavigateToLog(logId, storyId, undefined, citationType, citationTc);

    // Clear params to prevent re-navigation while preserving other search params (like tab)
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("storyId");
        newParams.delete("logId");
        newParams.delete("type");
        newParams.delete("tc");
        return newParams;
      },
      { replace: true },
    );
  }, [searchParams, stories, rundowns, dataReady]);

  // Sync URL params to State (for direct linking and back button on explore tab)
  useEffect(() => {
    if (searchParams.get("logId")) return; // handled by deeper navigation
    if (!dataReady || rundowns.length === 0) return; // wait for fresh data from server

    const rId = searchParams.get("rId");
    const eId = searchParams.get("eId");
    const sId = searchParams.get("sId");

    const currentRId = selectedRundown?.id || null;
    const currentEId = selectedEvent?.id || null;
    const currentSId = selectedStory?.id || null;

    if (rId === currentRId && eId === currentEId && sId === currentSId) {
      return; // in sync
    }

    if (!rId) {
      if (sId) {
        // If we have an sId but no rId/eId, it's a direct log or standalone story navigation
        // Don't reset the UI, just return
        return;
      }
      setDrillLevel("rundowns");
      setBreadcrumbs([]);
      if (selectedRundown) setSelectedRundown(null);
      if (selectedEvent) setSelectedEvent(null);
      if (selectedStory) setSelectedStory(null);
      return;
    }

    const rundown = rundowns.find((r) => r.id === rId);
    if (!rundown) return;

    let nextDrillLevel: DrillLevel = "events";
    const nextBreadcrumbs: BreadcrumbItem[] = [
      { level: "rundowns", id: rundown.id, title: rundown.name },
    ];
    let nextEvent: any = null;
    let nextStory: any = null;

    if (eId) {
      nextEvent = rundown.items?.find((e: any) => e.id === eId);
      if (nextEvent) {
        nextDrillLevel = "stories";
        nextBreadcrumbs.push({
          level: "events",
          id: nextEvent.id,
          title: nextEvent.title,
        });

        if (!nextEvent.summary) requestEventSummary(nextEvent.id);
        loadEventStories(nextEvent.id);
      }
    }

    if (nextEvent && sId) {
      nextStory = stories.find((s) => s.id === sId);
      if (nextStory) {
        nextDrillLevel = "logs";
        nextBreadcrumbs.push({
          level: "stories",
          id: nextStory.id,
          title: nextStory.title,
        });

        // SIEMPRE pedir los logs al servidor en cada drill-down (sin caché).
        setIsLoadingStoryLogs(true);
        socketService.emit("get-story-logs", { storyId: nextStory.id }, (logs: LogEntry[]) => {
          if (!isMountedRef.current) return;
          const updatedStory = { ...nextStory, logs };
          setSelectedStory(updatedStory);
          setStories((prev) => prev.map((s) => (s.id === nextStory.id ? updatedStory : s)));
          setIsLoadingStoryLogs(false);
        });
        loadTranscriptsForStory(nextStory.id);
      } else {
        setIsLoadingStoryLogs(true);
        socketService.emit("get-story-detail", { storyId: sId }, (storyData: any) => {
          if (!isMountedRef.current) return;
          if (storyData) {
            setStories((prev) => {
              if (prev.find((s) => s.id === storyData.id)) return prev;
              return [...prev, storyData];
            });
          } else {
            setIsLoadingStoryLogs(false);
          }
        });
      }
    }

    // Always refresh from the latest rundowns list — even when ids match —
    // so an updated summary/title propagates into the banner instead of being
    // pinned to the snapshot captured when the selection was first made.
    setSelectedRundown(rundown);
    if (nextEvent) setSelectedEvent(nextEvent);
    else if (currentEId) setSelectedEvent(null);

    if (nextStory) {
      if (nextStory.id !== currentSId) {
        setSelectedStory(nextStory);
        setExploreSelectedSpeakers(new Set());
        setExploreExpandCount(20);
      }
    } else if (!sId && currentSId) {
      setSelectedStory(null);
    }

    setDrillLevel(nextDrillLevel);
    setBreadcrumbs(nextBreadcrumbs);
  }, [
    searchParams,
    rundowns,
    stories,
    selectedRundown,
    selectedEvent,
    selectedStory,
    dataReady,
    loadEventStories,
    loadTranscriptsForStory,
  ]);

  // Auto-scroll to cited segment when transcript-context view opens
  React.useEffect(() => {
    if (drillLevel === "transcript-context" && transcriptContext?.targetId) {
      const scrollToCited = () => {
        const el = document.querySelector(`[data-transcript-id="${transcriptContext.targetId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      };
      // Delay to allow DOM rendering
      setTimeout(scrollToCited, 400);
    }
  }, [drillLevel, transcriptContext?.targetId]);

  const formatSummary = useCallback(
    (text: string): React.ReactNode => {
      if (!text) return "";
      let cleaned = text;

      // Extract content between [resumen] tags if they exist
      const resumenMatch = cleaned.match(/\[resumen\]([\s\S]*?)\[\/resumen\]/i);
      let formatted = "";

      if (resumenMatch && resumenMatch[1]) {
        formatted = resumenMatch[1].trim();
        // Clean @N-NOMBRE mentions → just NOMBRE
        formatted = formatted.replace(/@\d+-/g, "");
        formatted = formatted.toLowerCase().replace(/^.|\.\s+./g, (c: string) => c.toUpperCase());
      } else {
        // Remove bracketed IDs at the start like "[rundown_b836c018 | día 10]"
        cleaned = cleaned.replace(/^\[[\w_]+\s*\|[^\]]*\]\s*/gi, "");
        // Remove trailing metadata sections
        cleaned = cleaned.replace(
          /\s*(eventos del d[ií]a|events of the day)\s*[:：]\s*(\[.*)?$/gis,
          "",
        );
        cleaned = cleaned.replace(/\s*participantes destacados\s*[:：]\s*(\[.*)?$/gis, "");
        cleaned = cleaned.replace(/\s*keywords\s*[:：]\s*.*$/gis, "");
        cleaned = cleaned.replace(/\s*palabras clave\s*[:：]\s*.*$/gis, "");
        // Remove trailing "logs:" references (e.g., "logs: log_f5cc1f2f, log_4, ...")
        cleaned = cleaned.replace(/\s*logs?\s*[:：]\s*(log_[\w,\s]+.*)?$/gis, "");
        // Remove story/event/rundown ID lists (e.g., "stories incluidas: [story_6b104e28, ...]")
        cleaned = cleaned.replace(
          /\]?\s*stories?\s*(incluidas|relacionadas|asociadas|vinculadas)?\s*[:：]\s*[\w_,\s]*\]?/gi,
          "",
        );
        cleaned = cleaned.replace(
          /\s*\[?(story|rundown|event|milestone|log)_[\w]{4,}[\w_,\s]*(,\s*(story|rundown|event|milestone|log)_[\w]{4,})*\]?\s*/gi,
          "",
        );
        // Remove stray leading brackets like "[el evento..."
        cleaned = cleaned.replace(/^\[(?![^\]]*\])/, "");
        // Remove any remaining metadata-style lines
        const metadataPatterns =
          /^(\*{0,2})(participantes|participants|ubicación|ubicacion|location|fecha|date|duración|duracion|duration|temporada|season|episodio|episode|programa|program|tipo|type|categoría|categoria|category|rating|concursantes|contestants|competidores|competitors|jueces|judges|presentador|host|género|genero|genre|formato|format|canal|channel|cadena|network|producción|produccion|production|emisión|emision|air date|sinopsis|synopsis)\s*(\*{0,2})\s*[:：]/i;
        const lines = cleaned.split("\n");
        const narrativeLines = lines.filter((line) => {
          const trimmed = line.trim();
          if (!trimmed) return false;
          if (metadataPatterns.test(trimmed)) return false;
          if (/^[-•]\s*\w+\s*[:：]/.test(trimmed) && trimmed.length < 100) return false;
          return true;
        });
        formatted = narrativeLines.join(" ").replace(/\s+/g, " ").trim();
        // Clean @N-NOMBRE mentions → just NOMBRE
        formatted = formatted.replace(/@\d+-/g, "");
        formatted = formatted.toLowerCase().replace(/^.|\.\s+./g, (c: string) => c.toUpperCase());
      }

      // Normalize citation references: strip emoji prefixes like [📋 LOG-N] → [LOG-N]
      formatted = formatted.replace(/\[([^\]]*?)(LOG|TRANS|OTF)-(\d+)\]/gi, "[$2-$3]");
      // Expand grouped citations: [LOG-1, LOG-2] → [LOG-1][LOG-2]
      formatted = formatted.replace(/\[((?:(?:LOG|TRANS|OTF)-\d+,?\s*)+)\]/gi, (match) => {
        const inner = match.slice(1, -1);
        return inner
          .split(/,\s*/)
          .map((p) => `[${p.trim()}]`)
          .join("");
      });

      // Helper to render a citation badge
      const renderCitationBadge = (prefix: string, num: string, key: string) => {
        const isOtf = prefix === "OTF";
        const isLog = prefix === "LOG";
        // Colors matching log entry scheme: LOG=pink, TRANS=purple, OTF=cyan
        const bgColor = isOtf ? "#00e5ff" : isLog ? "#f72d60" : "#9c27b0";
        return (
          <sup
            key={key}
            title={`${prefix}-${num}`}
            style={{
              background: bgColor,
              color: "#fff",
              padding: "1px 5px",
              borderRadius: "5px",
              fontSize: "0.6em",
              fontWeight: 700,
              marginLeft: "2px",
              marginRight: "2px",
              display: "inline-block",
              lineHeight: "1.2",
              opacity: 0.9,
              userSelect: "none",
              letterSpacing: "0.3px",
            }}
          >
            {num}
          </sup>
        );
      };

      // Helper to process text for citations within a text fragment
      const processCitations = (text: string, baseKey: number | string = 0): React.ReactNode[] => {
        if (!/\[(LOG|TRANS|OTF)-\d+\]/i.test(text)) return [text];
        const citParts = text.split(/(\[(?:LOG|TRANS|OTF)-\d+\])/gi);
        const nodes: React.ReactNode[] = [];
        citParts.forEach((cp, ci) => {
          const cm = cp.match(/^\[(LOG|TRANS|OTF)-(\d+)\]$/i);
          if (cm) {
            nodes.push(renderCitationBadge(cm[1].toUpperCase(), cm[2], `cit-${baseKey}-${ci}`));
          } else if (cp) {
            nodes.push(cp);
          }
        });
        return nodes;
      };

      // Build a list of all participant name variants (full name, alias)
      // Strip numeric prefixes like "4-" and filter out technical names like "OTF_MIC_*"
      const nameTokens: string[] = [];
      participants.forEach((p: any) => {
        // Clean name: remove leading "N-" prefix (e.g., "4-ALDO GUTIÉRREZ" → "ALDO GUTIÉRREZ")
        const cleanName = (p.name || "").replace(/^\d+-/, "").trim();
        // Skip technical/mic names and very short names
        if (
          cleanName &&
          cleanName.length > 3 &&
          !/^OTF_/i.test(cleanName) &&
          !/^MIC_/i.test(cleanName)
        ) {
          nameTokens.push(cleanName);
        }
        // Alias
        const cleanAlias = (p.alias || "").replace(/^\d+-/, "").trim();
        if (
          cleanAlias &&
          cleanAlias.length > 3 &&
          !/^OTF_/i.test(cleanAlias) &&
          !/^MIC_/i.test(cleanAlias)
        ) {
          nameTokens.push(cleanAlias);
        }
      });

      if (nameTokens.length === 0) {
        // No participant names, process cameras then citations
        const camResult = processCameras(formatted);
        // Post-process camera results for citations
        const finalResult: React.ReactNode[] = [];
        (Array.isArray(camResult) ? camResult : [camResult]).forEach((node, ni) => {
          if (typeof node === "string") {
            finalResult.push(...processCitations(node, `cam-${ni}`));
          } else {
            finalResult.push(node);
          }
        });
        return finalResult;
      }

      // Sort longest first to avoid partial matches
      nameTokens.sort((a, b) => b.length - a.length);
      // Escape special regex chars and build OR pattern — use case-insensitive match without \b (since \b fails with accented chars)
      const escaped = nameTokens.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
      const nameRegex = new RegExp(`(${escaped.join("|")})`, "gi");

      const parts = formatted.split(nameRegex).filter((p) => p !== "");
      const nameLower = new Set(nameTokens.map((n) => n.toLowerCase()));

      // Key counter for React keys
      let keyIdx = 0;
      const result: React.ReactNode[] = [];
      parts.forEach((part) => {
        if (nameLower.has(part.toLowerCase())) {
          result.push(
            <span key={`n${keyIdx++}`} style={{ fontWeight: 700, color: "#fff" }}>
              {part.toUpperCase()}
            </span>,
          );
        } else {
          // For non-name text, process camera references then citations
          const camNodes = processCameras(part, keyIdx++);
          (Array.isArray(camNodes) ? camNodes : [camNodes]).forEach((node, ni) => {
            if (typeof node === "string") {
              result.push(...processCitations(node, `pc-${keyIdx}-${ni}`));
            } else {
              result.push(node);
            }
          });
        }
      });
      return result;
    },
    [participants],
  );

  // Helper: process camera references in text and render as orange badges
  const processCameras = (text: string, baseKey: number = 0): React.ReactNode[] => {
    // Match camera patterns: &a01, &amp;a01, &cam b02, cam c04, beautie a04, tracevrelin &e03
    // Captures: optional prefix (&amp;|&) + optional "cam" + camera code (letter(s) + digits)
    const cameraRegex = /(?:&amp;|&)?(?:cam\s*)?(?:beautie\s+|tracevrelin\s+)?([a-e])(\d{2,3})/gi;
    const result: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = cameraRegex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        result.push(text.substring(lastIndex, match.index));
      }
      // Build camera label
      const camLetter = match[1].toUpperCase();
      const camNumber = match[2];
      const camLabel = `CAM ${camLetter}${camNumber}`;
      result.push(
        <span
          key={`c${baseKey}_${match.index}`}
          style={{
            background: "rgba(250, 204, 21, 0.15)",
            color: "#FACC15",
            padding: "1px 6px",
            borderRadius: "4px",
            fontSize: "0.75em",
            fontWeight: 700,
            letterSpacing: "0.5px",
            whiteSpace: "nowrap",
          }}
        >
          {camLabel}
        </span>,
      );
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last match
    if (lastIndex < text.length) {
      result.push(text.substring(lastIndex));
    }

    return result.length > 0 ? result : [text];
  };

  // Clear state when project changes to prevent cross-project contamination
  const prevProjectIdRef = useRef<string | null>(null);
  useEffect(() => {
    const currentPid = activeProject?.id || null;
    if (prevProjectIdRef.current && prevProjectIdRef.current !== currentPid) {
      // Project changed — clear stale data immediately
      setRundowns([]);
      setStories([]);
      setParticipants([]);
      setDataReady(false);
      setSelectedRundown(null);
      setSelectedEvent(null);
      setSelectedStory(null);
      setDrillLevel("rundowns");
      setBreadcrumbs([]);
    }
    prevProjectIdRef.current = currentPid;
  }, [activeProject?.id]);

  // Redirect to projects page if no active project
  useEffect(() => {
    if (!activeProject?.id && !isEmbedded && !isProjectLoading) {
      navigate("/projects");
    }
  }, [activeProject?.id, isProjectLoading, isEmbedded, navigate]);

  // Fetch data on mount — uses lightweight get-explore-data endpoint
  // instead of piggybacking on the heavy init-data (which includes staff,
  // banks, groups, loggers, tags, cameras, etc. that we don't need).
  useEffect(() => {
    if (!activeProject?.id) {
      return;
    }

    const projectId = activeProject.id;

    const handleRundownsList = (data: Rundown[]) => {
      setRundowns(data);
      // Re-sync current selections from the fresh list so banners/breadcrumbs
      // don't render off a stale snapshot (e.g. summary text from a prior day).
      setSelectedRundown((prev) => (prev ? (data.find((r) => r.id === prev.id) ?? prev) : prev));
      setSelectedEvent((prev) => {
        if (!prev) return prev;
        for (const r of data) {
          const ev = r.items?.find((it: any) => it.id === prev.id);
          if (ev) return ev;
        }
        return prev;
      });
    };
    // Merge an updated story into the currently selectedStory if it matches.
    // selectedStory is independent local state and the logs panel reads from
    // it directly (StoryProducer.tsx:3179), so without this the right-side
    // panel stays stale even though `stories` got updated.
    const syncSelectedFromUpdated = (updated: Story) => {
      setSelectedStory((prev) => {
        if (!prev || prev.id !== updated.id) return prev;
        const mergedLogs = updated.logs && updated.logs.length > 0 ? updated.logs : prev.logs;
        const storyCameras = updated.cameras ?? prev.cameras ?? [];
        // Propagate story-level fields into each log so the metadata panel
        // reflects changes without a full reload.
        const logsWithMeta = mergedLogs?.map((l: any) => {
          // Entry-level streams take priority; fall back to story cameras
          const entryStreams = l.streams?.length ? l.streams : l.cameras?.length ? l.cameras : null;
          const effectiveStreams = entryStreams || storyCameras;
          return {
            ...l,
            storyRating: updated.rating ?? prev.rating ?? l.storyRating ?? 0,
            streams: effectiveStreams,
            storyTags: updated.tags ?? prev.tags ?? l.storyTags ?? [],
            storyParticipants:
              updated.participants ?? prev.participants ?? l.storyParticipants ?? [],
          };
        });
        return {
          ...prev,
          ...updated,
          logs: logsWithMeta || prev.logs,
        };
      });
    };

    const handleStoriesUpdate = (data: any) => {
      if (data.action === "deleted") {
        setStories((prev) => prev.filter((s) => s.id !== data.storyId));
        setSelectedStory((prev) => (prev && prev.id === data.storyId ? null : prev));
      } else if (data.story) {
        setStories((prev) => {
          const idx = prev.findIndex((s) => s.id === data.story.id);
          const next = [...prev];
          if (idx >= 0) next[idx] = data.story;
          else next.push(data.story);
          return next;
        });
        syncSelectedFromUpdated(data.story);
      } else if (data.stories && Array.isArray(data.stories)) {
        // Update multiple or ALL stories for the whole project
        const rundownId = data.rundownId;
        if (rundownId) {
          setStories((prev) => {
            const prevById = new Map(prev.map((s) => [s.id, s]));
            const others = prev.filter((s) => s.rundownId !== rundownId);
            // Conservá los logs ya cargados: el payload realtime de un update a
            // nivel de evento trae logs:[]/parcial y, sin esto, vaciaría la
            // historia que el usuario está viendo en la lista.
            const merged = (data.stories as Story[]).map((s) => {
              const existing = prevById.get(s.id);
              return {
                ...s,
                logs: s.logs && s.logs.length > 0 ? s.logs : (existing?.logs ?? []),
              };
            });
            return [...others, ...merged];
          });
        } else {
          // Sin rundownId, es un update parcial (merge)
          setStories((prev) => {
            const updateMap = new Map<string, Story>(data.stories.map((s: Story) => [s.id, s]));
            const updatedPrev = prev.map((story) => {
              const updated = updateMap.get(story.id);
              if (updated) {
                const mergedLogs =
                  updated.logs && updated.logs.length > 0 ? updated.logs : story.logs;
                const storyCameras = updated.cameras ?? story.cameras ?? [];
                // Propagate story-level fields to cached logs
                const logsWithMeta = mergedLogs?.map((l: any) => {
                  const entryStreams = l.streams?.length
                    ? l.streams
                    : l.cameras?.length
                      ? l.cameras
                      : null;
                  return {
                    ...l,
                    storyRating: updated.rating ?? story.rating ?? l.storyRating ?? 0,
                    streams: entryStreams || storyCameras,
                    storyTags: updated.tags ?? story.tags ?? l.storyTags ?? [],
                    storyParticipants:
                      updated.participants ?? story.participants ?? l.storyParticipants ?? [],
                  };
                });
                return {
                  ...story,
                  ...updated,
                  logs: logsWithMeta || story.logs,
                };
              }
              return story;
            });
            const newStories = data.stories.filter((s: any) => !prev.some((p) => p.id === s.id));
            return [...updatedPrev, ...newStories];
          });
        }
        // Also sync selectedStory so the logs panel re-renders in realtime
        // when the logger emits update-stories (the main path for live logs).
        (data.stories as Story[]).forEach(syncSelectedFromUpdated);
      }
    };
    const handleParticipantsUpdate = (data: any[]) => {
      // Accept any valid array — this event comes from intentional user actions (add/edit/delete)
      if (Array.isArray(data)) {
        setParticipants(data);
      }
    };

    // Realtime: a logger created a new log on a story — append it to the
    // matching story's logs (and to selectedStory if it's the one being viewed)
    // so it shows up in BIBLIOTECA without needing a refresh.
    const handleLogAdded = (rawLog: any) => {
      if (!rawLog?.storyId || !rawLog?.id) return;
      // The realtime `log-added` payload is built from a logger client and does
      // NOT pass through the server log normalizer — parse it at this boundary
      // so a malformed tc/text can't crash LogCard.
      const newLog = parseLog(rawLog) as unknown as LogEntry;
      const appendLog = (logs: LogEntry[] = []) =>
        logs.some((l) => l.id === newLog.id) ? logs : [...logs, newLog];
      setStories((prev) =>
        prev.map((s) => (s.id === rawLog.storyId ? { ...s, logs: appendLog(s.logs) } : s)),
      );
      setSelectedStory((prev) =>
        prev && prev.id === rawLog.storyId ? { ...prev, logs: appendLog(prev.logs) } : prev,
      );
    };

    // Listen for live updates (mutations from other users)
    const handleRundownRenamed = (data: { id: string; name: string }) => {
      setRundowns((prev) => prev.map((r) => (r.id === data.id ? { ...r, name: data.name } : r)));
      setSelectedRundown((prev) =>
        prev && prev.id === data.id ? { ...prev, name: data.name } : prev,
      );
    };

    socketService.on("rundowns-list-updated", handleRundownsList);
    socketService.on("rundown-renamed", handleRundownRenamed);
    socketService.on("stories-updated", handleStoriesUpdate);
    // stories-saved is emitted only to the sender (same socket) so the
    // StoryProducer gets updates from the Logger without a feedback loop.
    socketService.on("stories-saved", handleStoriesUpdate);
    socketService.on("log-added", handleLogAdded);
    socketService.on("participants-updated", handleParticipantsUpdate);

    // Fetch explore data directly via callback — pass projectId explicitly
    // to avoid the race condition where this fires before select-project
    // sets socket.projectId on the server.
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const fetchExploreData = (attempt = 1) => {
      // Don't emit if socket is not connected — wait for the onConnect handler
      if (!socketService.connected) {
        console.warn(
          `[StoryProducer] Socket not connected on attempt ${attempt}, waiting for connection…`,
        );
        // Schedule a retry — the onConnect handler below will also trigger a fetch
        if (attempt < 5) {
          retryTimer = setTimeout(() => fetchExploreData(attempt + 1), attempt * 1000);
        } else {
          console.error(
            "[StoryProducer] get-explore-data: socket never connected after 5 attempts",
          );
          setDataReady(true); // Unblock UI so user sees empty state instead of infinite loading
        }
        return;
      }

      socketService.emit("get-explore-data", { projectId, skipStats: true }, (response: any) => {
        if (cancelled) return;
        if (response?.success) {
          if (response.rundowns) setRundowns(response.rundowns);
          if (response.participants) setParticipants(response.participants);
          setDataReady(true);
        } else {
          console.warn(
            `[StoryProducer] get-explore-data attempt ${attempt} failed:`,
            response?.error,
          );
          // Retry up to 5 times with increasing delay — covers the case where
          // the socket just connected and select-project hasn't been processed yet
          if (attempt < 5) {
            retryTimer = setTimeout(() => fetchExploreData(attempt + 1), attempt * 800);
          } else {
            console.error("[StoryProducer] get-explore-data failed after 5 attempts");
            setDataReady(true); // Unblock UI so user sees empty state instead of infinite loading
          }
        }
      });
    };

    // If socket connects (or reconnects) after mount, trigger a fetch
    const onConnect = () => {
      if (cancelled) return;
      console.debug("[StoryProducer] Socket connected — fetching explore data");
      // Small delay to let select-project be processed first
      retryTimer = setTimeout(() => fetchExploreData(1), 300);
    };
    socketService.on("connect", onConnect);

    fetchExploreData();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      socketService.off("connect", onConnect);
      socketService.off("rundowns-list-updated", handleRundownsList);
      socketService.off("rundown-renamed", handleRundownRenamed);
      socketService.off("stories-updated", handleStoriesUpdate);
      socketService.off("stories-saved", handleStoriesUpdate);
      socketService.off("log-added", handleLogAdded);
      socketService.off("participants-updated", handleParticipantsUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject?.id]);

  // Offline status effect
  useEffect(() => {
    const handleOfflineUpdate = (e: any) => {
      setOfflineCount(e.detail.count);
    };
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    // Listen to UI custom event for queue size updates
    window.addEventListener("offline-sync-updated", handleOfflineUpdate);

    // Track whether this is the initial connection or a reconnection
    let hasConnectedOnce = false;

    // Use Socket real status instead of browser API to prevent false positives in dev
    const unsubSocket = socketService.onConnectionChange((connected) => {
      setIsOnline(connected);

      if (connected) {
        // Read pending count again after a delay if it cleared
        setTimeout(() => {
          setOfflineCount(offlineSyncService.getPendingCount());
        }, 1500);

        // Only re-fetch on RECONNECTION, not initial connection
        // (the fetch effect already emits get-explore-data on mount)
        if (hasConnectedOnce && activeProject?.id) {
          socketService.emit(
            "get-explore-data",
            { projectId: activeProject.id, skipStats: true },
            (response: any) => {
              if (response?.success) {
                if (response.rundowns) setRundowns(response.rundowns);
                if (response.participants) setParticipants(response.participants);
                setDataReady(true);
              }
            },
          );
        }
        hasConnectedOnce = true;
      } else {
        setOfflineCount(offlineSyncService.getPendingCount());
        // Invalidate data readiness — URL sync should wait for fresh init-data after reconnect
        setDataReady(false);
      }
    });

    // Backup browser listeners just in case
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("offline-sync-updated", handleOfflineUpdate);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      unsubSocket();
    };
  }, []);

  // Keyboard navigation (Escape = go back one level)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only in cards view
      if (viewMode !== "cards") return;

      // Escape = go back one level in hierarchy
      if (e.key === "Escape") {
        e.preventDefault();

        if (drillLevel === "logs" || drillLevel === "transcript-context") {
          updateUrlParams(selectedRundown?.id || null, selectedEvent?.id || null, null);
        } else if (drillLevel === "stories") {
          updateUrlParams(selectedRundown?.id || null, null, null);
        } else if (drillLevel === "events") {
          updateUrlParams(null, null, null);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [viewMode, drillLevel]);

  // Get stories for a specific event
  const getStoriesForEvent = (eventId: string) => {
    return stories.filter((s) => s.rundownId === eventId && s.status !== "archived");
  };

  // Calculate global project totals
  const globalStats = useMemo(() => {
    const rundownCount = rundowns.length;
    let eventCount = 0;
    let storyCount = 0;
    let logCount = 0;
    let transcriptCount = 0;
    let otfCount = 0;

    rundowns.forEach((r) => {
      eventCount += r.items?.length || 0;
      storyCount += r.stats?.stories || 0;
      logCount += r.stats?.logs || 0;
      transcriptCount += r.stats?.transcripts || 0;
      otfCount += r.stats?.otfs || 0;
    });

    return {
      rundowns: rundownCount,
      events: eventCount,
      stories: storyCount,
      logs: logCount,
      transcripts: transcriptCount || totalSegments,
      otfs: otfCount,
    };
  }, [rundowns, totalSegments]);

  // Request AI summary for an event if missing
  const requestEventSummary = (eventId: string) => {
    socketService.emit("ai:summary", { rundownId: eventId });
  };

  // Open the inline log editor (library, option A). Prefills with the log's
  // current text/title.
  // Persist an inline log-text edit (triggered by clicking a LogCard's text and
  // saving in place): optimistic local update of the containing story, keep
  // selectedStory/editingStory in sync, then emit update-stories (the server
  // upserts the log content — see server/events/story-events.js update-stories).
  const handleInlineSaveLogText = (storyId: string, log: any, newText: string) => {
    const story = stories.find((s) => s.id === storyId);
    if (!story) return;
    const trimmed = (newText || "").trim();
    const updatedStory = {
      ...story,
      logs: (story.logs || []).map((l: any) => (l.id === log.id ? { ...l, text: trimmed } : l)),
    };
    setStories((prev) => prev.map((s) => (s.id === storyId ? updatedStory : s)));
    if (selectedStory?.id === storyId) setSelectedStory(updatedStory);
    if (editingStory?.id === storyId) setEditingStory(updatedStory);
    socketService.emit("update-stories", [updatedStory]);
  };

  // Handle story update
  const handleUpdateStory = (story: Story) => {
    // Add notesAuthor if notes exist and no author yet
    const updatedStory =
      story.producerNotes && !story.notesAuthor
        ? { ...story, notesAuthor: user?.name || UNKNOWN_SPEAKER }
        : story;
    const handleUpdateStoryNotes = (updatedStory: Story) => {
      // Optimistic Update
      setStories((prev) => {
        const index = prev.findIndex((s) => s.id === updatedStory.id);
        if (index >= 0) {
          const next = [...prev];
          next[index] = { ...next[index], ...updatedStory };
          return next;
        }
        return prev;
      });
      if (selectedStory && selectedStory.id === updatedStory.id) {
        setSelectedStory({ ...selectedStory, ...updatedStory });
      }

      socketService.emit("update-story-notes", {
        storyId: updatedStory.id,
        producerNotes: updatedStory.producerNotes || "",
        notesAuthor: updatedStory.notesAuthor || "",
      });
    };
    handleUpdateStoryNotes(updatedStory);
    setEditingStory(null);
  };

  // Handle event update (for producer notes)
  const handleUpdateEvent = (event: RundownItem) => {
    if (!selectedRundown) return;

    // Add notesAuthor if notes exist and no author yet
    const updatedEvent =
      event.producerNotes && !event.notesAuthor
        ? { ...event, notesAuthor: user?.name || UNKNOWN_SPEAKER }
        : event;

    // Update the rundown with the modified event
    const updatedItems = selectedRundown.items.map((e) =>
      e.id === updatedEvent.id ? updatedEvent : e,
    );
    const updatedRundown = { ...selectedRundown, items: updatedItems };

    // Update local state
    setSelectedRundown(updatedRundown);
    setRundowns(rundowns.map((r) => (r.id === updatedRundown.id ? updatedRundown : r)));

    // Also update selectedEvent if it's the one being edited
    if (selectedEvent?.id === updatedEvent.id) {
      setSelectedEvent(updatedEvent);
    }

    const handleUpdateEventNotes = (updatedEvent: RundownItem) => {
      // Optimistic Update
      setRundowns((prev) => {
        return prev.map((rd) => ({
          ...rd,
          items: rd.items.map((item) =>
            item.id === updatedEvent.id ? { ...item, ...updatedEvent } : item,
          ),
        }));
      });
      if (selectedEvent && selectedEvent.id === updatedEvent.id) {
        setSelectedEvent({ ...selectedEvent, ...updatedEvent });
      }

      socketService.emit("update-event-notes", {
        eventId: updatedEvent.id,
        producerNotes: updatedEvent.producerNotes || "",
        notesAuthor: updatedEvent.notesAuthor || "",
      });
    };
    handleUpdateEventNotes(updatedEvent);
    setEditingEvent(null);
  };

  const updateUrlParams = (rId: string | null, eId: string | null, sId: string | null) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (rId) params.set("rId", rId);
      else params.delete("rId");
      if (eId) params.set("eId", eId);
      else params.delete("eId");
      if (sId) params.set("sId", sId);
      else params.delete("sId");
      // Drop cross-module drill params so the URL-sync effects re-engage on user navigation.
      params.delete("storyId");
      params.delete("logId");
      params.delete("type");
      params.delete("tc");
      return params;
    });
  };

  // Card navigation handlers
  const handleDrillDown = (level: DrillLevel, item: any) => {
    if (level === "rundowns") {
      updateUrlParams(item.id, null, null);
    } else if (level === "events") {
      updateUrlParams(selectedRundown?.id || null, item.id, null);
    } else if (level === "stories") {
      updateUrlParams(selectedRundown?.id || null, selectedEvent?.id || null, item.id);
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      // Home
      updateUrlParams(null, null, null);
    } else {
      const bc = breadcrumbs[index];
      if (bc.level === "rundowns") {
        updateUrlParams(bc.id, null, null);
      } else if (bc.level === "events") {
        const rundownBc = breadcrumbs.find((b) => b.level === "rundowns");
        updateUrlParams(rundownBc?.id || selectedRundown?.id || null, bc.id, null);
      }
    }
  };

  // Card component with hover tooltip for full summary

  const Card: React.FC<{
    id: string;
    type?: string;
    icon: string;
    title: string;
    subtitle?: string;
    scheduledDate?: string;
    createdAt?: string | number;
    summary?: string;
    notes?: string;
    notesAuthor?: string;
    status?: string;
    count?: number;
    countLabel?: string;
    stats?: {
      events?: number;
      stories?: number;
      logs?: number;
      transcripts?: number;
      otfs?: number;
    };
    onClick: () => void;
    onAddNotes?: () => void;
    onDelete?: () => void;
    selectable?: boolean;
    selected?: boolean;
    onToggleSelect?: () => void;
    exportSelectable?: boolean;
    exportSelected?: boolean;
    onToggleExportSelect?: () => void;
    storyData?: Story;
  }> = ({
    id,
    type = "story",
    icon,
    title,
    subtitle,
    scheduledDate,
    createdAt,
    summary,
    notes,
    notesAuthor: _notesAuthor,
    status,
    count,
    countLabel,
    stats,
    onClick,
    onAddNotes,
    onDelete,
    selectable,
    selected,
    onToggleSelect,
    exportSelectable,
    exportSelected,
    onToggleExportSelect,
    storyData,
  }) => {
    const createdAtLabel = (() => {
      if (createdAt === undefined || createdAt === null || createdAt === "") return null;
      const d = new Date(createdAt);
      if (isNaN(d.getTime())) return null;
      return d.toLocaleString([], {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    })();
    // Drag handler
    const handleDragStart = (e: React.DragEvent) => {
      const dragData: SelectableItem = {
        id,
        type: type as any,
        title,
        sourceId: storyData?.rundownId || "",
        tc: "",
        author: subtitle || "",
        tags: storyData?.tags || [],
        rating: storyData?.rating || 0,
        participants: storyData?.participants || [],
        directorId: storyData?.directorId,
        storyTags: storyData?.tags || [],
        storyParticipants: storyData?.participants || [],
        storyRating: storyData?.rating || 0,
        storyName: title || "",
        eventName: "",
        rundownName: "",
        entryTags: [],
        streams: storyData?.cameras || [],
        cameras: storyData?.cameras || [],
        originType: type || "",
        endAt: "",
        duration: "",
      };

      const isItemCurrentlySelected = isSelected(id);
      const itemsToDrag =
        isItemCurrentlySelected && selectedItems.length > 1 ? selectedItems : [dragData];

      e.dataTransfer.setData("application/json", JSON.stringify(itemsToDrag[0]));
      if (itemsToDrag.length > 1) {
        e.dataTransfer.setData("application/x-braindog-multi", JSON.stringify(itemsToDrag));
      }
      e.dataTransfer.effectAllowed = "copy";
    };

    // Type-based colors
    const typeColor = type === "rundown" ? "#f59e0b" : type === "event" ? "#38BDF8" : "#10B981"; // stories = green
    const typeColorRgb =
      type === "rundown" ? "245, 158, 11" : type === "event" ? "56, 189, 248" : "16, 185, 129";

    // Rundown date state: dim future rundowns, highlight today
    const dateState: "past" | "today" | "future" | null = (() => {
      if (type !== "rundown" || !scheduledDate) return null;
      const m = String(scheduledDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return null;
      const cardDate = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diff = cardDate.getTime() - today.getTime();
      if (diff === 0) return "today";
      return diff > 0 ? "future" : "past";
    })();
    const isFuture = dateState === "future";
    const isToday = dateState === "today";

    return (
      <div
        className="item-card"
        draggable={showAssembly}
        onDragStart={handleDragStart}
        onClick={onClick}
        style={{
          background: `linear-gradient(145deg, rgba(${typeColorRgb}, 0.06) 0%, #1a1a1a 100%)`,
          borderRadius: "var(--border-radius-md)",
          borderLeft: `3px solid ${typeColor}`,
          borderTop: `1px solid rgba(${typeColorRgb}, ${isToday ? 0.6 : 0.15})`,
          borderRight: `1px solid rgba(${typeColorRgb}, ${isToday ? 0.4 : 0.08})`,
          borderBottom: `1px solid rgba(${typeColorRgb}, ${isToday ? 0.4 : 0.08})`,
          padding: "20px",
          cursor: showAssembly ? "grab" : "pointer",
          display: "flex",
          flexDirection: "column",
          minHeight: "180px",
          position: "relative",
          overflow: "visible",
          opacity: isFuture ? 0.5 : 1,
          boxShadow: isToday
            ? `0 4px 20px rgba(${typeColorRgb}, 0.35), inset 0 1px 0 rgba(${typeColorRgb}, 0.2)`
            : `0 2px 12px rgba(${typeColorRgb}, 0.08), inset 0 1px 0 rgba(${typeColorRgb}, 0.05)`,
        }}
      >
        {/* TXT export selection is independent from the CUT selection mode. */}
        {exportSelectable && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              top: "12px",
              left: "12px",
              zIndex: 70,
            }}
          >
            <Checkbox
              checked={Boolean(exportSelected)}
              onCheckedChange={() => onToggleExportSelect?.()}
              aria-label={t("storyproducer.explore.selectStory", {
                title,
                defaultValue: `Select ${title}`,
              })}
              className="size-4 cursor-pointer border-muted-foreground/70 bg-background/90 shadow-sm hover:border-foreground data-checked:border-[var(--color-accent-success)] data-checked:bg-[var(--color-accent-success)] data-checked:text-white"
            />
          </div>
        )}

        {/* Status badge */}
        {status === "live" && (
          <div
            style={{
              position: "absolute",
              top: "12px",
              right: "12px",
              background: "var(--color-accent-danger)",
              color: "#fff",
              padding: "3px 8px",
              borderRadius: "6px",
              fontSize: "0.65rem",
              fontWeight: 700,
            }}
          >
            LIVE
          </div>
        )}

        {/* Delete Button */}
        {onDelete && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            style={{
              position: "absolute",
              top: "12px",
              right: status === "live" ? "50px" : "12px",
              background: "rgba(247, 45, 96, 0.2)",
              color: "var(--color-data-log)",
              width: "24px",
              height: "24px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.8rem",
              cursor: "pointer",
              zIndex: 60,
              transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--color-data-log)";
              e.currentTarget.style.color = "#fff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(247, 45, 96, 0.2)";
              e.currentTarget.style.color = "var(--color-data-log)";
            }}
            title={t("logger.data.deleteEvent")}
          >
            <i className="bx bx-trash"></i>
          </div>
        )}

        {/* Notes Indicator Tab */}
        {notes && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              if (onAddNotes) onAddNotes();
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget;
              el.style.transform = "scaleY(1.2)";
              el.style.paddingBottom = "7px";
              // Show notes tooltip
              const tooltip = el.querySelector(".notes-tooltip") as HTMLElement;
              if (tooltip) tooltip.style.display = "block";
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget;
              el.style.transform = "scaleY(1)";
              el.style.paddingBottom = "5px";
              const tooltip = el.querySelector(".notes-tooltip") as HTMLElement;
              if (tooltip) tooltip.style.display = "none";
            }}
            style={{
              position: "absolute",
              bottom: "0",
              right: "12px",
              background: isShowrunner ? "var(--color-role-showrunner)" : "#10B981",
              color: "#000",
              padding: "5px 6px 4px",
              borderRadius: "6px 6px 0 0",
              fontSize: "0.65rem",
              lineHeight: 1,
              cursor: "pointer",
              transition: "all 0.15s ease",
              transformOrigin: "top",
              zIndex: 50,
              boxShadow: isShowrunner
                ? "0 2px 6px rgba(245, 158, 11, 0.3)"
                : "0 2px 6px rgba(16, 185, 129, 0.3)",
            }}
          >
            <i className="bx bx-note"></i>
            {/* Notes Tooltip */}
            <div
              className="notes-tooltip"
              style={{
                display: "none",
                position: "absolute",
                bottom: "calc(100% + 8px)",
                right: "0",
                width: "280px",
                background: "rgba(15, 15, 15, 0.98)",
                backdropFilter: "blur(20px)",
                border: `1px solid ${isShowrunner ? "var(--color-role-showrunner)" : "#10B981"}`,
                borderRadius: "10px",
                padding: "14px",
                boxShadow: "0 10px 40px rgba(0,0,0,0.8)",
                pointerEvents: "none",
                zIndex: 2000,
              }}
            >
              <div
                style={{
                  fontSize: "0.65rem",
                  color: isShowrunner ? "var(--color-role-showrunner)" : "#10B981",
                  fontWeight: 800,
                  marginBottom: "6px",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                }}
              >
                <i className="bx bx-note" style={{ marginRight: "4px" }}></i> {NOTES_LABEL}
              </div>
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "#ccc",
                  lineHeight: 1.5,
                  fontStyle: "italic",
                  whiteSpace: "pre-wrap",
                }}
              >
                {notes.length > 200 ? notes.substring(0, 200) + "…" : notes}
              </div>
            </div>
          </div>
        )}
        {/* Selection Checkbox */}
        {selectable && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelect?.();
            }}
            style={{
              position: "absolute",
              top: exportSelectable ? "22px" : "-10px",
              left: "-10px",
              zIndex: 60,
              width: "24px",
              height: "24px",
              borderRadius: "50%",
              border: selected ? "none" : "2px solid rgba(255,255,255,0.3)",
              background: selected ? "var(--color-accent-success)" : "#222",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.2s ease",
              boxShadow: "0 4px 10px rgba(0,0,0,0.5)",
            }}
          >
            {selected && (
              <i className="bx bx-check" style={{ color: "#fff", fontSize: "1.2rem" }}></i>
            )}
          </div>
        )}

        {/* Icon and Title */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "10px",
            paddingLeft: exportSelectable ? "20px" : 0,
          }}
        >
          {type === "story" && summary ? (
            <TooltipProvider delay={200}>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <span
                      data-testid={`story-summary-trigger-${id}`}
                      aria-label={t("storyproducer.explore.storySummary", "Story summary")}
                      style={{
                        display: "inline-flex",
                        cursor: "help",
                        borderRadius: "4px",
                      }}
                    />
                  }
                >
                  <i
                    className={`bx ${icon}`}
                    aria-hidden="true"
                    style={{ fontSize: "1.5rem", color: typeColor }}
                  ></i>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  align="start"
                  sideOffset={8}
                  className="block w-96 max-w-[calc(100vw-2rem)] border border-border bg-popover p-4 text-popover-foreground shadow-xl"
                >
                  <div className="mb-2 flex items-center gap-2 text-xs font-extrabold uppercase tracking-wider text-emerald-500">
                    <i className="bx bx-book-open" aria-hidden="true"></i>
                    {t("storyproducer.explore.storySummary", "Story summary")}
                  </div>
                  <div
                    data-testid={`story-summary-tooltip-${id}`}
                    className="text-sm leading-relaxed"
                  >
                    {formatSummary(summary)}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <i
              className={`bx ${icon}`}
              aria-hidden="true"
              style={{ fontSize: "1.5rem", color: typeColor }}
            ></i>
          )}
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontWeight: 700,
                color: "#fff",
                fontSize: "1rem",
                lineHeight: 1.2,
              }}
            >
              {title}
            </div>
            {subtitle &&
              (() => {
                const subtitleColor =
                  type === "rundown"
                    ? "#10B981"
                    : type === "event"
                      ? "#38BDF8"
                      : "var(--color-data-log)";
                return (
                  <div
                    style={{
                      fontSize: "0.75rem",
                      marginTop: "2px",
                      fontWeight: 600,
                    }}
                  >
                    {subtitle.startsWith("por ") ? (
                      <>
                        <span style={{ color: "#fff", fontWeight: 400 }}>por </span>
                        <span style={{ color: subtitleColor }}>{subtitle.substring(4)}</span>
                      </>
                    ) : subtitle.startsWith("by ") ? (
                      <>
                        <span style={{ color: "#fff", fontWeight: 400 }}>by </span>
                        <span style={{ color: subtitleColor }}>{subtitle.substring(3)}</span>
                      </>
                    ) : (
                      <span style={{ color: subtitleColor }}>{subtitle}</span>
                    )}
                  </div>
                );
              })()}
            {scheduledDate && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  marginTop: "6px",
                  padding: "3px 8px",
                  background: isToday
                    ? `rgba(${typeColorRgb}, 0.25)`
                    : `rgba(${typeColorRgb}, 0.1)`,
                  border: `1px solid rgba(${typeColorRgb}, ${isToday ? 0.6 : 0.3})`,
                  borderRadius: "4px",
                  fontSize: "0.65rem",
                }}
              >
                <span
                  style={{
                    color: typeColor,
                    fontWeight: 800,
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                  }}
                >
                  {isToday ? "Hoy" : isFuture ? "Próximo" : "Scheduled"}
                </span>
                <span style={{ color: "#fff", fontWeight: 600 }}>{scheduledDate}</span>
              </div>
            )}
          </div>
        </div>

        {/* Spacer to push footer to bottom */}
        <div style={{ flex: 1 }} />

        {/* Footer */}
        {(() => {
          const stackMeta = type === "event" || type === "story";
          return (
            <div
              style={{
                display: "flex",
                flexDirection: stackMeta ? "column" : "row",
                justifyContent: "space-between",
                alignItems: stackMeta ? "stretch" : "center",
                gap: stackMeta ? "8px" : 0,
                marginTop: "auto",
                paddingTop: "12px",
                borderTop: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              {/* Stats with individual colors */}
              {stats ? (
                <span
                  style={{
                    fontSize: "0.75rem",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "6px",
                    rowGap: "4px",
                    alignItems: "center",
                    minWidth: 0,
                  }}
                >
                  {stats.events !== undefined && stats.events > 0 && (
                    <>
                      <strong style={{ color: "#38BDF8" }}>{stats.events}</strong>
                      <span style={{ color: "#666" }}>
                        {t("controller.stats.events").toLowerCase()}
                      </span>
                      <span style={{ color: "#444" }}>•</span>
                    </>
                  )}
                  {stats.stories !== undefined && stats.stories > 0 && (
                    <>
                      <strong style={{ color: "#10B981" }}>{stats.stories}</strong>
                      <span style={{ color: "#666" }}>
                        {t("controller.stats.stories").toLowerCase()}
                      </span>
                      <span style={{ color: "#444" }}>•</span>
                    </>
                  )}
                  {stats.logs !== undefined && stats.logs > 0 && (
                    <>
                      <strong style={{ color: "var(--color-data-log)" }}>{stats.logs}</strong>
                      <span style={{ color: "#666" }}>
                        {t("controller.stats.logs").toLowerCase()}
                      </span>
                      {((stats.transcripts !== undefined && stats.transcripts > 0) ||
                        (stats.otfs !== undefined && stats.otfs > 0)) && (
                        <span style={{ color: "#444" }}>•</span>
                      )}
                    </>
                  )}
                  {stats.transcripts !== undefined && stats.transcripts > 0 && (
                    <>
                      <strong style={{ color: "#8B5CF6" }}>{stats.transcripts}</strong>
                      <span style={{ color: "#666" }}>transcript</span>
                      {stats.otfs !== undefined && stats.otfs > 0 && (
                        <span style={{ color: "#444" }}>•</span>
                      )}
                    </>
                  )}
                  {stats.otfs !== undefined && stats.otfs > 0 && (
                    <>
                      <strong style={{ color: "var(--color-data-otf, #06b6d4)" }}>
                        {stats.otfs}
                      </strong>
                      <span style={{ color: "#666" }}>otfs</span>
                    </>
                  )}
                </span>
              ) : (
                count !== undefined && (
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: typeColor,
                      opacity: 0.8,
                    }}
                  >
                    {count} {countLabel}
                  </span>
                )
              )}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginLeft: stackMeta ? 0 : "auto",
                  justifyContent: stackMeta ? "space-between" : "flex-end",
                }}
              >
                {createdAtLabel && (
                  <span
                    style={{
                      fontSize: "0.65rem",
                      color: "#666",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                    title={t("storyproducer.createdAt", "Created at")}
                  >
                    <i className="bx bx-time-five" style={{ fontSize: "0.75rem" }}></i>
                    {createdAtLabel}
                  </span>
                )}
                {onAddNotes && !notes && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddNotes();
                    }}
                    style={{
                      background: "transparent",
                      border: "1px solid rgba(255,255,255,0.1)",
                      color: "#888",
                      padding: "4px 10px",
                      borderRadius: "4px",
                      fontSize: "0.7rem",
                      cursor: "pointer",
                      fontWeight: 400,
                    }}
                  >
                    <i className="bx bx-note"></i> {t("storyproducer.addNotes")}
                  </button>
                )}
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  return (
    <div
      data-testid="explore-container"
      style={{
        height: "100%",
        width: "100%",
        background: isEmbedded ? "transparent" : "var(--color-bg-mesh)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        userSelect: "text",
      }}
    >
      {/* Header */}
      {!isEmbedded && (
        <header
          data-testid="explore-header"
          style={{
            height: "88px",
            boxSizing: "border-box",
            padding: "0 40px",
            borderBottom: `5px solid ${ACCENT_COLOR}`,
            borderTop: "none",
            borderLeft: "none",
            borderRight: "none",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "var(--color-panel-bg)",
            backdropFilter: "blur(10px)",
            boxShadow: `0 0 20px ${ACCENT_COLOR}15`,
            zIndex: 100, // Ensure header stays on top if needed
          }}
        >
          {/* LEFT: Logo & Breadcrumb */}
          <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
            <RoleBrandingHeader
              role={isShowrunner ? "showrunner" : "sp"}
              roleLabel={MODULE_TITLE.toUpperCase()}
              projectName={activeProject?.name}
              onHomeClick={() => {
                clearActiveProject();
                navigate("/projects");
              }}
              testIdPrefix="explore"
            />
          </div>

          {/* CENTER: Panel Toggles */}
          <div style={{ display: "flex", justifyContent: "center", flex: 1 }}>
            <div
              style={{
                display: "flex",
                gap: "8px",
                background: "rgba(255,255,255,0.03)",
                borderRadius: "12px",
                padding: "6px",
                border: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              <button
                data-testid="explore-toggle-discover"
                onClick={() => setShowDiscover((prev) => !prev)}
                style={{
                  padding: "8px 20px",
                  background: showDiscover ? ACCENT_COLOR : "transparent",
                  border: "none",
                  borderRadius: "8px",
                  color: showDiscover ? "#fff" : "#888",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.2s",
                }}
              >
                <i className="bx bx-brain" style={{ fontSize: "1.1rem" }}></i>
                {t("controller.layout.discover", "BRAIN")}
              </button>

              {/* EXPLORE Button */}
              <button
                data-testid="explore-toggle-explore"
                onClick={() => {
                  if (isShowrunner) {
                    // Explore/Pulse share the right pane as a tab pair. The library
                    // is "showing" only when explore is open AND pulse isn't. Clicking
                    // the tab while the library is visible must HIDE it (close the
                    // pane) — previously this branch only ever added "explore", so the
                    // library could never be closed and re-appeared on every click.
                    batchPanels((p) => {
                      const libraryVisible = p.has("explore") && !p.has("pulse");
                      if (libraryVisible) {
                        p.delete("explore");
                      } else {
                        p.delete("pulse");
                        p.add("explore");
                      }
                    });
                  } else {
                    setShowExplore((prev) => !prev);
                  }
                }}
                style={{
                  padding: "8px 20px",
                  background:
                    showExplore && (!isShowrunner || !showPulse) ? ACCENT_COLOR : "transparent",
                  border: "none",
                  borderRadius: "8px",
                  color: showExplore && (!isShowrunner || !showPulse) ? "#fff" : "#888",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  userSelect: "none",
                  transition: "all 0.2s",
                }}
              >
                <i className="bx bx-compass" style={{ fontSize: "1.1rem" }}></i>
                {t("controller.layout.explore", "LIBRARY")}
              </button>

              {/* PULSE button (Show Runner only) */}
              {isShowrunner && (
                <button
                  data-testid="explore-toggle-pulse"
                  onClick={() => setShowPulse(true)}
                  style={{
                    padding: "8px 20px",
                    background: showPulse ? ACCENT_COLOR : "transparent",
                    border: "none",
                    borderRadius: "8px",
                    color: showPulse ? "#fff" : "#888",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "all 0.2s",
                  }}
                >
                  <i className="bx bx-pulse" style={{ fontSize: "1.1rem" }}></i>
                  {t("controller.layout.pulse", "PULSE")}
                </button>
              )}

              <>
                <button
                  data-testid="explore-toggle-cut"
                  onClick={() => {
                    if (showAssembly && assemblyFilter === "all") {
                      togglePanel("cut", false);
                    } else {
                      togglePanel("cut", true);
                    }
                  }}
                  style={{
                    padding: "8px 20px",
                    background:
                      showAssembly && assemblyFilter === "all" ? ACCENT_COLOR : "transparent",
                    border: "none",
                    borderRadius: "8px",
                    color: showAssembly && assemblyFilter === "all" ? "#fff" : "#888",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "all 0.2s",
                  }}
                >
                  <i className="bx bx-movie" style={{ fontSize: "1.1rem" }}></i>
                  {t("controller.layout.cut", "CUT")}
                </button>
                <button
                  data-testid="explore-toggle-approved"
                  onClick={() => {
                    if (showAssembly && assemblyFilter === "approved") {
                      togglePanel("vtr", false);
                    } else {
                      togglePanel("vtr", true);
                    }
                  }}
                  style={{
                    padding: "8px 20px",
                    background:
                      showAssembly && assemblyFilter === "approved" ? ACCENT_COLOR : "transparent",
                    border: "none",
                    borderRadius: "8px",
                    color: showAssembly && assemblyFilter === "approved" ? "#fff" : "#888",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "all 0.2s",
                  }}
                >
                  <i className="bx bx-check-shield" style={{ fontSize: "1.1rem" }}></i>
                  {t("controller.layout.approved", "VTR")}
                </button>
              </>

              {/* SUMMARIES Button — available to all roles */}
              <button
                data-testid="explore-toggle-summaries"
                onClick={() => setShowSummaries((prev) => !prev)}
                style={{
                  padding: "8px 20px",
                  background: showSummaries ? ACCENT_COLOR : "transparent",
                  border: "none",
                  borderRadius: "8px",
                  color: showSummaries ? "#fff" : "#888",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.2s",
                }}
              >
                <i className="bx bx-bar-chart-alt-2" style={{ fontSize: "1.1rem" }}></i>
                {t("controller.layout.summaries", "SUMMARIES")}
              </button>

              {/* FEED toggle (live stream panel) */}
              <button
                data-testid="explore-toggle-feed"
                onClick={() => setShowFeed((prev) => !prev)}
                style={{
                  padding: "8px 20px",
                  background: showFeed ? ACCENT_COLOR : "transparent",
                  border: "none",
                  borderRadius: "8px",
                  color: showFeed ? "#fff" : "#888",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.2s",
                }}
              >
                <span
                  className="blink"
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: "var(--color-accent-danger, #f72d60)",
                    boxShadow: "0 0 6px var(--color-accent-danger, #f72d60)",
                    display: "inline-block",
                  }}
                />
                {t("controller.layout.feed", "FEED")}
              </button>

              {/* VIDEO: buscar y reproducir grabación por día/hora */}
              <button
                data-testid="explore-toggle-videosearch"
                onClick={() => setShowVideoSearch((prev) => !prev)}
                style={{
                  padding: "8px 20px",
                  background: showVideoSearch ? ACCENT_COLOR : "transparent",
                  border: "none",
                  borderRadius: "8px",
                  color: showVideoSearch ? "#fff" : "#888",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.2s",
                }}
              >
                <i className="bx bx-time-five" style={{ fontSize: "1.1rem" }}></i>
                {t("controller.layout.videoSearch", "VIDEO")}
              </button>
            </div>
          </div>

          {/* RIGHT: User & Global Actions */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              justifyContent: "flex-end",
              flex: 1,
            }}
          >
            <div style={{ display: "flex", gap: "8px" }}>
              {(offlineCount > 0 || !isOnline) && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "0 12px",
                    borderRadius: "4px",
                    background: !isOnline ? "rgba(239,68,68,0.1)" : "rgba(139,92,246,0.1)",
                    color: !isOnline ? "#ef4444" : "#8b5cf6",
                    border: `1px solid ${!isOnline ? "rgba(239,68,68,0.2)" : "rgba(139,92,246,0.2)"}`,
                    fontSize: "0.8rem",
                    fontWeight: 600,
                  }}
                  title={
                    !isOnline
                      ? "Estás offline. Tus notas se guardarán en tu disco duro."
                      : "Sincronizando notas con la nube..."
                  }
                >
                  <i
                    className={`bx ${!isOnline ? "bx-cloud-drizzle" : "bx-cloud-upload bx-flashing"}`}
                  ></i>
                  {!isOnline ? `Offline (${offlineCount})` : `Syncing ${offlineCount}`}
                </div>
              )}
              <ButtonGroup>
                <LanguageToggle />
                {(user?.role === "Story Producer" ||
                  user?.role === "Admin" ||
                  user?.role === "Superadmin") && (
                  <Button
                    data-testid="storyproducer-export-markers"
                    variant="outline"
                    size="icon"
                    onClick={() => setIsExportMarkersOpen(true)}
                    disabled={!activeProject}
                    title={t("logger.export.title", "Exportar markers")}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <i className="bx bx-download" style={{ fontSize: "1.2rem" }}></i>
                  </Button>
                )}
                <BugReportButton module="storyproducer" />
                <FullscreenButton isInline={true} />
              </ButtonGroup>
              <ConnectionStatusWidget />
              <UserMenu role={isShowrunner ? "showrunner" : "sp"} testIdPrefix="explore-user" />
            </div>
          </div>
        </header>
      )}

      {/* MULTI-PANEL MAIN CONTAINER */}
      <div
        style={{
          flex: 1,
          display: "flex",
          overflow: "hidden",
          padding: isEmbedded ? "0" : "20px",
          gap: isEmbedded ? "0" : "20px",
        }}
      >
        {/* Panel LEFT: DISCOVERY (colapsable, flex responsive) */}
        <div
          data-testid="explore-discover-panel"
          onMouseEnter={() => setActiveFocus("player")}
          style={{
            flex: showDiscover ? 1 : 0,
            width: showDiscover ? "auto" : 0,
            minWidth: showDiscover ? "360px" : 0,
            height: "100%",
            overflow: "hidden",
            background: "var(--color-panel-bg)",
            border: showDiscover ? "1px solid var(--color-panel-border)" : "none",
            borderRadius: "var(--border-radius-lg)",
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
            opacity: showDiscover ? 1 : 0,
            transition: "flex 0.3s ease, opacity 0.3s ease, width 0.3s ease",
            pointerEvents: showDiscover ? "auto" : "none",
          }}
        >
          {showDiscover && (
            <React.Suspense fallback={null}>
              <Discovery
                onNavigateToLog={handleNavigateToLog}
                hideAiDetails={user?.role !== "Superadmin"}
              />
            </React.Suspense>
          )}
        </div>

        {/* Panel LEFT: FEED (live stream) */}
        <div
          data-testid="explore-feed-panel"
          onMouseEnter={() => setActiveFocus("player")}
          style={{
            flex: showFeed ? 1 : 0,
            width: showFeed ? "auto" : 0,
            minWidth: showFeed ? "360px" : 0,
            height: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
            opacity: showFeed ? 1 : 0,
            transition: "flex 0.3s ease, opacity 0.3s ease, width 0.3s ease",
            pointerEvents: showFeed ? "auto" : "none",
          }}
        >
          {showFeed && (
            <StreamFeed
              externalStories={stories as unknown as DomainStory[]}
              externalParticipants={participants}
            />
          )}
        </div>

        {/* Panel: VIDEO SEARCH (buscar grabación por día/hora) */}
        <div
          data-testid="explore-videosearch-panel"
          style={{
            flex: showVideoSearch ? 1 : 0,
            width: showVideoSearch ? "auto" : 0,
            minWidth: showVideoSearch ? "420px" : 0,
            height: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
            background: showVideoSearch ? "var(--color-panel-bg)" : "transparent",
            border: showVideoSearch ? "1px solid var(--color-panel-border)" : "none",
            borderRadius: showVideoSearch ? "var(--border-radius-lg)" : 0,
            opacity: showVideoSearch ? 1 : 0,
            transition: "flex 0.3s ease, opacity 0.3s ease, width 0.3s ease",
            pointerEvents: showVideoSearch ? "auto" : "none",
          }}
        >
          {showVideoSearch && <VideoSearchPanel />}
        </div>

        {/* Panel CENTER: EXPLORE or PULSE */}
        <div
          onMouseEnter={() => setActiveFocus("player")}
          style={{
            flex: showExplore || (isShowrunner && showPulse) ? 1 : "0 0 0",
            width: showExplore || (isShowrunner && showPulse) ? "auto" : 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            background: isEmbedded ? "transparent" : "var(--color-panel-bg)",
            border: isEmbedded
              ? "none"
              : `1px solid ${isShowrunner && showPulse ? "rgba(245, 158, 11, 0.2)" : "var(--color-panel-border)"}`,
            borderRadius: isEmbedded ? "0" : "var(--border-radius-lg)",
            overflow: "hidden",
            opacity: showExplore || (isShowrunner && showPulse) ? 1 : 0,
            transition: "flex 0.3s ease, opacity 0.3s ease, width 0.3s ease",
            pointerEvents: showExplore || (isShowrunner && showPulse) ? "auto" : "none",
          }}
        >
          {/* PULSE Dashboard (Show Runner only) */}
          {isShowrunner && showPulse ? (
            <PulseDashboard projectId={activeProject?.id || null} />
          ) : (
            <>
              {/* Header Local para Explore */}
              <div
                data-testid="explore-panel-header"
                style={{
                  padding: "12px 15px",
                  background: "var(--color-bg-base)",
                  borderBottom: "1px solid var(--color-panel-border)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <i
                    className="bx bx-compass"
                    style={{ color: ACCENT_COLOR, fontSize: "1.2rem" }}
                  ></i>
                  <span
                    style={{
                      fontWeight: 900,
                      letterSpacing: "2px",
                      fontSize: "1rem",
                      color: "#fff",
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("controller.layout.explore", "LIBRARY")}
                  </span>

                  {/* Global Stats - Show when at rundowns level */}
                  {drillLevel === "rundowns" && globalStats.rundowns > 0 && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginLeft: "16px",
                        background: "rgba(255,255,255,0.05)",
                        padding: "6px 14px",
                        borderRadius: "6px",
                        fontSize: "0.75rem",
                        color: "#888",
                      }}
                    >
                      <span>
                        <strong style={{ color: "#f59e0b" }}>{globalStats.rundowns}</strong>{" "}
                        Rundowns
                      </span>
                      <span style={{ color: "#444" }}>•</span>
                      <span>
                        <strong style={{ color: "#38BDF8" }}>{globalStats.events}</strong> Events
                      </span>
                      <span style={{ color: "#444" }}>•</span>
                      <span>
                        <strong style={{ color: "#10B981" }}>
                          {globalStats.stories.toLocaleString()}
                        </strong>{" "}
                        Stories
                      </span>
                      <span style={{ color: "#444" }}>•</span>
                      <span>
                        <strong style={{ color: "var(--color-data-log)" }}>
                          {globalStats.logs.toLocaleString()}
                        </strong>{" "}
                        Logs
                      </span>
                      {globalStats.transcripts > 0 && (
                        <>
                          <span style={{ color: "#444" }}>•</span>
                          <span>
                            <strong style={{ color: "var(--color-data-transcript)" }}>
                              {globalStats.transcripts.toLocaleString()}
                            </strong>{" "}
                            Transcripts
                          </span>
                        </>
                      )}
                      {globalStats.otfs > 0 && (
                        <>
                          <span style={{ color: "#444" }}>•</span>
                          <span>
                            <strong
                              style={{
                                color: "var(--color-data-otf, #06b6d4)",
                              }}
                            >
                              {globalStats.otfs.toLocaleString()}
                            </strong>{" "}
                            OTs
                          </span>
                        </>
                      )}
                    </div>
                  )}
                  {/* Breadcrumbs integrated here */}
                  {viewMode === "cards" && breadcrumbs.length > 0 && (
                    <div
                      data-testid="explore-breadcrumbs"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginLeft: "16px",
                        background: "rgba(255,255,255,0.05)",
                        padding: "4px 12px",
                        borderRadius: "6px",
                        overflow: "hidden",
                        minWidth: 0,
                        flexShrink: 1,
                      }}
                    >
                      <button
                        onClick={() => handleBreadcrumbClick(-1)}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: ACCENT_COLOR,
                          cursor: "pointer",
                          fontSize: "0.8rem",
                          padding: 0,
                        }}
                      >
                        <i className="bx bx-home"></i>
                      </button>
                      {breadcrumbs.map((bc, idx) => {
                        // Color based on level
                        const levelColor =
                          bc.level === "rundowns"
                            ? "#f59e0b"
                            : bc.level === "events"
                              ? "#38BDF8"
                              : ACCENT_COLOR;
                        const isLast = idx === breadcrumbs.length - 1;
                        return (
                          <React.Fragment key={bc.level}>
                            <i
                              className="bx bx-chevron-right"
                              style={{ color: "#444", fontSize: "0.8rem" }}
                            ></i>
                            <button
                              onClick={() => !isLast && handleBreadcrumbClick(idx)}
                              style={{
                                background: "transparent",
                                border: "none",
                                color: levelColor,
                                cursor: isLast ? "default" : "pointer",
                                fontSize: "0.8rem",
                                padding: 0,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                flex: "0 1 auto",
                              }}
                            >
                              {bc.title}
                            </button>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Right side: Add Notes (story-level) + View Mode Toggle */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  {drillLevel === "logs" && selectedStory && !isReadOnly && (
                    <button
                      onClick={() => setEditingStory(selectedStory)}
                      style={{
                        background: selectedStory.producerNotes ? ACCENT_COLOR : "transparent",
                        border: selectedStory.producerNotes ? "none" : `1px solid ${ACCENT_COLOR}`,
                        color: selectedStory.producerNotes ? "#000" : ACCENT_COLOR,
                        padding: "5px 12px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "0.75rem",
                        fontWeight: selectedStory.producerNotes ? 700 : 400,
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <i className="bx bx-note"></i>
                      {selectedStory.producerNotes
                        ? t("storyproducer.viewNotes")
                        : t("storyproducer.addNotes")}
                    </button>
                  )}
                  {drillLevel === "logs" && selectedStory && (
                    <button
                      data-testid="explore-export-story-txt"
                      onClick={() => handleExportStoryTxt(selectedStory)}
                      title={t("storyproducer.exportTxt", "Exportar TXT")}
                      style={{
                        background: "transparent",
                        border: "1px solid rgba(255,255,255,0.15)",
                        color: "#bbb",
                        padding: "5px 12px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "0.75rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <i className="bx bx-download"></i> TXT
                    </button>
                  )}
                  {drillLevel === "logs" && selectedStory && (
                    <Badge
                      data-testid="explore-otf-mode"
                      variant={displayAsOtf ? "default" : "outline"}
                      size="xs"
                      className={`cursor-pointer select-none ${
                        displayAsOtf
                          ? "bg-[#00e5ff] text-black border-transparent hover:bg-[#00e5ff]/90"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                      onClick={() => setDisplayAsOtf((v) => !v)}
                      title={t(
                        "storyproducer.explore.otfModeHint",
                        "Mostrar transcripciones como OTF",
                      )}
                    >
                      <i
                        className={`bx ${displayAsOtf ? "bx-microphone" : "bx-microphone-off"} mr-1`}
                      ></i>
                      {t("storyproducer.explore.otfMode", "OTF MODE")}
                    </Badge>
                  )}
                  <div
                    style={{
                      display: "flex",
                      gap: "4px",
                      background: "rgba(255,255,255,0.05)",
                      borderRadius: "8px",
                      padding: "4px",
                    }}
                  >
                    <button
                      data-testid="explore-view-cards"
                      onClick={() => setViewMode("cards")}
                      style={{
                        padding: "4px 10px",
                        background: viewMode === "cards" ? ACCENT_COLOR : "transparent",
                        border: "none",
                        borderRadius: "6px",
                        color: viewMode === "cards" ? "#fff" : "#888",
                        cursor: "pointer",
                        fontSize: "0.75rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <i className="bx bx-grid-alt"></i> {t("storyproducer.explore.cards", "Cards")}
                    </button>
                    <button
                      data-testid="explore-view-list"
                      onClick={() => setViewMode("list")}
                      style={{
                        padding: "4px 10px",
                        background: viewMode === "list" ? ACCENT_COLOR : "transparent",
                        border: "none",
                        borderRadius: "6px",
                        color: viewMode === "list" ? "#fff" : "#888",
                        cursor: "pointer",
                        fontSize: "0.75rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <i className="bx bx-list-ul"></i> {t("storyproducer.explore.list", "List")}
                    </button>
                  </div>
                </div>
              </div>

              <main
                id="explore-main-area"
                data-testid="explore-main-content"
                onClick={clearHighlight}
                onScroll={(e) => {
                  const target = e.currentTarget;
                  const storyId = selectedStory?.id || "root";
                  localStorage.setItem(`explore_scroll_${storyId}`, String(target.scrollTop));
                }}
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflowY: drillLevel === "logs" ? "hidden" : "scroll",
                  overflowX: "hidden",
                  padding: drillLevel === "logs" ? "0" : "0 24px 24px 24px",
                  transition: "all 0.3s ease",
                  display: drillLevel === "logs" ? "flex" : "block",
                  flexDirection: "column",
                }}
              >
                {/* === CARDS VIEW MODE === */}
                {viewMode === "cards" && (
                  <>
                    {/* Level: Rundowns */}
                    {drillLevel === "rundowns" && (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                          gap: "20px",
                          paddingTop: "24px",
                        }}
                      >
                        {rundowns.length === 0 ? (
                          <div
                            style={{
                              color: "#666",
                              textAlign: "center",
                              padding: "48px",
                              gridColumn: "1 / -1",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: "12px",
                            }}
                          >
                            {!dataReady ? (
                              <>
                                <i
                                  className="bx bx-loader-alt bx-spin"
                                  style={{
                                    fontSize: "2rem",
                                    color: ACCENT_COLOR,
                                  }}
                                ></i>
                                <span>{t("common.loading", "CARGANDO...")}</span>
                              </>
                            ) : (
                              t("storyproducer.explore.noRundowns", "No rundowns available")
                            )}
                          </div>
                        ) : (
                          [...rundowns]
                            .sort((a: any, b: any) => {
                              const ta = a.scheduledAt
                                ? new Date(a.scheduledAt).getTime()
                                : a.createdAt
                                  ? new Date(a.createdAt).getTime()
                                  : 0;
                              const tb = b.scheduledAt
                                ? new Date(b.scheduledAt).getTime()
                                : b.createdAt
                                  ? new Date(b.createdAt).getTime()
                                  : 0;
                              return ta - tb;
                            })
                            .map((rundown) => {
                              return (
                                <Card
                                  key={rundown.id}
                                  id={rundown.id}
                                  type="rundown"
                                  icon="bx-calendar"
                                  title={rundown.name}
                                  scheduledDate={rundown.date}
                                  summary={rundown.summary}
                                  stats={{ events: rundown.items?.length || 0 }}
                                  onClick={() => handleDrillDown("rundowns", rundown)}
                                  onDelete={
                                    user?.role === "Superadmin"
                                      ? () => handleDeleteClick("rundown", rundown.id, rundown.name)
                                      : undefined
                                  }
                                  selectable={false}
                                />
                              );
                            })
                        )}
                      </div>
                    )}

                    {drillLevel === "events" && selectedRundown && (
                      <div style={{ paddingTop: "24px" }}>
                        {selectedRundown.summary && (
                          <div
                            data-testid="rundown-summary-banner"
                            style={{
                              background:
                                "linear-gradient(145deg, rgba(245, 158, 11, 0.08) 0%, rgba(25, 25, 25, 0.95) 100%)",
                              borderRadius: "12px",
                              borderLeft: "3px solid #f59e0b",
                              padding: "16px 20px",
                              marginBottom: "20px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              <i
                                className="bx bx-brain"
                                style={{ fontSize: "1rem", color: "#f59e0b" }}
                              ></i>
                              <span
                                style={{
                                  fontSize: "0.65rem",
                                  color: "#f59e0b",
                                  fontWeight: 900,
                                  textTransform: "uppercase",
                                  letterSpacing: "2px",
                                }}
                              >
                                {t("storyproducer.explore.rundownSummary", "Rundown Summary")}
                              </span>
                            </div>
                            <div
                              style={{
                                fontSize: "0.85rem",
                                color: "#ccc",
                                lineHeight: 1.6,
                              }}
                            >
                              {formatSummary(selectedRundown.summary)}
                            </div>
                          </div>
                        )}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                            gap: "20px",
                          }}
                        >
                          {!selectedRundown.items || selectedRundown.items.length === 0 ? (
                            <div
                              style={{
                                color: "#666",
                                textAlign: "center",
                                padding: "48px",
                                gridColumn: "1 / -1",
                              }}
                            >
                              {t("storyproducer.explore.noEvents", "No events in this rundown")}
                            </div>
                          ) : (
                            [...selectedRundown.items]
                              .sort((a: any, b: any) => {
                                const ta = a.scheduledAt
                                  ? new Date(a.scheduledAt).getTime()
                                  : a.createdAt
                                    ? new Date(a.createdAt).getTime()
                                    : 0;
                                const tb = b.scheduledAt
                                  ? new Date(b.scheduledAt).getTime()
                                  : b.createdAt
                                    ? new Date(b.createdAt).getTime()
                                    : 0;
                                return ta - tb;
                              })
                              .map((event) => {
                                // Use local stories if loaded, otherwise server stats
                                // Server-side stats as single source of truth
                                const storyCount = event.stats?.stories || 0;
                                const logCount = event.stats?.logs || 0;
                                const transcriptCount = event.stats?.transcripts || 0;
                                const otfCount = event.stats?.otfs || 0;
                                return (
                                  <Card
                                    key={event.id}
                                    id={event.id}
                                    type="event"
                                    icon="bx-layer"
                                    title={event.title}
                                    createdAt={(event as any).createdAt}
                                    scheduledDate={
                                      event.scheduledAt
                                        ? new Date(event.scheduledAt).toLocaleTimeString("en-GB", {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })
                                        : event.plannedTime && event.plannedTime !== "00:00:00"
                                          ? event.plannedTime.substring(0, 5)
                                          : undefined
                                    }
                                    status={event.status}
                                    summary={event.summary}
                                    notes={event.producerNotes}
                                    notesAuthor={event.notesAuthor}
                                    stats={{
                                      stories: storyCount,
                                      logs: logCount,
                                      transcripts: transcriptCount,
                                      otfs: otfCount,
                                    }}
                                    onClick={() => handleDrillDown("events", event)}
                                    onAddNotes={
                                      isReadOnly ? undefined : () => setEditingEvent(event)
                                    }
                                    onDelete={
                                      user?.role === "Superadmin"
                                        ? () => handleDeleteClick("event", event.id, event.title)
                                        : undefined
                                    }
                                    selectable={false} // Events not selectable yet
                                  />
                                );
                              })
                          )}
                        </div>
                      </div>
                    )}

                    {drillLevel === "stories" && selectedEvent && (
                      <div style={{ paddingTop: "24px" }}>
                        {selectedEvent.summary && (
                          <div
                            data-testid="event-summary-banner"
                            style={{
                              background:
                                "linear-gradient(145deg, rgba(56, 189, 248, 0.08) 0%, rgba(25, 25, 25, 0.95) 100%)",
                              borderRadius: "12px",
                              borderLeft: "3px solid #38BDF8",
                              padding: "16px 20px",
                              marginBottom: "20px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              <i
                                className="bx bx-brain"
                                style={{ fontSize: "1rem", color: "#38BDF8" }}
                              ></i>
                              <span
                                style={{
                                  fontSize: "0.65rem",
                                  color: "#38BDF8",
                                  fontWeight: 900,
                                  textTransform: "uppercase",
                                  letterSpacing: "2px",
                                }}
                              >
                                {t("storyproducer.explore.eventSummary", "Event Summary")}
                              </span>
                            </div>
                            <div
                              style={{
                                fontSize: "0.85rem",
                                color: "#ccc",
                                lineHeight: 1.6,
                              }}
                            >
                              {formatSummary(selectedEvent.summary)}
                            </div>
                          </div>
                        )}
                        {(() => {
                          const authorsInEvent = Array.from(
                            new Set(
                              selectedEventStories
                                .map((s) => (s.author || "").trim())
                                .filter(Boolean),
                            ),
                          ).sort((a, b) => a.localeCompare(b));
                          if (authorsInEvent.length === 0) return null;
                          return (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                marginBottom: "16px",
                                flexWrap: "wrap",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "0.7rem",
                                  color: "#888",
                                  fontWeight: 700,
                                  textTransform: "uppercase",
                                  letterSpacing: "1px",
                                }}
                              >
                                <i className="bx bx-filter-alt" style={{ marginRight: "4px" }}></i>
                                {t("storyproducer.explore.filterByAuthor", "Author")}
                              </span>
                              <select
                                value={storyAuthorFilter}
                                onChange={(e) => setStoryAuthorFilter(e.target.value)}
                                style={{
                                  background: "rgba(0,0,0,0.4)",
                                  color: "#fff",
                                  border: "1px solid rgba(247, 45, 96, 0.3)",
                                  borderRadius: "6px",
                                  padding: "6px 10px",
                                  fontSize: "0.8rem",
                                  cursor: "pointer",
                                  minWidth: "180px",
                                }}
                              >
                                <option value="">
                                  {t("storyproducer.explore.allAuthors", "All authors")}
                                </option>
                                {authorsInEvent.map((a) => (
                                  <option key={a} value={a}>
                                    {a}
                                  </option>
                                ))}
                              </select>
                              {storyAuthorFilter && (
                                <button
                                  onClick={() => setStoryAuthorFilter("")}
                                  style={{
                                    background: "transparent",
                                    color: "#888",
                                    border: "1px solid rgba(255,255,255,0.15)",
                                    borderRadius: "6px",
                                    padding: "6px 10px",
                                    fontSize: "0.75rem",
                                    cursor: "pointer",
                                  }}
                                  title={t("common.clear", "Clear")}
                                >
                                  <i className="bx bx-x"></i>
                                </button>
                              )}
                            </div>
                          );
                        })()}
                        {selectedEventStories.length > 0 && (
                          <div
                            data-testid="story-export-toolbar"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: "12px",
                              marginBottom: "20px",
                              padding: "12px 14px",
                              background: "rgba(16, 185, 129, 0.06)",
                              border: "1px solid rgba(16, 185, 129, 0.22)",
                              borderRadius: "10px",
                              flexWrap: "wrap",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                minWidth: 0,
                              }}
                            >
                              <Checkbox
                                id={`select-all-stories-${selectedEvent.id}`}
                                checked={
                                  visibleSelectedEventStories.length > 0 &&
                                  visibleSelectedEventStories.every((story) =>
                                    selectedStoryExportIds.has(story.id),
                                  )
                                }
                                disabled={visibleSelectedEventStories.length === 0}
                                onCheckedChange={toggleAllVisibleStoriesForTxtExport}
                                aria-label={t(
                                  "storyproducer.explore.selectAllVisible",
                                  "Select all visible stories",
                                )}
                                className="size-5 cursor-pointer border-white/30 data-checked:border-emerald-500 data-checked:bg-emerald-500"
                              />
                              <label
                                htmlFor={`select-all-stories-${selectedEvent.id}`}
                                style={{
                                  color: "#ddd",
                                  fontSize: "0.8rem",
                                  fontWeight: 700,
                                  cursor:
                                    visibleSelectedEventStories.length > 0 ? "pointer" : "default",
                                }}
                              >
                                {t(
                                  "storyproducer.explore.selectAllVisible",
                                  "Select all visible stories",
                                )}
                              </label>
                              <span
                                aria-live="polite"
                                style={{
                                  color: "#888",
                                  fontSize: "0.75rem",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("storyproducer.explore.storiesSelected", {
                                  count: selectedStoryExportIds.size,
                                  defaultValue: "{{count}} selected",
                                })}
                              </span>
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              {selectedStoryExportIds.size > 0 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setSelectedStoryExportIds(new Set())}
                                  disabled={isExportingSelectedStories}
                                  className="text-zinc-400 hover:text-white"
                                >
                                  {t("common.clear", "Clear")}
                                </Button>
                              )}
                              <Button
                                type="button"
                                size="sm"
                                onClick={handleExportSelectedStoriesTxt}
                                disabled={
                                  selectedStoryExportIds.size === 0 || isExportingSelectedStories
                                }
                                className="bg-emerald-600 text-white hover:bg-emerald-500"
                              >
                                {isExportingSelectedStories ? (
                                  <i className="bx bx-loader-alt bx-spin" aria-hidden="true"></i>
                                ) : (
                                  <DownloadIcon aria-hidden="true" />
                                )}
                                {isExportingSelectedStories
                                  ? t("storyproducer.explore.exportingStories", "Exporting...")
                                  : t(
                                      "storyproducer.explore.exportSelectedTxt",
                                      "Export selected TXT",
                                    )}
                              </Button>
                            </div>
                          </div>
                        )}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                            gap: "20px",
                          }}
                        >
                          {(() => {
                            if (loadingEventIds.has(selectedEvent.id)) {
                              return (
                                <div
                                  style={{
                                    color: "#666",
                                    textAlign: "center",
                                    padding: "48px",
                                    gridColumn: "1 / -1",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: "12px",
                                  }}
                                >
                                  <i
                                    className="bx bx-loader-alt bx-spin"
                                    style={{
                                      fontSize: "2rem",
                                      color: ACCENT_COLOR,
                                    }}
                                  ></i>
                                  <span>
                                    {t(
                                      "storyproducer.explore.loadingStories",
                                      "Loading stories...",
                                    )}
                                  </span>
                                </div>
                              );
                            }
                            const eventStories = visibleSelectedEventStories;
                            return eventStories.length === 0 ? (
                              <div
                                style={{
                                  color: "#666",
                                  textAlign: "center",
                                  padding: "48px",
                                  gridColumn: "1 / -1",
                                }}
                              >
                                {storyAuthorFilter
                                  ? t(
                                      "storyproducer.explore.noStoriesForAuthor",
                                      "No stories for this author",
                                    )
                                  : t(
                                      "storyproducer.explore.noStories",
                                      "No stories in this event",
                                    )}
                              </div>
                            ) : (
                              eventStories.map((story) => (
                                <Card
                                  key={story.id}
                                  id={story.id}
                                  type="story"
                                  icon="bx-book-open"
                                  title={story.title}
                                  subtitle={story.author ? `by ${story.author}` : undefined}
                                  createdAt={story.createdAt}
                                  summary={story.summary || story.aiSummary}
                                  notes={story.producerNotes}
                                  notesAuthor={story.notesAuthor}
                                  status={undefined}
                                  stats={{
                                    logs: story.logCount ?? story.logs?.length ?? 0,
                                    transcripts:
                                      story.transcriptCount ??
                                      storyTranscripts[story.id]?.length ??
                                      0,
                                    otfs: story.otfCount ?? 0,
                                  }}
                                  onClick={() => handleDrillDown("stories", story)}
                                  onAddNotes={isReadOnly ? undefined : () => setEditingStory(story)}
                                  onDelete={
                                    user?.role === "Superadmin"
                                      ? () => handleDeleteClick("story", story.id, story.title)
                                      : undefined
                                  }
                                  selectable={isSelectionMode}
                                  selected={isSelected(story.id)}
                                  exportSelectable
                                  exportSelected={selectedStoryExportIds.has(story.id)}
                                  storyData={story}
                                  onToggleSelect={() =>
                                    toggleItem({
                                      id: story.id,
                                      type: "story",
                                      title: story.title,
                                      author: story.author,
                                      sourceId: story.rundownId,
                                      storyId: story.id,
                                      tc: "",
                                      tags: story.tags || [],
                                      rating: story.rating || 0,
                                      participants: story.participants || [],
                                      directorId: story.directorId,
                                      storyTags: story.tags || [],
                                      storyParticipants: story.participants || [],
                                      storyRating: story.rating || 0,
                                      storyName: story.title || "",
                                      eventName: "",
                                      rundownName: "",
                                      entryTags: [],
                                      streams: story.cameras || [],
                                      cameras: story.cameras || [],
                                      originType: "story",
                                      endAt: "",
                                    })
                                  }
                                  onToggleExportSelect={() => toggleStoryForTxtExport(story.id)}
                                />
                              ))
                            );
                          })()}
                        </div>
                      </div>
                    )}

                    {/* Level: Logs */}
                    {drillLevel === "logs" && selectedStory && (
                      <div
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          minHeight: 0,
                          width: "100%",
                          maxWidth: "800px",
                          margin: "0 auto",
                          padding: "0 24px 24px 24px",
                        }}
                      >
                        {/* Fixed Header Wrapper (summary + producer notes + mock player) */}
                        <div
                          style={{
                            background: "var(--color-panel-bg, #161616)",
                            paddingTop: "8px",
                            paddingBottom: "16px",
                            marginBottom: "16px",
                            borderBottom: "1px solid rgba(255,255,255,0.05)",
                            flexShrink: 0,
                            width: "100%",
                          }}
                        >
                          {/* MOCK VIDEO PLAYER (BIBLIOTECA) */}
                          <div
                            style={{
                              width: "100%",
                              background: "var(--color-bg-base)",
                              borderRadius: "8px",
                              border: "1px solid rgba(255,255,255,0.05)",
                              boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                              display: "flex",
                              flexDirection: "column",
                              overflow: "hidden",
                              transition: "all 0.3s ease",
                            }}
                          >
                            {/* Header to toggle mock player */}
                            <div
                              style={{
                                padding: "8px 16px",
                                background: "rgba(20,20,20,0.9)",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                cursor: "pointer",
                                borderBottom: isMockPlayerCollapsed
                                  ? "none"
                                  : "1px solid rgba(255,255,255,0.05)",
                              }}
                              onClick={() => setIsMockPlayerCollapsed(!isMockPlayerCollapsed)}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "16px",
                                  flexWrap: "wrap",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "0.8rem",
                                    color: "var(--color-role-logger)",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  <i className="bx bx-video"></i>
                                  Stream Player
                                </div>
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "16px",
                                }}
                              >
                                {/* STREAM 1/2 conmuta cámara. Vive en el header (no como overlay
                                    del video); stopPropagation evita que al elegir stream se
                                    colapse el player. El audio usa el canal por defecto. */}
                                <div
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    display: "flex",
                                    gap: "16px",
                                    alignItems: "center",
                                    cursor: "default",
                                    fontSize: "0.8rem",
                                  }}
                                >
                                  <div
                                    style={{ display: "flex", gap: "8px", alignItems: "center" }}
                                  >
                                    {(["1", "2"] as const).map((s, i) => (
                                      <React.Fragment key={s}>
                                        {i > 0 && <span style={{ color: "#444" }}>|</span>}
                                        <span
                                          style={{
                                            color:
                                              activeStream === s
                                                ? "var(--color-role-logger)"
                                                : "#888",
                                            cursor: "pointer",
                                            fontWeight: activeStream === s ? 600 : 400,
                                          }}
                                          onClick={() => {
                                            if (activeStream === s) return;
                                            setActiveStream(s);
                                            setPlayerClip((prev) =>
                                              prev
                                                ? {
                                                    ...prev,
                                                    channel: streamToChannel(s),
                                                  }
                                                : prev,
                                            );
                                          }}
                                        >
                                          STREAM {s}
                                        </span>
                                      </React.Fragment>
                                    ))}
                                  </div>
                                </div>
                                <i
                                  className={`bx bx-chevron-${isMockPlayerCollapsed ? "down" : "up"}`}
                                  style={{ color: "#aaa" }}
                                ></i>
                              </div>
                            </div>

                            {!isMockPlayerCollapsed && (
                              <div
                                onMouseEnter={() => setActiveFocus("player")}
                                style={{
                                  position: "relative",
                                  width: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <div
                                  style={{
                                    width: "100%",
                                    aspectRatio: "16/9",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    position: "relative",
                                    background: "#000",
                                    overflow: "hidden",
                                  }}
                                >
                                  {/* Video real del relay (HLS). Llena el área 16:9. */}
                                  {playerClip && (
                                    <RelayClipPlayer
                                      ref={playerRef}
                                      at={playerClip.at}
                                      channel={playerClip.channel}
                                      autoPlayOnLoad={isPlaying}
                                      onPlayingChange={(playing) => {
                                        setIsPlaying(playing);
                                        if (playing) {
                                          assemblyRef.current?.pauseVtr();
                                        }
                                      }}
                                      onLoadingChange={setPlayerLoading}
                                      onWallClockChange={setPlayerWallMs}
                                      onProgressChange={setPlayerProgress}
                                      onError={setPlayerError}
                                    />
                                  )}

                                  {/* Placeholder cuando no hay clip cargado */}
                                  {!playerClip && (
                                    <div
                                      style={{
                                        textAlign: "center",
                                        color: "var(--color-role-logger)",
                                        fontSize: "1.2rem",
                                        fontWeight: 700,
                                        textTransform: "uppercase",
                                        letterSpacing: "2px",
                                        padding: "0 24px",
                                      }}
                                    >
                                      {t(
                                        "storyproducer.explore.clickLog",
                                        "Haz clic en un log para reproducir",
                                      )}
                                    </div>
                                  )}

                                  {/* Overlay de carga */}
                                  {playerClip && playerLoading && !playerError && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        color: "rgba(255,255,255,0.6)",
                                        fontSize: "0.9rem",
                                        pointerEvents: "none",
                                        zIndex: 2,
                                      }}
                                    >
                                      {t("common.loading", "LOADING...")}
                                    </div>
                                  )}

                                  {/* Overlay de error */}
                                  {playerClip && playerError && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        inset: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "10px",
                                        color: "rgba(255,255,255,0.7)",
                                        textAlign: "center",
                                        padding: "24px",
                                        zIndex: 2,
                                      }}
                                    >
                                      <i
                                        className="bx bx-error-circle"
                                        style={{
                                          fontSize: "2rem",
                                          color: "#f72d60",
                                        }}
                                      ></i>
                                      <span
                                        style={{
                                          fontSize: "0.9rem",
                                          maxWidth: "440px",
                                        }}
                                      >
                                        {playerError}
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {/* SHORTCUTS POPOVER — fuera del div 16/9 para no ser recortado */}
                                {showShortcutsModal && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      bottom: "60px",
                                      right: "12px",
                                      width: "300px",
                                      backgroundColor: "rgba(14, 14, 18, 0.97)",
                                      border: "1px solid rgba(255,255,255,0.1)",
                                      borderRadius: "10px",
                                      boxShadow: "0 8px 32px rgba(0,0,0,0.7)",
                                      backdropFilter: "blur(12px)",
                                      WebkitBackdropFilter: "blur(12px)",
                                      zIndex: 20,
                                      padding: "12px 16px",
                                      color: "#fff",
                                      animation: "fadeIn 0.15s ease-out",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        borderBottom: "1px solid rgba(255,255,255,0.07)",
                                        paddingBottom: "8px",
                                        marginBottom: "10px",
                                      }}
                                    >
                                      <i
                                        className="bx bxs-keyboard"
                                        style={{
                                          fontSize: "1rem",
                                          color: "var(--color-role-sp, #F72D60)",
                                        }}
                                      />
                                      <span style={{ fontWeight: 600, fontSize: "0.82rem" }}>
                                        Atajos del Player
                                      </span>
                                    </div>
                                    {(() => {
                                      const kb: React.CSSProperties = {
                                        background: "rgba(255,255,255,0.1)",
                                        border: "1px solid rgba(255,255,255,0.18)",
                                        borderRadius: "4px",
                                        padding: "1px 5px",
                                        fontFamily: "monospace",
                                        fontSize: "0.68rem",
                                        color: "#fff",
                                      };
                                      const row = (label: string, keys: React.ReactNode) => (
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            padding: "3px 0",
                                          }}
                                        >
                                          <span style={{ color: "#999", fontSize: "0.72rem" }}>
                                            {label}
                                          </span>
                                          <div style={{ display: "flex", gap: "3px" }}>{keys}</div>
                                        </div>
                                      );
                                      return (
                                        <div
                                          style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "1px",
                                          }}
                                        >
                                          {row("Retroceder (×1 ×2 ×4 ×8)", <kbd style={kb}>J</kbd>)}
                                          {row("Pausa", <kbd style={kb}>K</kbd>)}
                                          {row("Play (×1 ×2 ×4 ×8)", <kbd style={kb}>L</kbd>)}
                                          {row("Play / Pause", <kbd style={kb}>Espacio</kbd>)}
                                          {row(
                                            "±1 frame",
                                            <>
                                              <kbd style={kb}>←</kbd>
                                              <kbd style={kb}>→</kbd>
                                            </>,
                                          )}
                                          {row(
                                            "±5 segundos",
                                            <>
                                              <kbd style={kb}>Shift</kbd>
                                              <span style={{ color: "#555", margin: "0 2px" }}>
                                                +
                                              </span>
                                              <kbd style={kb}>←</kbd>
                                              <kbd style={kb}>→</kbd>
                                            </>,
                                          )}
                                          {row(
                                            "Marcar Entrada / Salida",
                                            <>
                                              <kbd style={kb}>I</kbd>
                                              <kbd style={kb}>O</kbd>
                                            </>,
                                          )}
                                          {row(
                                            "Ir a Entrada / Salida",
                                            <>
                                              <kbd style={kb}>Shift+I</kbd>
                                              <kbd style={kb}>Shift+O</kbd>
                                            </>,
                                          )}
                                          {row("Insertar al corte", <kbd style={kb}>,</kbd>)}
                                          {row(
                                            "Navegar tarjetas ↑↓",
                                            <>
                                              <kbd style={kb}>↑</kbd>
                                              <kbd style={kb}>↓</kbd>
                                            </>,
                                          )}
                                        </div>
                                      );
                                    })()}
                                    <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}}`}</style>
                                  </div>
                                )}

                                {/* Controls Bar */}
                                <div
                                  style={{
                                    background: "rgba(20,20,20,0.9)",
                                    display: "flex",
                                    flexDirection: "column",
                                    borderTop: "1px solid rgba(255,255,255,0.05)",
                                  }}
                                >
                                  {/* Seek bar: click/drag para ir adelante o atrás en el clip */}
                                  <div
                                    onPointerDown={(e) => {
                                      if (!playerClip || !playerProgress.ready) return;
                                      e.currentTarget.setPointerCapture(e.pointerId);
                                      previewSeekFromPointer(e.clientX, e.currentTarget);
                                    }}
                                    onPointerMove={(e) => {
                                      // Solo arrastra mientras el botón sigue presionado.
                                      if (e.buttons !== 1 || !playerClip) return;
                                      if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
                                      previewSeekFromPointer(e.clientX, e.currentTarget);
                                    }}
                                    onPointerUp={(e) => {
                                      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                                        e.currentTarget.releasePointerCapture(e.pointerId);
                                      }
                                      if (!playerClip || !playerProgress.ready) {
                                        cancelPendingSeek();
                                        return;
                                      }
                                      commitSeekFromPointer(e.clientX, e.currentTarget);
                                    }}
                                    onPointerCancel={(e) => {
                                      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                                        e.currentTarget.releasePointerCapture(e.pointerId);
                                      }
                                      cancelPendingSeek();
                                    }}
                                    style={{
                                      position: "relative",
                                      height: "14px",
                                      display: "flex",
                                      alignItems: "center",
                                      padding: "0 16px",
                                      cursor:
                                        playerClip && playerProgress.ready ? "pointer" : "default",
                                      touchAction: "none",
                                    }}
                                  >
                                    {/* Riel */}
                                    <div
                                      style={{
                                        position: "relative",
                                        width: "100%",
                                        height: "4px",
                                        borderRadius: "2px",
                                        background: "rgba(255,255,255,0.15)",
                                      }}
                                    >
                                      {/* Relleno reproducido */}
                                      <div
                                        style={{
                                          position: "absolute",
                                          left: 0,
                                          top: 0,
                                          bottom: 0,
                                          width: `${playerSeekFraction * 100}%`,
                                          borderRadius: "2px",
                                          background: "var(--color-role-logger)",
                                        }}
                                      />
                                      {/* Thumb */}
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: "50%",
                                          left: `${playerSeekFraction * 100}%`,
                                          width: "12px",
                                          height: "12px",
                                          borderRadius: "50%",
                                          background: "#fff",
                                          transform: "translate(-50%, -50%)",
                                          boxShadow: "0 1px 4px rgba(0,0,0,0.5)",
                                          opacity: playerClip && playerProgress.ready ? 1 : 0,
                                          pointerEvents: "none",
                                        }}
                                      />
                                    </div>
                                  </div>

                                  {/* Top row: playback controls + TC + speed */}
                                  <div
                                    style={{
                                      height: "48px",
                                      display: "flex",
                                      alignItems: "center",
                                      padding: "0 16px",
                                      justifyContent: "space-between",
                                      position: "relative",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "12px",
                                        color: "#fff",
                                      }}
                                    >
                                      {/* -5s */}
                                      <i
                                        className="bx bx-rewind"
                                        onClick={() => playerRef.current?.seekBy(-5)}
                                        style={{
                                          fontSize: "1.3rem",
                                          cursor: playerClip ? "pointer" : "default",
                                          color: "#aaa",
                                          opacity: playerClip ? 1 : 0.4,
                                        }}
                                        title="Retroceder 5 segundos"
                                        onMouseEnter={(e) => {
                                          if (playerClip) e.currentTarget.style.color = "#fff";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.color = "#aaa";
                                        }}
                                      />

                                      {/* -1 Frame (1/29.97s) */}
                                      <i
                                        className="bx bx-chevron-left"
                                        onClick={() => playerRef.current?.seekBy(-1 / 29.97)}
                                        style={{
                                          fontSize: "1.4rem",
                                          cursor: playerClip ? "pointer" : "default",
                                          color: "#aaa",
                                          opacity: playerClip ? 1 : 0.4,
                                        }}
                                        title="Retroceder 1 frame (1/29.97s)"
                                        onMouseEnter={(e) => {
                                          if (playerClip) e.currentTarget.style.color = "#fff";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.color = "#aaa";
                                        }}
                                      />

                                      {/* Play / Pause */}
                                      <i
                                        className={`bx ${isPlaying ? "bx-pause" : "bx-play"}`}
                                        onClick={() => playerRef.current?.togglePlay()}
                                        style={{
                                          fontSize: "1.6rem",
                                          cursor: playerClip ? "pointer" : "default",
                                          color: "var(--color-role-logger)",
                                          opacity: playerClip ? 1 : 0.4,
                                        }}
                                        title="Reproducir / Pausar"
                                      />

                                      {/* +1 Frame (1/29.97s) */}
                                      <i
                                        className="bx bx-chevron-right"
                                        onClick={() => playerRef.current?.seekBy(1 / 29.97)}
                                        style={{
                                          fontSize: "1.4rem",
                                          cursor: playerClip ? "pointer" : "default",
                                          color: "#aaa",
                                          opacity: playerClip ? 1 : 0.4,
                                        }}
                                        title="Avanzar 1 frame (1/29.97s)"
                                        onMouseEnter={(e) => {
                                          if (playerClip) e.currentTarget.style.color = "#fff";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.color = "#aaa";
                                        }}
                                      />

                                      {/* +5s */}
                                      <i
                                        className="bx bx-fast-forward"
                                        onClick={() => playerRef.current?.seekBy(5)}
                                        style={{
                                          fontSize: "1.3rem",
                                          cursor: playerClip ? "pointer" : "default",
                                          color: "#aaa",
                                          opacity: playerClip ? 1 : 0.4,
                                        }}
                                        title="Avanzar 5 segundos"
                                        onMouseEnter={(e) => {
                                          if (playerClip) e.currentTarget.style.color = "#fff";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.color = "#aaa";
                                        }}
                                      />
                                    </div>

                                    <div
                                      style={{
                                        position: "absolute",
                                        left: "50%",
                                        top: "50%",
                                        transform: "translate(-50%, -50%)",
                                        pointerEvents: "none",
                                        fontSize: "1.25rem",
                                        fontFamily: "monospace",
                                        color: "#fff",
                                        textShadow: "0 1px 4px rgba(0,0,0,0.5)",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {playerTc || "--:--:--"}
                                    </div>

                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "16px",
                                      }}
                                    >
                                      <i
                                        className="bx bxs-keyboard"
                                        onClick={() => setShowShortcutsModal((prev) => !prev)}
                                        title="Atajos de teclado del player (Teclado)"
                                        style={{
                                          color: showShortcutsModal
                                            ? "var(--color-role-sp, #F72D60)"
                                            : "#aaa",
                                          cursor: "pointer",
                                          fontSize: "1.3rem",
                                          transition: "all 0.2s",
                                          opacity: showShortcutsModal ? 1 : 0.45,
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.color =
                                            "var(--color-role-sp, #F72D60)";
                                          e.currentTarget.style.opacity = "1";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.color = showShortcutsModal
                                            ? "var(--color-role-sp, #F72D60)"
                                            : "#aaa";
                                          e.currentTarget.style.opacity = showShortcutsModal
                                            ? "1"
                                            : "0.45";
                                        }}
                                      ></i>
                                      <i
                                        className="bx bx-fullscreen"
                                        onClick={() => playerRef.current?.requestFullscreen()}
                                        style={{
                                          color: "#aaa",
                                          cursor: "pointer",
                                          fontSize: "1.15rem",
                                          transition: "color 0.2s",
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
                                        onMouseLeave={(e) => (e.currentTarget.style.color = "#aaa")}
                                      ></i>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* IN / OUT bar — NLE style consolidated bar */}
                          {!isMockPlayerCollapsed && (
                            <div
                              style={{
                                height: "40px",
                                display: "flex",
                                alignItems: "center",
                                padding: "0 12px",
                                gap: "10px",
                                background: "rgba(15,15,15,0.95)",
                                borderRadius: "0 0 8px 8px",
                                border: "1px solid rgba(255,255,255,0.05)",
                                borderTop: "none",
                                marginTop: "-1px",
                              }}
                            >
                              {/* Mark IN button */}
                              <button
                                onClick={() => {
                                  if (playerTc) setMarkInTc(playerTc);
                                }}
                                disabled={!playerTc}
                                title="Marcar punto de entrada (I)"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  border: "none",
                                  cursor: playerTc ? "pointer" : "default",
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  background: markInTc
                                    ? "rgba(76,175,80,0.18)"
                                    : "rgba(255,255,255,0.05)",
                                  color: markInTc ? "#4CAF50" : "#888",
                                  transition: "all 0.2s",
                                  opacity: playerTc ? 1 : 0.4,
                                }}
                              >
                                <i
                                  className="bx bx-left-arrow-alt"
                                  style={{ fontSize: "0.95rem" }}
                                ></i>
                                IN
                              </button>

                              {/* IN timecode & jump button */}
                              {markInTc ? (
                                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                  <span
                                    style={{
                                      fontFamily: "monospace",
                                      fontSize: "0.75rem",
                                      color: "#4CAF50",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {markInTc}
                                  </span>
                                  <button
                                    onClick={() => {
                                      const wallMs = parseRelayTcToMs(markInTc, playerClip?.at);
                                      if (wallMs !== null)
                                        playerRef.current?.seekToWallClock(wallMs);
                                    }}
                                    title="Ir al punto de entrada (Shift+I)"
                                    style={{
                                      background: "transparent",
                                      border: "none",
                                      color: "#aaa",
                                      cursor: "pointer",
                                      display: "flex",
                                      padding: "2px",
                                      borderRadius: "3px",
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.color = "#4CAF50")}
                                    onMouseLeave={(e) => (e.currentTarget.style.color = "#aaa")}
                                  >
                                    <i
                                      className="bx bx-redo"
                                      style={{ transform: "scaleX(-1)", fontSize: "0.85rem" }}
                                    ></i>
                                  </button>
                                </div>
                              ) : (
                                <span
                                  style={{
                                    color: "#444",
                                    fontSize: "0.7rem",
                                    fontFamily: "monospace",
                                  }}
                                >
                                  --:--:--
                                </span>
                              )}

                              <div
                                style={{
                                  width: "1px",
                                  height: "16px",
                                  background: "rgba(255,255,255,0.08)",
                                }}
                              />

                              {/* Mark OUT button */}
                              <button
                                onClick={() => {
                                  if (playerTc) setMarkOutTc(playerTc);
                                }}
                                disabled={!playerTc}
                                title="Marcar punto de salida (O)"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  border: "none",
                                  cursor: playerTc ? "pointer" : "default",
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  background: markOutTc
                                    ? "rgba(244,67,54,0.18)"
                                    : "rgba(255,255,255,0.05)",
                                  color: markOutTc ? "#f44336" : "#888",
                                  transition: "all 0.2s",
                                  opacity: playerTc ? 1 : 0.4,
                                }}
                              >
                                OUT
                                <i
                                  className="bx bx-right-arrow-alt"
                                  style={{ fontSize: "0.95rem" }}
                                ></i>
                              </button>

                              {/* OUT timecode & jump button */}
                              {markOutTc ? (
                                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                  <span
                                    style={{
                                      fontFamily: "monospace",
                                      fontSize: "0.75rem",
                                      color: "#f44336",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {markOutTc}
                                  </span>
                                  <button
                                    onClick={() => {
                                      const wallMs = parseRelayTcToMs(markOutTc, playerClip?.at);
                                      if (wallMs !== null)
                                        playerRef.current?.seekToWallClock(wallMs);
                                    }}
                                    title="Ir al punto de salida (Shift+O)"
                                    style={{
                                      background: "transparent",
                                      border: "none",
                                      color: "#aaa",
                                      cursor: "pointer",
                                      display: "flex",
                                      padding: "2px",
                                      borderRadius: "3px",
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.color = "#f44336")}
                                    onMouseLeave={(e) => (e.currentTarget.style.color = "#aaa")}
                                  >
                                    <i className="bx bx-redo" style={{ fontSize: "0.85rem" }}></i>
                                  </button>
                                </div>
                              ) : (
                                <span
                                  style={{
                                    color: "#444",
                                    fontSize: "0.7rem",
                                    fontFamily: "monospace",
                                  }}
                                >
                                  --:--:--
                                </span>
                              )}

                              <div
                                style={{
                                  width: "1px",
                                  height: "16px",
                                  background: "rgba(255,255,255,0.08)",
                                }}
                              />

                              {/* Clear marks */}
                              {(markInTc || markOutTc) && (
                                <button
                                  onClick={() => {
                                    setMarkInTc(null);
                                    setMarkOutTc(null);
                                  }}
                                  title="Limpiar marcas"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    padding: "4px 6px",
                                    borderRadius: "4px",
                                    border: "none",
                                    cursor: "pointer",
                                    background: "rgba(255,255,255,0.04)",
                                    color: "#666",
                                    fontSize: "0.8rem",
                                    transition: "all 0.2s",
                                    marginRight: "8px",
                                  }}
                                  onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
                                  onMouseLeave={(e) => (e.currentTarget.style.color = "#666")}
                                >
                                  <i className="bx bx-x"></i>
                                </button>
                              )}

                              {/* Duration Display */}
                              <span
                                style={{
                                  fontFamily: "monospace",
                                  fontSize: "0.7rem",
                                  color: markInTc && markOutTc ? "#fff" : "#555",
                                  background:
                                    markInTc && markOutTc
                                      ? "rgba(255,255,255,0.04)"
                                      : "transparent",
                                  padding: markInTc && markOutTc ? "2px 8px" : "0",
                                  borderRadius: "4px",
                                }}
                              >
                                DUR:{" "}
                                {(() => {
                                  if (!markInTc || !markOutTc) return "--:--:--;--";
                                  const inMs =
                                    parseRelayTcToMs(markInTc, playerClip?.at || new Date()) || 0;
                                  const outMs =
                                    parseRelayTcToMs(markOutTc, playerClip?.at || new Date()) || 0;
                                  const durMs = Math.abs(outMs - inMs);

                                  const h = Math.floor(durMs / 3600000);
                                  const m = Math.floor((durMs % 3600000) / 60000);
                                  const s = Math.floor((durMs % 60000) / 1000);
                                  const f = Math.floor(((durMs % 1000) / 1000) * 29.97);

                                  const formatH = String(h).padStart(2, "0");
                                  const formatM = String(m).padStart(2, "0");
                                  const formatS = String(s).padStart(2, "0");
                                  const formatF = String(f).padStart(2, "0");

                                  return `${formatH}:${formatM}:${formatS};${formatF}`;
                                })()}
                              </span>

                              {/* Spacer */}
                              <div style={{ flex: 1 }} />

                              {/* Insert at end button */}
                              {markInTc && markOutTc && activeProjectId && (
                                <button
                                  onClick={handleInsertClipToCut}
                                  title="Insertar al final del corte (,)"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "30px",
                                    height: "28px",
                                    borderRadius: "5px",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    cursor: "pointer",
                                    background: sendToCutFlash
                                      ? "rgba(76,175,80,0.25)"
                                      : "rgba(255,255,255,0.06)",
                                    color: sendToCutFlash ? "#4CAF50" : "#aaa",
                                    fontSize: "1.1rem",
                                    transition: "all 0.2s",
                                  }}
                                >
                                  {sendToCutFlash ? (
                                    <i className="bx bx-check"></i>
                                  ) : (
                                    <i className="bx bx-add-to-queue"></i>
                                  )}
                                </button>
                              )}

                              {/* Drag to position — draggable icon button */}
                              {markInTc && markOutTc && activeProjectId && (
                                <div
                                  draggable
                                  onDragStart={(e) => {
                                    const log = activeLogRef;
                                    const parseTcMs = (tc: string): number => {
                                      if (tc.includes("T")) return new Date(tc).getTime();
                                      const parts = tc.split(":").map(Number);
                                      if (parts.length === 4)
                                        return (
                                          (parts[0] * 3600 + parts[1] * 60 + parts[2]) * 1000 +
                                          parts[3] * 40
                                        );
                                      if (parts.length === 3)
                                        return (parts[0] * 3600 + parts[1] * 60 + parts[2]) * 1000;
                                      return 0;
                                    };
                                    const durMs = Math.abs(
                                      parseTcMs(markOutTc!) - parseTcMs(markInTc!),
                                    );
                                    const durS = Math.floor(durMs / 1000) % 60;
                                    const durM = Math.floor(durMs / 60000) % 60;
                                    const durH = Math.floor(durMs / 3600000);
                                    const computedDuration = `${durH > 0 ? `${durH}:` : ""}${durM.toString().padStart(2, "0")}:${durS.toString().padStart(2, "0")}`;

                                    const dragItem = {
                                      id: `log_io_${Date.now()}_${crypto.randomUUID().substring(0, 8)}`,
                                      type: (log?.type || "log") as any,
                                      title: log?.text || "Log",
                                      tc: markInTc,
                                      tcIn: markInTc,
                                      tcOut: markOutTc,
                                      author: log?.author,
                                      tags: log?.tags || [],
                                      rating: log?.rating || 0,
                                      participants: log?.participants || [],
                                      sourceId: selectedStory?.id || "",
                                      storyId: selectedStory?.id || "",
                                      duration: computedDuration,
                                      directorId: log?.directorId,
                                      rundownName:
                                        (log as any)?.rundownName ||
                                        (selectedStory as any)?.rundownName ||
                                        "",
                                      eventName:
                                        (log as any)?.eventName ||
                                        (selectedStory as any)?.eventName ||
                                        "",
                                      storyName:
                                        (log as any)?.storyName || selectedStory?.title || "",
                                    };
                                    e.dataTransfer.setData(
                                      "application/json",
                                      JSON.stringify(dragItem),
                                    );
                                    e.dataTransfer.effectAllowed = "copy";
                                  }}
                                  onDragEnd={() => {
                                    setMarkInTc(null);
                                    setMarkOutTc(null);
                                  }}
                                  title="Arrastrar al corte en la posición deseada"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "30px",
                                    height: "28px",
                                    borderRadius: "5px",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    cursor: "grab",
                                    background: "rgba(255,255,255,0.06)",
                                    color: "#aaa",
                                    fontSize: "1.1rem",
                                    transition: "all 0.2s",
                                  }}
                                >
                                  <i
                                    className="bx bx-grid-vertical"
                                    style={{ fontSize: "1.1rem" }}
                                  ></i>
                                </div>
                              )}
                            </div>
                          )}

                          {(selectedStory.summary ||
                            selectedStory.aiSummary ||
                            selectedStory.producerNotes) && (
                            <div
                              style={{
                                background: "#111",
                                borderRadius: "12px",
                                marginBottom: "12px",
                                border: "1px solid rgba(247, 45, 96, 0.3)",
                                overflow: "hidden",
                              }}
                            >
                              {/* Header to toggle context */}
                              <div
                                style={{
                                  padding: "8px 16px",
                                  background: "rgba(20,20,20,0.9)",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  cursor: "pointer",
                                  borderBottom: isSummaryCollapsed
                                    ? "none"
                                    : "1px solid rgba(255,255,255,0.05)",
                                }}
                                onClick={() => setIsSummaryCollapsed(!isSummaryCollapsed)}
                              >
                                <div
                                  style={{
                                    fontSize: "0.8rem",
                                    color: "var(--color-role-logger)",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  <i className="bx bx-info-circle"></i>
                                  Contexto de la Historia
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    fontSize: "0.8rem",
                                    color: "#aaa",
                                  }}
                                >
                                  {isSummaryCollapsed ? "Mostrar Resumen" : "Ocultar Resumen"}
                                  <i
                                    className={`bx bx-chevron-${isSummaryCollapsed ? "down" : "up"}`}
                                  ></i>
                                </div>
                              </div>

                              {!isSummaryCollapsed && (
                                <div style={{ padding: "16px" }}>
                                  {(selectedStory.summary || selectedStory.aiSummary) && (
                                    <div
                                      style={{
                                        color: "#aaa",
                                        fontSize: "0.9rem",
                                        lineHeight: 1.6,
                                      }}
                                    >
                                      {formatSummary(
                                        selectedStory.summary || selectedStory.aiSummary || "",
                                      )}
                                    </div>
                                  )}
                                  {/* Producer Notes Display */}
                                  {selectedStory.producerNotes && (
                                    <div
                                      style={{
                                        marginTop: "16px",
                                        padding: "12px 16px",
                                        background: `rgba(${isShowrunner ? "245, 158, 11" : "0, 208, 132"}, 0.08)`,
                                        borderRadius: "8px",
                                        border: `1px solid rgba(${isShowrunner ? "245, 158, 11" : "0, 208, 132"}, 0.2)`,
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "0.7rem",
                                          color: ACCENT_COLOR,
                                          fontWeight: 700,
                                          marginBottom: "6px",
                                          textTransform: "uppercase",
                                        }}
                                      >
                                        <i
                                          className="bx bx-note"
                                          style={{ marginRight: "4px" }}
                                        ></i>
                                        {NOTES_LABEL}{" "}
                                        {selectedStory.notesAuthor && (
                                          <span style={{ textTransform: "none" }}>
                                            <span
                                              style={{
                                                fontWeight: 400,
                                                color: "rgba(255,255,255,0.6)",
                                              }}
                                            >
                                              by{" "}
                                            </span>
                                            <span
                                              style={{
                                                fontWeight: 600,
                                                color: ACCENT_COLOR,
                                              }}
                                            >
                                              {selectedStory.notesAuthor}
                                            </span>
                                          </span>
                                        )}
                                      </div>
                                      <div
                                        style={{
                                          color: "#ccc",
                                          fontSize: "0.9rem",
                                          lineHeight: 1.5,
                                          fontStyle: "italic",
                                        }}
                                      >
                                        {selectedStory.producerNotes}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Logs View (Scroll Container) */}
                        <div
                          style={{
                            flex: 1,
                            overflowY: "auto",
                            overflowX: "hidden",
                            width: "100%",
                            paddingBottom: "24px",
                          }}
                        >
                          {/* Logs View */}
                          <>
                            {isLoadingStoryLogs ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: "60px 24px",
                                  gap: "12px",
                                  color: "rgba(255,255,255,0.4)",
                                }}
                              >
                                <div
                                  style={{
                                    width: "28px",
                                    height: "28px",
                                    borderRadius: "50%",
                                    border: "2px solid rgba(255,255,255,0.1)",
                                    borderTopColor: "var(--color-role-sp, #F72D60)",
                                    animation: "spin 0.8s linear infinite",
                                  }}
                                />
                                <span style={{ fontSize: "0.8rem", letterSpacing: "0.5px" }}>
                                  Cargando logs...
                                </span>
                                <style>{`
                                  @keyframes spin {
                                    to { transform: rotate(360deg); }
                                  }
                                `}</style>
                              </div>
                            ) : !selectedStory.logs || selectedStory.logs.length === 0 ? (
                              <div
                                style={{
                                  color: "#555",
                                  textAlign: "center",
                                  padding: "24px",
                                }}
                              >
                                {t("storyproducer.explore.noLogs", "No logs in this story")}
                              </div>
                            ) : (
                              [...selectedStory.logs].sort(compareLogs).map((log) => (
                                <div
                                  key={log.id}
                                  id={`mock-log-${log.id}`}
                                  onClick={() => {
                                    {
                                      // Carga el clip de este log en el STREAM PLAYER de arriba.
                                      handleTimecodeClick(log);
                                      // Pequeño retardo para dejar que el estado se actualice antes de scrollear suavemente
                                      setTimeout(() => {
                                        const el = document.getElementById(`mock-log-${log.id}`);
                                        if (el)
                                          el.scrollIntoView({
                                            behavior: "smooth",
                                            block: "center",
                                          });
                                      }, 50);
                                    }
                                  }}
                                  style={{
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    borderRadius: "8px",
                                    background:
                                      activeLogRef?.id === log.id
                                        ? "rgba(255, 220, 0, 0.03)"
                                        : "transparent",
                                    boxShadow: "none",
                                    padding: activeLogRef?.id === log.id ? "4px" : "0",
                                    transform:
                                      activeLogRef?.id === log.id ? "scale(1.01)" : "scale(1)",
                                    marginBottom: "12px",
                                  }}
                                >
                                  <LogCard
                                    key={log.id}
                                    log={{
                                      ...log,
                                      storyTags: selectedStory.tags,
                                      storyParticipants: selectedStory.participants,
                                      storyRating: selectedStory.rating,
                                      streams: log.streams?.length
                                        ? log.streams
                                        : selectedStory.cameras,
                                      storyDirectorId: selectedStory.directorId,
                                      storyName: selectedStory.title,
                                    }}
                                    participants={participants}
                                    directors={contextDirectors}
                                    accentColor={ACCENT_COLOR}
                                    getCameraName={getCameraName}
                                    getTagColor={getTagColor}
                                    getTagLabel={getTagLabel}
                                    displayAsOtf={displayAsOtf}
                                    onTimecodeClick={handleTimecodeClick}
                                    selectable={isSelectionMode || showAssembly}
                                    selected={isSelected(log.id)}
                                    onNavigateSource={(sourceId, itemId, itemType) => {
                                      handleNavigateToLog(
                                        itemId,
                                        sourceId,
                                        undefined,
                                        itemType || "log",
                                      );
                                    }}
                                    highlightText={highlightLogId === log.id ? highlightText : null}
                                    isHighlighted={
                                      highlightLogId === log.id || activeLogRef?.id === log.id
                                    }
                                    storyId={selectedStory.id}
                                    transcripts={getTranscriptsForLog(log.tc, selectedStory.id)}
                                    expandedTranscriptLogIds={expandedTranscriptLogIds}
                                    toggleTranscriptExpand={toggleTranscriptExpand}
                                    onSaveText={
                                      isReadOnly
                                        ? undefined
                                        : (newText: string) =>
                                            handleInlineSaveLogText(selectedStory.id, log, newText)
                                    }
                                    onDelete={
                                      user?.role === "Superadmin"
                                        ? () => handleDeleteClick("log", log.id, "Log")
                                        : undefined
                                    }
                                    showDragHandle={true}
                                    draggable={true}
                                    onDragStart={(e: any) => {
                                      const dragItem = {
                                        id: log.id,
                                        // Library logs llegan con type === "transcription" (supabase-mapper), pero
                                        // SelectableItem espera "transcript" — normalizamos para que el card en CUT
                                        // se renderice como TRANSCRIPT en lugar de degradarse a LOG.
                                        // Con OTF MODE activo, las transcripciones se promueven a "otf".
                                        ...resolveDragMeta(log.type, log.originType),
                                        title: log.text || "Log",
                                        tc: log.tc,
                                        tcIn:
                                          log.startAt ||
                                          (log.createdAt
                                            ? new Date(log.createdAt).toISOString()
                                            : undefined),
                                        tcOut: log.endAt || undefined,
                                        author: log.author,
                                        tags: log.tags,
                                        cameras: log.cameras,
                                        rating: log.rating,
                                        participants: log.participants,
                                        sourceId: log.sourceId || log.id,
                                        storyId: selectedStory.id,
                                        duration: log.duration || null,
                                        directorId: log.directorId,
                                        storyTags: (log as any).storyTags || [],
                                        storyParticipants: (log as any).storyParticipants || [],
                                        storyRating: (log as any).storyRating || 0,
                                        storyName:
                                          (log as any).storyName || selectedStory?.title || "",
                                        eventName:
                                          (log as any).eventName || selectedEvent?.title || "",
                                        rundownName:
                                          (log as any).rundownName || selectedRundown?.name || "",
                                        entryTags: (log as any).entryTags || [],
                                        streams: (log as any).streams || [],
                                        words: log.words || [],
                                        endAt: (log as any).endAt || "",
                                        storyDirectorId:
                                          selectedStory?.directorId || log.directorId,
                                      };

                                      const itemsToDrag =
                                        isSelected(log.id) && selectedItems.length > 1
                                          ? selectedItems
                                          : [dragItem];

                                      e.dataTransfer.setData(
                                        "application/json",
                                        JSON.stringify(
                                          itemsToDrag.length === 1 ? itemsToDrag[0] : itemsToDrag,
                                        ),
                                      );
                                      if (itemsToDrag.length > 1) {
                                        e.dataTransfer.setData(
                                          "application/x-braindog-multi",
                                          JSON.stringify(itemsToDrag),
                                        );
                                      }
                                    }}
                                    onToggleSelect={() =>
                                      toggleItem({
                                        id: log.id,
                                        // Library logs llegan con type === "transcription" (supabase-mapper), pero
                                        // SelectableItem espera "transcript" — normalizamos para que el card en CUT
                                        // se renderice como TRANSCRIPT en lugar de degradarse a LOG.
                                        // Con OTF MODE activo, las transcripciones se promueven a "otf".
                                        ...resolveDragMeta(log.type, log.originType),
                                        title: log.text || "Log",
                                        tc: log.tc,
                                        tcIn:
                                          log.startAt ||
                                          (log.createdAt
                                            ? new Date(log.createdAt).toISOString()
                                            : undefined),
                                        tcOut: log.endAt || undefined,
                                        author: log.author,
                                        tags: log.tags,
                                        rating: log.rating,
                                        participants: log.participants,
                                        sourceId: log.sourceId || log.id,
                                        storyId: selectedStory.id,
                                        duration: log.tc,
                                        directorId: log.directorId,
                                        storyTags: log.storyTags || selectedStory.tags || [],
                                        storyParticipants:
                                          log.storyParticipants || selectedStory.participants || [],
                                        storyRating: log.storyRating ?? selectedStory.rating ?? 0,
                                        storyName: log.storyName || selectedStory.title || "",
                                        eventName: log.eventName || selectedEvent?.title || "",
                                        rundownName: log.rundownName || selectedRundown?.name || "",
                                        entryTags: log.entryTags || [],
                                        streams: log.streams || selectedStory.cameras || [],
                                        cameras: log.cameras || [],
                                        endAt: log.endAt || "",
                                        words: log.words || [],
                                        storyDirectorId:
                                          selectedStory?.directorId || log.directorId,
                                      })
                                    }
                                  />
                                </div>
                              ))
                            )}
                          </>
                        </div>
                      </div>
                    )}

                    {/* Level: Transcript Context (from TRANS citation click) */}
                    {drillLevel === "transcript-context" && transcriptContext && (
                      <div style={{ maxWidth: "800px", margin: "0 auto" }}>
                        {/* Header */}
                        <div
                          style={{
                            background:
                              "linear-gradient(145deg, rgba(40, 40, 40, 0.95) 0%, rgba(25, 25, 25, 0.95) 100%)",
                            borderRadius: "16px",
                            padding: "20px 24px",
                            marginBottom: "16px",
                            border: "1px solid rgba(99, 102, 241, 0.3)",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                          }}
                        >
                          <i
                            className="bx bx-microphone"
                            style={{ fontSize: "1.5rem", color: "#8B5CF6" }}
                          ></i>
                          <div>
                            <h2
                              style={{
                                margin: 0,
                                color: "#fff",
                                fontSize: "1.1rem",
                              }}
                            >
                              {t("storyproducer.explore.transcriptContext", "Transcript Context")}
                            </h2>
                            <span style={{ fontSize: "0.75rem", color: "#888" }}>
                              {transcriptContext.context.length} segments — cited highlight
                            </span>
                          </div>
                          <button
                            onClick={() => {
                              setDrillLevel("rundowns");
                              setTranscriptContext(null);
                            }}
                            style={{
                              marginLeft: "auto",
                              background: "transparent",
                              border: "1px solid rgba(255,255,255,0.15)",
                              color: "#aaa",
                              padding: "6px 14px",
                              borderRadius: "8px",
                              cursor: "pointer",
                              fontSize: "0.8rem",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <i className="bx bx-arrow-back" style={{ fontSize: "0.9rem" }}></i>
                            Back
                          </button>
                        </div>

                        {/* Segments */}
                        <div
                          ref={tcCtxContainerRef}
                          onMouseDown={(e) => {
                            // Dismiss highlight tooltip when clicking outside text paragraphs
                            if (
                              tcCtxHighlight &&
                              !(e.target as HTMLElement).closest("p[data-seg-id]") &&
                              !(e.target as HTMLElement).closest("[data-highlight-tooltip]")
                            ) {
                              setTcCtxHighlight(null);
                              window.getSelection()?.removeAllRanges();
                            }
                          }}
                          style={{
                            background: "rgba(20, 20, 25, 0.9)",
                            borderRadius: "16px",
                            overflow: "hidden",
                            border: "1px solid rgba(255,255,255,0.06)",
                            position: "relative",
                          }}
                        >
                          {(() => {
                            // Speaker color mapping — cited speaker first, rest by participants list order
                            const speakerColors: Record<string, any> = {};
                            const uniqueSpeakers = new Set<string>();
                            transcriptContext.context.forEach((t) =>
                              uniqueSpeakers.add(t.speaker || UNKNOWN_SPEAKER),
                            );

                            [...uniqueSpeakers].forEach((name) => {
                              const p = participants.find(
                                (p: any) => p.name === name || p.alias === name,
                              );
                              speakerColors[name] = getParticipantColor(
                                p?.metadata?.colorId,
                                p?.metadata?.customColor,
                              );
                            });

                            // Filter segments by selected speakers (empty = all visible)
                            const hasSpeakerFilter = tcSelectedSpeakers.size > 0;
                            const filteredSegments = hasSpeakerFilter
                              ? transcriptContext.context.filter((t) => {
                                  const sp = t.speaker || UNKNOWN_SPEAKER;
                                  return (
                                    t.id === transcriptContext.targetId ||
                                    tcSelectedSpeakers.has(sp)
                                  );
                                })
                              : transcriptContext.context;

                            // Find target index on the filtered list
                            const targetIdx = filteredSegments.findIndex(
                              (t) => t.id === transcriptContext.targetId,
                            );
                            const totalBefore = targetIdx >= 0 ? targetIdx : 0;
                            const totalAfter =
                              targetIdx >= 0 ? filteredSegments.length - targetIdx - 1 : 0;

                            // Build a Set of visible segment IDs for quick lookup
                            const visibleIds = new Set<string>();
                            filteredSegments.forEach((t, idx) => {
                              const isTarget = t.id === transcriptContext.targetId;
                              if (isTarget) {
                                visibleIds.add(t.id);
                                return;
                              }
                              if (idx < targetIdx && targetIdx - idx <= tcExpandBefore)
                                visibleIds.add(t.id);
                              if (idx > targetIdx && idx - targetIdx <= tcExpandAfter)
                                visibleIds.add(t.id);
                            });

                            return (
                              <>
                                {/* Speaker legend — clickable toggles */}
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    flexWrap: "wrap",
                                    padding: "10px 14px",
                                    background: "rgba(255,255,255,0.02)",
                                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                                  }}
                                >
                                  {Object.entries(speakerColors).map(([name, _colorSchema]) => {
                                    const isActive =
                                      tcSelectedSpeakers.size === 0 || tcSelectedSpeakers.has(name);
                                    return (
                                      <ParticipantBadge
                                        key={name}
                                        participant={
                                          participants.find(
                                            (p: any) => p.name === name || p.alias === name,
                                          ) || { id: name, name: name }
                                        }
                                        variant="pill"
                                        size="sm"
                                        isActive={isActive}
                                        onClick={() => {
                                          setTcSelectedSpeakers((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(name)) {
                                              next.delete(name);
                                            } else {
                                              next.add(name);
                                            }
                                            return next;
                                          });
                                          // Reset expand counters when toggling speakers
                                          setTcExpandBefore(5);
                                          setTcExpandAfter(5);
                                        }}
                                      />
                                    );
                                  })}
                                </div>

                                {/* "Show more above" button */}
                                {tcExpandBefore < totalBefore && (
                                  <div
                                    onClick={() =>
                                      setTcExpandBefore((prev) => Math.min(prev + 10, totalBefore))
                                    }
                                    style={{
                                      padding: "8px 14px",
                                      textAlign: "center",
                                      fontSize: "0.72rem",
                                      color: "#8B5CF6",
                                      cursor: "pointer",
                                      background: "rgba(139, 92, 246, 0.06)",
                                      borderBottom: "1px solid rgba(255,255,255,0.04)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: "6px",
                                      userSelect: "none" as const,
                                      transition: "background 0.15s",
                                    }}
                                    onMouseOver={(e) => {
                                      (e.currentTarget as HTMLDivElement).style.background =
                                        "rgba(139, 92, 246, 0.12)";
                                    }}
                                    onMouseOut={(e) => {
                                      (e.currentTarget as HTMLDivElement).style.background =
                                        "rgba(139, 92, 246, 0.06)";
                                    }}
                                  >
                                    <i
                                      className="bx bx-chevron-up"
                                      style={{ fontSize: "1rem" }}
                                    ></i>
                                    {t("storyproducer.showMoreAbove")} (
                                    {totalBefore - tcExpandBefore})
                                  </div>
                                )}

                                {/* Segment cards — filtered by selected speakers + visible range */}
                                {(() => {
                                  // Build visible segments array for index-based operations
                                  const visibleSegments = transcriptContext.context.filter((t) =>
                                    visibleIds.has(t.id),
                                  );

                                  // Calculate selection range for action bar
                                  const selectedSegs = visibleSegments.filter((s) =>
                                    tcCtxSelectedIds.has(s.id),
                                  );
                                  const selRange =
                                    selectedSegs.length > 0
                                      ? (() => {
                                          const toUTC = (ts: string) =>
                                            ts.includes("Z") || ts.includes("+") ? ts : ts + "Z";
                                          let minStart = Infinity,
                                            maxEnd = -Infinity;
                                          let minStartStr = "",
                                            maxEndStr = "";
                                          selectedSegs.forEach((s) => {
                                            const startNorm =
                                              typeof s.startAt === "number"
                                                ? new Date(
                                                    s.startAt > 1e12 ? s.startAt : s.startAt * 1000,
                                                  ).toISOString()
                                                : s.startAt;
                                            const endNorm =
                                              typeof s.endAt === "number"
                                                ? new Date(
                                                    s.endAt > 1e12 ? s.endAt : s.endAt * 1000,
                                                  ).toISOString()
                                                : s.endAt;
                                            try {
                                              const startMs = new Date(toUTC(startNorm)).getTime();
                                              const endMs = new Date(toUTC(endNorm)).getTime();
                                              if (startMs < minStart) {
                                                minStart = startMs;
                                                minStartStr = startNorm;
                                              }
                                              if (endMs > maxEnd) {
                                                maxEnd = endMs;
                                                maxEndStr = endNorm;
                                              }
                                            } catch {}
                                          });
                                          const formatTcLocal = (iso: string) => {
                                            try {
                                              const d = new Date(
                                                iso.includes("Z") || iso.includes("+")
                                                  ? iso
                                                  : iso + "Z",
                                              );
                                              return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}:${String(d.getUTCSeconds()).padStart(2, "0")}`;
                                            } catch {
                                              return iso;
                                            }
                                          };
                                          const durationMs = maxEnd - minStart;
                                          const totalSec = Math.max(
                                            0,
                                            Math.round(durationMs / 1000),
                                          );
                                          const durStr =
                                            totalSec < 60
                                              ? `${totalSec}s`
                                              : `${Math.floor(totalSec / 60)}m ${totalSec % 60}s`;
                                          return {
                                            tcIn: formatTcLocal(minStartStr),
                                            tcOut: formatTcLocal(maxEndStr),
                                            duration: durStr,
                                            text: selectedSegs.map((s) => s.text).join(" "),
                                            speakers: [
                                              ...new Set(
                                                selectedSegs.map(
                                                  (s) => s.speaker || UNKNOWN_SPEAKER,
                                                ),
                                              ),
                                            ],
                                            tcInRaw: minStartStr,
                                            tcOutRaw: maxEndStr,
                                            tcInMs: minStart,
                                            tcOutMs: maxEnd,
                                          };
                                        })()
                                      : null;

                                  // Handle segment click with shift/meta support
                                  const handleTcCtxClick = (
                                    segId: string,
                                    visIdx: number,
                                    e: React.MouseEvent,
                                  ) => {
                                    e.stopPropagation();
                                    if (e.shiftKey && tcCtxLastClickedIdx >= 0) {
                                      const start = Math.min(tcCtxLastClickedIdx, visIdx);
                                      const end = Math.max(tcCtxLastClickedIdx, visIdx);
                                      setTcCtxSelectedIds((prev) => {
                                        const next = new Set(prev);
                                        for (let i = start; i <= end; i++)
                                          next.add(visibleSegments[i].id);
                                        return next;
                                      });
                                    } else if (e.metaKey || e.ctrlKey) {
                                      setTcCtxSelectedIds((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(segId)) next.delete(segId);
                                        else next.add(segId);
                                        return next;
                                      });
                                    } else {
                                      setTcCtxSelectedIds((prev) => {
                                        if (prev.has(segId) && prev.size === 1) return new Set();
                                        return new Set([segId]);
                                      });
                                    }
                                    setTcCtxLastClickedIdx(visIdx);
                                  };

                                  // Build drag data for a segment
                                  const buildDragItem = (t: any) => ({
                                    id: t.id,
                                    type: "transcript" as const,
                                    title: t.text || "",
                                    tc: t.tc,
                                    tcIn:
                                      typeof t.startAt === "number"
                                        ? new Date(
                                            t.startAt > 1e12 ? t.startAt : t.startAt * 1000,
                                          ).toISOString()
                                        : t.startAt,
                                    author: t.speaker || UNKNOWN_SPEAKER,
                                    tags: t.tags || [],
                                    rating: t.rating || 0,
                                    participants: t.participants || [],
                                    sourceId: t.storyId || "",
                                    duration: t.tc,
                                    storyTags: selectedStory?.tags || [],
                                    storyParticipants: selectedStory?.participants || [],
                                    storyRating: selectedStory?.rating || 0,
                                    storyName: selectedStory?.title || "",
                                    eventName: selectedEvent?.title || "",
                                    rundownName: selectedRundown?.name || "",
                                    entryTags: [],
                                    streams: t.streams?.length
                                      ? t.streams
                                      : selectedStory?.cameras || [],
                                    cameras: t.cameras || [],
                                    originType: "transcript",
                                    endAt:
                                      typeof t.endAt === "number"
                                        ? new Date(
                                            t.endAt > 1e12 ? t.endAt : t.endAt * 1000,
                                          ).toISOString()
                                        : t.endAt || "",
                                  });

                                  // Add selected segments to CUT project
                                  const handleAddToCut = () => {
                                    if (!activeProjectId || selectedSegs.length === 0 || !selRange)
                                      return;
                                    const item = {
                                      id: `cut_${Date.now()}_${crypto.randomUUID().substring(0, 8)}`,
                                      type: "transcript" as const,
                                      title:
                                        selRange.text.length > 80
                                          ? selRange.text.substring(0, 80) + "…"
                                          : selRange.text,
                                      selectedText: selRange.text,
                                      tcIn: selRange.tcInRaw,
                                      tcOut: selRange.tcOutRaw,
                                      tcInMs: selRange.tcInMs,
                                      tcOutMs: selRange.tcOutMs,
                                      speakerName: selRange.speakers.join(", "),
                                      sourceSegmentId: selectedSegs[0]?.id,
                                      sourceId: selectedSegs[0]?.storyId || selectedStory?.id || "",
                                      storyId: selectedSegs[0]?.storyId || selectedStory?.id || "",
                                      tc: selRange.tcIn,
                                      duration: selRange.duration,
                                      author: selRange.speakers.join(", "),
                                      participants: selRange.speakers,
                                      tags: selectedStory?.tags || [],
                                      streams: selectedSegs[0]?.streams?.length
                                        ? selectedSegs[0].streams
                                        : selectedStory?.cameras || [],
                                      cameras: selectedSegs[0]?.cameras || [],
                                      rundownName: (selectedStory as any)?.rundownName || "",
                                      eventName: (selectedStory as any)?.eventName || "",
                                      storyName: selectedStory?.title || selectedStory?.name || "",
                                    };
                                    addItemsToProject(activeProjectId, [item]);
                                    setTcCtxAddedFeedback(true);
                                    setTimeout(() => setTcCtxAddedFeedback(false), 1500);
                                    setTcCtxSelectedIds(new Set());
                                  };

                                  return (
                                    <>
                                      {/* Selection hint */}
                                      <div
                                        style={{
                                          padding: "4px 14px",
                                          fontSize: "0.6rem",
                                          color: "#555",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "4px",
                                          borderBottom: "1px solid rgba(255,255,255,0.03)",
                                        }}
                                      >
                                        <i className="bx bx-info-circle"></i>☑ Checkbox para
                                        transcripts · ✎ Subraya texto para logs
                                      </div>

                                      {visibleSegments.map((t, visIdx) => {
                                        const isTarget = t.id === transcriptContext.targetId;
                                        const speaker = t.speaker || UNKNOWN_SPEAKER;
                                        const sColor = speakerColors[speaker];
                                        const isSegSelected = tcCtxSelectedIds.has(t.id);

                                        return (
                                          <div
                                            key={t.id}
                                            data-transcript-id={t.id}
                                            style={{
                                              display: "flex",
                                              gap: "0",
                                              alignItems: "stretch",
                                              borderBottom: "1px solid rgba(255,255,255,0.03)",
                                              background: isSegSelected
                                                ? "rgba(139,92,246,0.15)"
                                                : isTarget
                                                  ? "rgba(99, 102, 241, 0.12)"
                                                  : "transparent",
                                              borderLeft: isTarget
                                                ? "3px solid #8B5CF6"
                                                : "3px solid transparent",
                                              transition: "background 0.15s",
                                              cursor: "pointer",
                                              position: "relative",
                                            }}
                                            onMouseOver={(e) => {
                                              if (!isSegSelected)
                                                (
                                                  e.currentTarget as HTMLDivElement
                                                ).style.background = isTarget
                                                  ? "rgba(139,92,246,0.18)"
                                                  : "rgba(255,255,255,0.04)";
                                            }}
                                            onMouseOut={(e) => {
                                              (e.currentTarget as HTMLDivElement).style.background =
                                                isSegSelected
                                                  ? "rgba(139,92,246,0.15)"
                                                  : isTarget
                                                    ? "rgba(139,92,246,0.12)"
                                                    : "transparent";
                                            }}
                                          >
                                            {/* Checkbox column — always visible, draggable for multi-select */}
                                            <div
                                              draggable
                                              onDragStart={(e) => {
                                                // If this segment is selected, drag all selected; otherwise just this one
                                                const itemsToDrag =
                                                  isSegSelected && tcCtxSelectedIds.size > 1
                                                    ? selectedSegs.map(buildDragItem)
                                                    : [buildDragItem(t)];
                                                e.dataTransfer.setData(
                                                  "application/json",
                                                  JSON.stringify(
                                                    itemsToDrag.length === 1
                                                      ? itemsToDrag[0]
                                                      : itemsToDrag[0],
                                                  ),
                                                );
                                                // For multi-drag, store all items
                                                if (itemsToDrag.length > 1) {
                                                  e.dataTransfer.setData(
                                                    "application/x-braindog-multi",
                                                    JSON.stringify(itemsToDrag),
                                                  );
                                                }
                                                e.dataTransfer.effectAllowed = "copy";
                                              }}
                                              onClick={(e) => handleTcCtxClick(t.id, visIdx, e)}
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                padding: "0 8px",
                                                borderRight: "1px solid rgba(255,255,255,0.05)",
                                                cursor: "grab",
                                                flexShrink: 0,
                                              }}
                                            >
                                              <div
                                                style={{
                                                  width: "16px",
                                                  height: "16px",
                                                  borderRadius: "4px",
                                                  border: isSegSelected
                                                    ? "none"
                                                    : "1.5px solid rgba(255,255,255,0.3)",
                                                  background: isSegSelected
                                                    ? "#8B5CF6"
                                                    : "transparent",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                  transition: "all 0.15s",
                                                }}
                                              >
                                                {isSegSelected && (
                                                  <i
                                                    className="bx bx-check"
                                                    style={{
                                                      color: "#fff",
                                                      fontSize: "0.75rem",
                                                    }}
                                                  ></i>
                                                )}
                                              </div>
                                            </div>

                                            {/* Speaker color bar */}
                                            <div
                                              style={{
                                                width: isSegSelected ? "4px" : "3px",
                                                background: sColor?.bg || "rgba(255,255,255,0.1)",
                                                flexShrink: 0,
                                                opacity: isSegSelected ? 1 : 0.7,
                                                transition: "all 0.15s",
                                              }}
                                            ></div>

                                            {/* Content area — text is selectable for highlight+drag */}
                                            <div
                                              style={{
                                                flex: 1,
                                                padding: "10px 14px",
                                                cursor: "default",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "8px",
                                                  marginBottom: "4px",
                                                  cursor: "pointer",
                                                }}
                                                onClick={(e) => handleTcCtxClick(t.id, visIdx, e)}
                                              >
                                                <span
                                                  style={{
                                                    fontSize: "0.7rem",
                                                    fontWeight: 700,
                                                    color: sColor?.bg || "#fff",
                                                  }}
                                                >
                                                  {speaker}
                                                </span>
                                                <span
                                                  style={{
                                                    fontSize: "0.6rem",
                                                    color: "#666",
                                                    fontFamily: "monospace",
                                                  }}
                                                >
                                                  {t.tc}
                                                </span>
                                                {isTarget && (
                                                  <span
                                                    style={{
                                                      fontSize: "0.55rem",
                                                      background: "#8B5CF6",
                                                      color: "#fff",
                                                      padding: "1px 6px",
                                                      borderRadius: "6px",
                                                      fontWeight: 700,
                                                    }}
                                                  >
                                                    CITED
                                                  </span>
                                                )}
                                                {isSegSelected && (
                                                  <i
                                                    className="bx bx-check-circle"
                                                    style={{
                                                      fontSize: "0.8rem",
                                                      color: "#8B5CF6",
                                                      marginLeft: "auto",
                                                    }}
                                                  ></i>
                                                )}
                                              </div>
                                              <p
                                                data-seg-id={t.id}
                                                data-seg-tc={t.tc}
                                                data-seg-speaker={speaker}
                                                data-seg-story-id={t.storyId || ""}
                                                data-seg-start-at={
                                                  typeof t.startAt === "number"
                                                    ? t.startAt
                                                    : t.startAt
                                                }
                                                data-seg-end-at={
                                                  typeof t.endAt === "number" ? t.endAt : t.endAt
                                                }
                                                onMouseUp={(e) => {
                                                  const sel = window.getSelection();
                                                  const selectedText = sel?.toString().trim();
                                                  if (selectedText && selectedText.length > 0) {
                                                    // Get position relative to the scroll container
                                                    const _rect = (
                                                      e.target as HTMLElement
                                                    ).getBoundingClientRect();
                                                    const containerRect =
                                                      tcCtxContainerRef.current?.getBoundingClientRect();
                                                    if (containerRect) {
                                                      // Position tooltip above the mouse
                                                      const x = e.clientX - containerRect.left;
                                                      const y = e.clientY - containerRect.top - 50;
                                                      setTcCtxHighlight({
                                                        text: selectedText,
                                                        segId: t.id,
                                                        x,
                                                        y,
                                                      });
                                                    }
                                                  }
                                                }}
                                                style={{
                                                  margin: 0,
                                                  fontSize: "0.8rem",
                                                  color: isSegSelected
                                                    ? "#fff"
                                                    : isTarget
                                                      ? "#e0e0e0"
                                                      : "#bbb",
                                                  lineHeight: 1.5,
                                                  fontWeight: isSegSelected
                                                    ? 500
                                                    : isTarget
                                                      ? 600
                                                      : 400,
                                                  cursor: "text",
                                                  userSelect: "text",
                                                  WebkitUserSelect: "text",
                                                }}
                                              >
                                                {t.text}
                                              </p>
                                            </div>
                                          </div>
                                        );
                                      })}

                                      {/* Selection Action Bar — sticky at bottom when segments are selected */}
                                      {selRange && selectedSegs.length > 0 && (
                                        <div
                                          style={{
                                            position: "sticky",
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            background: "rgba(15, 15, 20, 0.97)",
                                            backdropFilter: "blur(20px)",
                                            borderTop: "2px solid #8B5CF6",
                                            padding: "10px 14px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "12px",
                                            zIndex: 10,
                                            animation: "slideUp 0.2s ease-out",
                                          }}
                                        >
                                          {/* Selection info */}
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "8px",
                                              flex: 1,
                                              overflow: "hidden",
                                            }}
                                          >
                                            <div
                                              style={{
                                                background: "#8B5CF6",
                                                color: "#fff",
                                                borderRadius: "50%",
                                                width: "24px",
                                                height: "24px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontWeight: 800,
                                                fontSize: "0.75rem",
                                                flexShrink: 0,
                                              }}
                                            >
                                              {selectedSegs.length}
                                            </div>
                                            <div style={{ overflow: "hidden" }}>
                                              <div
                                                style={{
                                                  fontSize: "0.7rem",
                                                  color: "#aaa",
                                                  display: "flex",
                                                  gap: "8px",
                                                  alignItems: "center",
                                                }}
                                              >
                                                <span
                                                  style={{
                                                    fontFamily: "monospace",
                                                    color: "#8B5CF6",
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {selRange.tcIn} → {selRange.tcOut}
                                                </span>
                                                <span style={{ color: "#555" }}>•</span>
                                                <span style={{ color: "#888" }}>
                                                  {selRange.duration}
                                                </span>
                                                <span style={{ color: "#555" }}>•</span>
                                                <span style={{ color: "#888" }}>
                                                  {selRange.speakers.join(", ")}
                                                </span>
                                              </div>
                                              <div
                                                style={{
                                                  fontSize: "0.65rem",
                                                  color: "#666",
                                                  whiteSpace: "nowrap",
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  maxWidth: "400px",
                                                }}
                                              >
                                                "{selRange.text.substring(0, 100)}
                                                {selRange.text.length > 100 ? "…" : ""}"
                                              </div>
                                            </div>
                                          </div>

                                          {/* Actions */}
                                          <div
                                            style={{
                                              display: "flex",
                                              gap: "6px",
                                              flexShrink: 0,
                                            }}
                                          >
                                            <button
                                              onClick={() => {
                                                setTcCtxSelectedIds(new Set());
                                                setTcCtxLastClickedIdx(-1);
                                              }}
                                              style={{
                                                background: "transparent",
                                                border: "1px solid rgba(255,255,255,0.1)",
                                                color: "#888",
                                                padding: "6px 12px",
                                                borderRadius: "6px",
                                                fontSize: "0.75rem",
                                                cursor: "pointer",
                                                transition: "all 0.2s",
                                              }}
                                              onMouseOver={(e) =>
                                                (e.currentTarget.style.color = "#fff")
                                              }
                                              onMouseOut={(e) =>
                                                (e.currentTarget.style.color = "#888")
                                              }
                                            >
                                              <i className="bx bx-x"></i> {t("discovery.clear")}
                                            </button>
                                            <button
                                              onClick={handleAddToCut}
                                              disabled={!activeProjectId}
                                              style={{
                                                background: tcCtxAddedFeedback
                                                  ? "#10b981"
                                                  : "#8B5CF6",
                                                border: "none",
                                                color: "#fff",
                                                padding: "6px 16px",
                                                borderRadius: "6px",
                                                fontSize: "0.75rem",
                                                fontWeight: 700,
                                                cursor: activeProjectId ? "pointer" : "default",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px",
                                                transition: "all 0.3s",
                                                boxShadow: "0 4px 12px rgba(139, 92, 246, 0.3)",
                                                opacity: activeProjectId ? 1 : 0.5,
                                              }}
                                            >
                                              {tcCtxAddedFeedback ? (
                                                <>
                                                  <i className="bx bx-check"></i>{" "}
                                                  {t("storyproducer.textSelector.added")}
                                                </>
                                              ) : (
                                                <>
                                                  <i className="bx bx-cut"></i>{" "}
                                                  {t("storyproducer.textSelector.addToCut")}
                                                </>
                                              )}
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                      {/* Floating highlight tooltip — appears when text is selected */}
                                      {tcCtxHighlight &&
                                        (() => {
                                          const seg = transcriptContext.context.find(
                                            (s) => s.id === tcCtxHighlight.segId,
                                          );
                                          const handleHighlightAddToCut = () => {
                                            if (!activeProjectId || !seg) return;
                                            const item = {
                                              id: `cut_hl_${Date.now()}_${crypto.randomUUID().substring(0, 8)}`,
                                              type: "transcript" as const,
                                              title:
                                                tcCtxHighlight.text.length > 80
                                                  ? tcCtxHighlight.text.substring(0, 80) + "…"
                                                  : tcCtxHighlight.text,
                                              selectedText: tcCtxHighlight.text,
                                              tc: seg.tc,
                                              tcIn:
                                                typeof seg.startAt === "number"
                                                  ? new Date(
                                                      seg.startAt > 1e12
                                                        ? seg.startAt
                                                        : seg.startAt * 1000,
                                                    ).toISOString()
                                                  : seg.startAt,
                                              tcOut:
                                                typeof seg.endAt === "number"
                                                  ? new Date(
                                                      seg.endAt > 1e12
                                                        ? seg.endAt
                                                        : seg.endAt * 1000,
                                                    ).toISOString()
                                                  : seg.endAt || undefined,
                                              author: seg.speaker || UNKNOWN_SPEAKER,
                                              sourceId: seg.storyId || selectedStory?.id || "",
                                              storyId: seg.storyId || selectedStory?.id || "",
                                              sourceSegmentId: seg.id,
                                              tags: selectedStory?.tags || [],
                                              streams: seg.streams?.length
                                                ? seg.streams
                                                : selectedStory?.cameras || [],
                                              cameras: seg.cameras || [],
                                              rating: 0,
                                              participants: [seg.speaker || UNKNOWN_SPEAKER],
                                              rundownName:
                                                (selectedStory as any)?.rundownName || "",
                                              eventName: (selectedStory as any)?.eventName || "",
                                              storyName:
                                                selectedStory?.title || selectedStory?.name || "",
                                              duration: (() => {
                                                const inTs =
                                                  typeof seg.startAt === "number"
                                                    ? seg.startAt > 1e12
                                                      ? seg.startAt
                                                      : seg.startAt * 1000
                                                    : new Date(seg.startAt).getTime();
                                                const outTs =
                                                  typeof seg.endAt === "number"
                                                    ? seg.endAt > 1e12
                                                      ? seg.endAt
                                                      : seg.endAt * 1000
                                                    : seg.endAt
                                                      ? new Date(seg.endAt).getTime()
                                                      : 0;
                                                if (outTs > inTs) {
                                                  const d = outTs - inTs;
                                                  return `${String(Math.floor(d / 3600000)).padStart(2, "0")}:${String(Math.floor((d % 3600000) / 60000)).padStart(2, "0")}:${String(Math.floor((d % 60000) / 1000)).padStart(2, "0")}`;
                                                }
                                                return "";
                                              })(),
                                            };
                                            addItemsToProject(activeProjectId, [item]);
                                            setTcCtxAddedFeedback(true);
                                            setTimeout(() => setTcCtxAddedFeedback(false), 1500);
                                            setTcCtxHighlight(null);
                                            window.getSelection()?.removeAllRanges();
                                          };

                                          return (
                                            <div
                                              data-highlight-tooltip
                                              draggable
                                              onDragStart={(e) => {
                                                if (!seg) return;
                                                const dragData = {
                                                  id: `cut_hl_${Date.now()}_${crypto.randomUUID().substring(0, 8)}`,
                                                  type: "transcript",
                                                  title:
                                                    tcCtxHighlight.text.length > 80
                                                      ? tcCtxHighlight.text.substring(0, 80) + "…"
                                                      : tcCtxHighlight.text,
                                                  selectedText: tcCtxHighlight.text,
                                                  tc: seg.tc,
                                                  tcIn:
                                                    typeof seg.startAt === "number"
                                                      ? new Date(
                                                          seg.startAt > 1e12
                                                            ? seg.startAt
                                                            : seg.startAt * 1000,
                                                        ).toISOString()
                                                      : seg.startAt,
                                                  tcOut:
                                                    typeof seg.endAt === "number"
                                                      ? new Date(
                                                          seg.endAt > 1e12
                                                            ? seg.endAt
                                                            : seg.endAt * 1000,
                                                        ).toISOString()
                                                      : seg.endAt || undefined,
                                                  author: seg.speaker || UNKNOWN_SPEAKER,
                                                  sourceId: seg.storyId || selectedStory?.id || "",
                                                  storyId: seg.storyId || selectedStory?.id || "",
                                                  sourceSegmentId: seg.id,
                                                  tags: selectedStory?.tags || [],
                                                  entryTags: [],
                                                  storyTags: selectedStory?.tags || [],
                                                  streams: seg.streams?.length
                                                    ? seg.streams
                                                    : selectedStory?.cameras || [],
                                                  cameras: seg.cameras || [],
                                                  rating: 0,
                                                  storyRating: (selectedStory as any)?.rating || 0,
                                                  participants: [seg.speaker || UNKNOWN_SPEAKER],
                                                  duration: "",
                                                  storyParticipants: [],
                                                  storyName: selectedStory?.title || "",
                                                  eventName: selectedEvent?.title || "",
                                                  rundownName: selectedRundown?.name || "",
                                                  originType: "transcript",
                                                  endAt:
                                                    typeof seg.endAt === "number"
                                                      ? new Date(
                                                          seg.endAt > 1e12
                                                            ? seg.endAt
                                                            : seg.endAt * 1000,
                                                        ).toISOString()
                                                      : seg.endAt || "",
                                                };
                                                e.dataTransfer.setData(
                                                  "application/json",
                                                  JSON.stringify(dragData),
                                                );
                                                e.dataTransfer.effectAllowed = "copy";
                                              }}
                                              onDragEnd={() => {
                                                setTcCtxHighlight(null);
                                                window.getSelection()?.removeAllRanges();
                                              }}
                                              style={{
                                                position: "absolute",
                                                left: `${Math.max(10, Math.min(tcCtxHighlight.x - 80, 300))}px`,
                                                top: `${Math.max(10, tcCtxHighlight.y)}px`,
                                                background: "rgba(15, 15, 20, 0.97)",
                                                backdropFilter: "blur(16px)",
                                                border: "1.5px solid #8B5CF6",
                                                borderRadius: "10px",
                                                padding: "6px 10px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                zIndex: 100,
                                                cursor: "grab",
                                                boxShadow:
                                                  "0 8px 32px rgba(139, 92, 246, 0.3), 0 0 0 1px rgba(139, 92, 246, 0.1)",
                                                animation: "fadeIn 0.15s ease-out",
                                                maxWidth: "280px",
                                              }}
                                            >
                                              {/* Drag handle */}
                                              <i
                                                className="bx bx-move"
                                                style={{
                                                  color: "#8B5CF6",
                                                  fontSize: "0.9rem",
                                                  flexShrink: 0,
                                                }}
                                              ></i>
                                              {/* Preview */}
                                              <div
                                                style={{
                                                  flex: 1,
                                                  overflow: "hidden",
                                                  minWidth: 0,
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    fontSize: "0.65rem",
                                                    color: "#8B5CF6",
                                                    fontWeight: 700,
                                                    marginBottom: "2px",
                                                  }}
                                                >
                                                  ✎ Log seleccionado
                                                </div>
                                                <div
                                                  style={{
                                                    fontSize: "0.7rem",
                                                    color: "#bbb",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                  }}
                                                >
                                                  "{tcCtxHighlight.text.substring(0, 60)}
                                                  {tcCtxHighlight.text.length > 60 ? "…" : ""}"
                                                </div>
                                              </div>
                                              {/* Add to CUT button */}
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleHighlightAddToCut();
                                                }}
                                                disabled={!activeProjectId}
                                                style={{
                                                  background: tcCtxAddedFeedback
                                                    ? "#10b981"
                                                    : "#8B5CF6",
                                                  border: "none",
                                                  color: "#fff",
                                                  padding: "4px 10px",
                                                  borderRadius: "6px",
                                                  fontSize: "0.7rem",
                                                  fontWeight: 700,
                                                  cursor: activeProjectId ? "pointer" : "default",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "4px",
                                                  flexShrink: 0,
                                                  transition: "all 0.2s",
                                                  whiteSpace: "nowrap",
                                                }}
                                              >
                                                {tcCtxAddedFeedback ? (
                                                  <>
                                                    <i className="bx bx-check"></i>
                                                  </>
                                                ) : (
                                                  <>
                                                    <i className="bx bx-cut"></i>{" "}
                                                    {t("storyproducer.assembly.cut")}
                                                  </>
                                                )}
                                              </button>
                                              {/* Close button */}
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setTcCtxHighlight(null);
                                                  window.getSelection()?.removeAllRanges();
                                                }}
                                                style={{
                                                  background: "transparent",
                                                  border: "none",
                                                  color: "#555",
                                                  padding: "2px",
                                                  cursor: "pointer",
                                                  fontSize: "0.8rem",
                                                  flexShrink: 0,
                                                }}
                                              >
                                                <i className="bx bx-x"></i>
                                              </button>
                                            </div>
                                          );
                                        })()}
                                    </>
                                  );
                                })()}

                                {/* "Show more below" button */}
                                {tcExpandAfter < totalAfter && (
                                  <div
                                    onClick={() =>
                                      setTcExpandAfter((prev) => Math.min(prev + 10, totalAfter))
                                    }
                                    style={{
                                      padding: "8px 14px",
                                      textAlign: "center",
                                      fontSize: "0.72rem",
                                      color: "#8B5CF6",
                                      cursor: "pointer",
                                      background: "rgba(139, 92, 246, 0.06)",
                                      borderTop: "1px solid rgba(255,255,255,0.04)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: "6px",
                                      userSelect: "none" as const,
                                      transition: "background 0.15s",
                                    }}
                                    onMouseOver={(e) => {
                                      (e.currentTarget as HTMLDivElement).style.background =
                                        "rgba(139, 92, 246, 0.12)";
                                    }}
                                    onMouseOut={(e) => {
                                      (e.currentTarget as HTMLDivElement).style.background =
                                        "rgba(139, 92, 246, 0.06)";
                                    }}
                                  >
                                    <i
                                      className="bx bx-chevron-down"
                                      style={{ fontSize: "1rem" }}
                                    ></i>
                                    {t("storyproducer.showMoreBelow")} ({totalAfter - tcExpandAfter}
                                    )
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* === LIST VIEW MODE === (Original) */}
                {viewMode === "list" && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "16px",
                      paddingTop: "24px",
                      paddingBottom: "100px",
                    }}
                  >
                    {rundowns.length === 0 ? (
                      <div
                        style={{
                          color: "#666",
                          textAlign: "center",
                          padding: "48px",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        {!dataReady ? (
                          <>
                            <i
                              className="bx bx-loader-alt bx-spin"
                              style={{ fontSize: "2rem", color: ACCENT_COLOR }}
                            ></i>
                            <span>{t("common.loading", "CARGANDO...")}</span>
                          </>
                        ) : (
                          t("storyproducer.explore.noRundowns", "No rundowns available")
                        )}
                      </div>
                    ) : (
                      rundowns.map((rundown) => (
                        <div
                          key={rundown.id}
                          style={{
                            background: "rgba(30, 30, 30, 0.5)",
                            borderRadius: "12px",
                            border:
                              expandedRundownId === rundown.id
                                ? "1px solid #f59e0b"
                                : "1px solid rgba(255,255,255,0.08)",
                            overflow: "visible",
                            position: "relative",
                          }}
                        >
                          <div
                            onClick={() =>
                              setExpandedRundownId(
                                expandedRundownId === rundown.id ? null : rundown.id,
                              )
                            }
                            style={{
                              padding: "16px 20px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              background:
                                expandedRundownId === rundown.id
                                  ? "linear-gradient(90deg, rgba(245, 158, 11, 0.1) 0%, transparent 100%)"
                                  : "transparent",
                              borderRadius: "12px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                position: "relative",
                              }}
                            >
                              <i
                                className={`bx ${expandedRundownId === rundown.id ? "bx-chevron-down" : "bx-chevron-right"}`}
                                style={{ fontSize: "1.25rem", color: "#666" }}
                              ></i>

                              {/* Icon with hover */}
                              <div
                                onMouseEnter={() => setHoveredListItemId(rundown.id)}
                                onMouseLeave={() => setHoveredListItemId(null)}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <i
                                  className="bx bx-calendar"
                                  style={{
                                    fontSize: "1.25rem",
                                    color: "#f59e0b",
                                  }}
                                ></i>

                                {/* Tooltip */}
                                {hoveredListItemId === rundown.id && rundown.summary && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "100%",
                                      left: "0",
                                      zIndex: 2000,
                                      background: "rgba(15, 15, 20, 0.98)",
                                      border: "1px solid #f59e0b",
                                      borderRadius: "12px",
                                      padding: "16px",
                                      boxShadow: "0 10px 40px rgba(0,0,0,0.8)",
                                      width: "320px",
                                      maxHeight: "300px",
                                      overflowY: "auto",
                                      pointerEvents: "auto",
                                      marginTop: "0px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "0.7rem",
                                        color: "#f59e0b",
                                        fontWeight: 700,
                                        marginBottom: "8px",
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      Full Summary
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "0.85rem",
                                        color: "#ccc",
                                        lineHeight: 1.6,
                                      }}
                                    >
                                      {formatSummary(rundown.summary)}
                                    </div>
                                  </div>
                                )}
                              </div>

                              <span
                                style={{
                                  fontWeight: 700,
                                  color: "#fff",
                                  fontSize: "1rem",
                                }}
                              >
                                {rundown.name}
                              </span>
                              <span style={{ fontSize: "0.75rem", color: "#666" }}>
                                {rundown.items?.length || 0} events
                              </span>
                            </div>
                          </div>

                          {expandedRundownId === rundown.id && (
                            <div style={{ padding: "0 12px 12px 12px" }}>
                              {rundown.items.map((event) => {
                                const eventStories = getStoriesForEvent(event.id);
                                const isExpanded = expandedEventId === event.id;
                                return (
                                  <div
                                    key={event.id}
                                    style={{
                                      background: "rgba(40, 40, 40, 0.6)",
                                      borderRadius: "8px",
                                      border: isExpanded
                                        ? "1px solid rgba(56, 189, 248, 0.5)"
                                        : "1px solid rgba(255,255,255,0.05)",
                                      marginBottom: "8px",
                                      overflow: "visible",
                                      position: "relative",
                                    }}
                                  >
                                    <div
                                      onClick={() => {
                                        const isExpanding = !isExpanded;
                                        setExpandedEventId(isExpanding ? event.id : null);
                                        if (isExpanding) loadEventStories(event.id);
                                      }}
                                      style={{
                                        padding: "12px 16px",
                                        cursor: "pointer",
                                        display: "flex",
                                        gap: "12px",
                                        alignItems: "center",
                                      }}
                                    >
                                      <i
                                        className={`bx ${isExpanded ? "bx-chevron-down" : "bx-chevron-right"}`}
                                        style={{
                                          fontSize: "1rem",
                                          color: "#666",
                                        }}
                                      ></i>

                                      {/* Event content with hover */}
                                      <div
                                        style={{
                                          flex: 1,
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "10px",
                                          position: "relative",
                                        }}
                                      >
                                        <div
                                          onMouseEnter={() => setHoveredListItemId(event.id)}
                                          onMouseLeave={() => setHoveredListItemId(null)}
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          <i
                                            className="bx bx-layer"
                                            style={{
                                              fontSize: "1.25rem",
                                              color: "#38BDF8",
                                            }}
                                          ></i>

                                          {/* Tooltip */}
                                          {hoveredListItemId === event.id &&
                                            (event.summary || event.producerNotes) && (
                                              <div
                                                style={{
                                                  position: "absolute",
                                                  top: "100%",
                                                  left: "0",
                                                  zIndex: 2000,
                                                  background: "rgba(15, 15, 20, 0.98)",
                                                  border: "1px solid #38BDF8",
                                                  borderRadius: "12px",
                                                  padding: "16px",
                                                  boxShadow: "0 10px 40px rgba(0,0,0,0.8)",
                                                  width: "320px",
                                                  maxHeight: "300px",
                                                  overflowY: "auto",
                                                  pointerEvents: "auto",
                                                  marginTop: "0px",
                                                }}
                                              >
                                                {event.summary && (
                                                  <>
                                                    <div
                                                      style={{
                                                        fontSize: "0.7rem",
                                                        color: "#38BDF8",
                                                        fontWeight: 700,
                                                        marginBottom: "8px",
                                                        textTransform: "uppercase",
                                                      }}
                                                    >
                                                      Full Summary
                                                    </div>
                                                    <div
                                                      style={{
                                                        fontSize: "0.85rem",
                                                        color: "#ccc",
                                                        lineHeight: 1.6,
                                                        marginBottom: event.producerNotes
                                                          ? "12px"
                                                          : "0",
                                                      }}
                                                    >
                                                      {formatSummary(event.summary)}
                                                    </div>
                                                  </>
                                                )}
                                                {event.producerNotes && (
                                                  <>
                                                    <div
                                                      style={{
                                                        fontSize: "0.7rem",
                                                        color: "var(--color-accent-success)",
                                                        fontWeight: 700,
                                                        marginTop: "8px",
                                                        marginBottom: "6px",
                                                        textTransform: "uppercase",
                                                      }}
                                                    >
                                                      <i
                                                        className="bx bx-note"
                                                        style={{
                                                          marginRight: "4px",
                                                        }}
                                                      ></i>{" "}
                                                      {NOTES_LABEL}{" "}
                                                      {event.notesAuthor &&
                                                        `by ${event.notesAuthor}`}
                                                    </div>
                                                    <div
                                                      style={{
                                                        fontSize: "0.85rem",
                                                        color: "#aaa",
                                                        lineHeight: 1.5,
                                                        fontStyle: "italic",
                                                      }}
                                                    >
                                                      {event.producerNotes}
                                                    </div>
                                                  </>
                                                )}
                                              </div>
                                            )}
                                        </div>

                                        <div>
                                          <span
                                            style={{
                                              fontWeight: 600,
                                              color: "#ddd",
                                              fontSize: "0.9rem",
                                            }}
                                          >
                                            {event.title}
                                          </span>
                                        </div>

                                        {/* Edit/Add Notes Button for Event */}
                                        {!isReadOnly && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setEditingEvent(event);
                                            }}
                                            style={{
                                              marginLeft: "auto",
                                              background: "transparent",
                                              border: "none",
                                              color: event.producerNotes ? ACCENT_COLOR : "#666",
                                              cursor: "pointer",
                                              padding: "4px",
                                            }}
                                          >
                                            <i
                                              className="bx bx-note"
                                              style={{ fontSize: "1.2rem" }}
                                            ></i>
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    {isExpanded && (
                                      <div style={{ padding: "0 12px 12px 12px" }}>
                                        {eventStories.length === 0 ? (
                                          <div
                                            style={{
                                              color: "#666",
                                              textAlign: "center",
                                              padding: "16px",
                                              fontSize: "0.85rem",
                                              fontStyle: "italic",
                                            }}
                                          >
                                            {t(
                                              "storyproducer.explore.noStories",
                                              "No stories in this event",
                                            )}
                                          </div>
                                        ) : (
                                          eventStories.map((story) => {
                                            return (
                                              <div
                                                key={story.id}
                                                style={{
                                                  background: "rgba(50, 50, 50, 0.6)",
                                                  borderRadius: "6px",
                                                  marginBottom: "6px",
                                                  border:
                                                    editingStory?.id === story.id
                                                      ? `1px solid ${ACCENT_COLOR}`
                                                      : "1px solid rgba(255,255,255,0.03)",
                                                  overflow: "visible",
                                                  position: "relative",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "12px",
                                                    padding: "10px 14px",
                                                    cursor: "pointer",
                                                  }}
                                                  onClick={() => {
                                                    const isExpanding =
                                                      expandedStoryId !== story.id;
                                                    setExpandedStoryId(
                                                      isExpanding ? story.id : null,
                                                    );
                                                    // SIEMPRE pedir los logs al
                                                    // servidor al expandir (sin
                                                    // caché de sesión).
                                                    if (isExpanding) {
                                                      socketService.emit(
                                                        "get-story-logs",
                                                        { storyId: story.id },
                                                        (logs: LogEntry[]) => {
                                                          setStories((prev) =>
                                                            prev.map((s) =>
                                                              s.id === story.id
                                                                ? { ...s, logs }
                                                                : s,
                                                            ),
                                                          );
                                                        },
                                                      );
                                                    }
                                                    if (isExpanding)
                                                      loadTranscriptsForStory(story.id);
                                                    if (isExpanding) {
                                                      setListStoryContentView((prev) => ({
                                                        ...prev,
                                                        [story.id]: "logs",
                                                      }));
                                                      setListExploreSelectedSpeakers((prev) => ({
                                                        ...prev,
                                                        [story.id]: new Set(),
                                                      }));
                                                      setListExploreExpandCount((prev) => ({
                                                        ...prev,
                                                        [story.id]: 20,
                                                      }));
                                                    }
                                                  }}
                                                >
                                                  <i
                                                    className={`bx ${expandedStoryId === story.id ? "bx-chevron-down" : "bx-chevron-right"}`}
                                                    style={{
                                                      fontSize: "1rem",
                                                      color: "#666",
                                                    }}
                                                  ></i>

                                                  <div
                                                    onMouseEnter={() =>
                                                      setHoveredListItemId(story.id)
                                                    }
                                                    onMouseLeave={() => setHoveredListItemId(null)}
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                    }}
                                                  >
                                                    <i
                                                      className="bx bx-book-open"
                                                      style={{
                                                        fontSize: "1.25rem",
                                                        color: ACCENT_COLOR,
                                                      }}
                                                    ></i>

                                                    {/* Tooltip */}
                                                    {hoveredListItemId === story.id &&
                                                      (story.summary ||
                                                        story.aiSummary ||
                                                        story.producerNotes) && (
                                                        <div
                                                          style={{
                                                            position: "absolute",
                                                            top: "100%",
                                                            left: "0",
                                                            zIndex: 2000,
                                                            background: "rgba(20, 20, 20, 0.98)",
                                                            border: `1px solid ${ACCENT_COLOR}`,
                                                            borderRadius: "12px",
                                                            padding: "16px",
                                                            boxShadow:
                                                              "0 10px 40px rgba(0,0,0,0.8)",
                                                            width: "320px",
                                                            maxHeight: "300px",
                                                            overflowY: "auto",
                                                            pointerEvents: "auto",
                                                            marginTop: "0px",
                                                          }}
                                                        >
                                                          {(story.summary || story.aiSummary) && (
                                                            <>
                                                              <div
                                                                style={{
                                                                  fontSize: "0.7rem",
                                                                  color: ACCENT_COLOR,
                                                                  fontWeight: 700,
                                                                  marginBottom: "8px",
                                                                  textTransform: "uppercase",
                                                                }}
                                                              >
                                                                Full Summary
                                                              </div>
                                                              <div
                                                                style={{
                                                                  fontSize: "0.85rem",
                                                                  color: "#ccc",
                                                                  lineHeight: 1.6,
                                                                  marginBottom: story.producerNotes
                                                                    ? "12px"
                                                                    : "0",
                                                                }}
                                                              >
                                                                {formatSummary(
                                                                  story.summary ||
                                                                    story.aiSummary ||
                                                                    "",
                                                                )}
                                                              </div>
                                                            </>
                                                          )}
                                                          {story.producerNotes && (
                                                            <>
                                                              <div
                                                                style={{
                                                                  fontSize: "0.7rem",
                                                                  color:
                                                                    "var(--color-accent-success)",
                                                                  fontWeight: 700,
                                                                  marginTop: "8px",
                                                                  marginBottom: "6px",
                                                                  textTransform: "uppercase",
                                                                }}
                                                              >
                                                                <i
                                                                  className="bx bx-note"
                                                                  style={{
                                                                    marginRight: "4px",
                                                                  }}
                                                                ></i>{" "}
                                                                {NOTES_LABEL}{" "}
                                                                {story.notesAuthor && (
                                                                  <span
                                                                    style={{
                                                                      textTransform: "none",
                                                                      marginLeft: "4px",
                                                                    }}
                                                                  >
                                                                    <span
                                                                      style={{
                                                                        fontWeight: 400,
                                                                        color:
                                                                          "rgba(255,255,255,0.6)",
                                                                      }}
                                                                    >
                                                                      by{" "}
                                                                    </span>
                                                                    <span
                                                                      style={{
                                                                        fontWeight: 600,
                                                                        color:
                                                                          "var(--color-accent-success)",
                                                                      }}
                                                                    >
                                                                      {story.notesAuthor}
                                                                    </span>
                                                                  </span>
                                                                )}
                                                              </div>
                                                              <div
                                                                style={{
                                                                  fontSize: "0.85rem",
                                                                  color: "#aaa",
                                                                  lineHeight: 1.5,
                                                                  fontStyle: "italic",
                                                                }}
                                                              >
                                                                {story.producerNotes}
                                                              </div>
                                                            </>
                                                          )}
                                                        </div>
                                                      )}
                                                  </div>

                                                  <div style={{ flex: 1 }}>
                                                    <span
                                                      style={{
                                                        fontWeight: 600,
                                                        color: "#ccc",
                                                        fontSize: "0.85rem",
                                                      }}
                                                    >
                                                      {story.title}
                                                    </span>
                                                  </div>

                                                  {/* Add/View Notes Button for Story */}
                                                  {!isReadOnly && (
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditingStory(story);
                                                      }}
                                                      style={{
                                                        background: "transparent",
                                                        border: "none",
                                                        color: story.producerNotes
                                                          ? ACCENT_COLOR
                                                          : "#666",
                                                        cursor: "pointer",
                                                      }}
                                                    >
                                                      <i
                                                        className="bx bx-note"
                                                        style={{
                                                          fontSize: "1.2rem",
                                                        }}
                                                      ></i>
                                                    </button>
                                                  )}
                                                </div>

                                                {/* Inline Content Expansion */}
                                                {expandedStoryId === story.id && (
                                                  <div
                                                    style={{
                                                      padding: "0 12px 12px 36px",
                                                      display: "flex",
                                                      flexDirection: "column",
                                                      gap: "4px",
                                                    }}
                                                  >
                                                    {/* Mini Toggle */}
                                                    {(() => {
                                                      const viewMode =
                                                        listStoryContentView[story.id] || "logs";
                                                      const logCount = story.logs?.length || 0;
                                                      const transCount =
                                                        storyTranscripts[story.id]?.length || 0;
                                                      return (
                                                        <div
                                                          style={{
                                                            display: "flex",
                                                            gap: "0",
                                                            marginBottom: "6px",
                                                            borderRadius: "8px",
                                                            overflow: "hidden",
                                                            border:
                                                              "1px solid rgba(255,255,255,0.06)",
                                                          }}
                                                        >
                                                          <button
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              setListStoryContentView((prev) => ({
                                                                ...prev,
                                                                [story.id]: "logs",
                                                              }));
                                                            }}
                                                            style={{
                                                              flex: 1,
                                                              padding: "6px 10px",
                                                              background:
                                                                viewMode === "logs"
                                                                  ? "rgba(247, 45, 96, 0.12)"
                                                                  : "rgba(255,255,255,0.02)",
                                                              border: "none",
                                                              color:
                                                                viewMode === "logs"
                                                                  ? "var(--color-data-log)"
                                                                  : "#555",
                                                              fontWeight:
                                                                viewMode === "logs" ? 700 : 400,
                                                              fontSize: "0.75rem",
                                                              cursor: "pointer",
                                                              borderBottom:
                                                                viewMode === "logs"
                                                                  ? "2px solid var(--color-data-log)"
                                                                  : "2px solid transparent",
                                                            }}
                                                          >
                                                            <i
                                                              className="bx bx-list-ul"
                                                              style={{
                                                                marginRight: "4px",
                                                              }}
                                                            ></i>
                                                            Logs ({logCount})
                                                          </button>
                                                          <button
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              setListStoryContentView((prev) => ({
                                                                ...prev,
                                                                [story.id]: "transcripts",
                                                              }));
                                                              setListExploreSelectedSpeakers(
                                                                (prev) => ({
                                                                  ...prev,
                                                                  [story.id]: new Set(),
                                                                }),
                                                              );
                                                              setListExploreExpandCount((prev) => ({
                                                                ...prev,
                                                                [story.id]: 20,
                                                              }));
                                                            }}
                                                            style={{
                                                              flex: 1,
                                                              padding: "6px 10px",
                                                              background:
                                                                viewMode === "transcripts"
                                                                  ? "rgba(139, 92, 246, 0.12)"
                                                                  : "rgba(255,255,255,0.02)",
                                                              border: "none",
                                                              color:
                                                                viewMode === "transcripts"
                                                                  ? "#8B5CF6"
                                                                  : "#555",
                                                              fontWeight:
                                                                viewMode === "transcripts"
                                                                  ? 700
                                                                  : 400,
                                                              fontSize: "0.75rem",
                                                              cursor: "pointer",
                                                              borderBottom:
                                                                viewMode === "transcripts"
                                                                  ? "2px solid #8B5CF6"
                                                                  : "2px solid transparent",
                                                            }}
                                                          >
                                                            <i
                                                              className="bx bx-microphone"
                                                              style={{
                                                                marginRight: "4px",
                                                              }}
                                                            ></i>
                                                            Trans ({transCount})
                                                          </button>
                                                        </div>
                                                      );
                                                    })()}

                                                    {/* Logs View */}
                                                    {(listStoryContentView[story.id] || "logs") ===
                                                      "logs" && (
                                                      <>
                                                        {!story.logs || story.logs.length === 0 ? (
                                                          <div
                                                            style={{
                                                              color: "#555",
                                                              fontSize: "0.8rem",
                                                              fontStyle: "italic",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            {t(
                                                              "storyproducer.explore.noLogsAvailable",
                                                              "No logs available",
                                                            )}
                                                          </div>
                                                        ) : (
                                                          [...story.logs]
                                                            .sort(compareLogs)
                                                            .map((log) => (
                                                              <LogCard
                                                                key={log.id}
                                                                log={{
                                                                  ...log,
                                                                  storyTags: story.tags,
                                                                  storyParticipants:
                                                                    story.participants,
                                                                  storyRating: story.rating,
                                                                  streams: log.streams?.length
                                                                    ? log.streams
                                                                    : story.cameras,
                                                                  storyDirectorId: story.directorId,
                                                                  storyName: story.title,
                                                                }}
                                                                participants={participants}
                                                                directors={contextDirectors}
                                                                accentColor={ACCENT_COLOR}
                                                                getCameraName={getCameraName}
                                                                getTagColor={getTagColor}
                                                                getTagLabel={getTagLabel}
                                                                displayAsOtf={displayAsOtf}
                                                                onTimecodeClick={
                                                                  handleTimecodeClick
                                                                }
                                                                selectable={
                                                                  isSelectionMode || showAssembly
                                                                }
                                                                selected={isSelected(log.id)}
                                                                onNavigateSource={(
                                                                  sourceId,
                                                                  itemId,
                                                                  itemType,
                                                                ) => {
                                                                  handleNavigateToLog(
                                                                    itemId,
                                                                    sourceId,
                                                                    undefined,
                                                                    itemType || "log",
                                                                  );
                                                                }}
                                                                highlightText={
                                                                  highlightLogId === log.id
                                                                    ? highlightText
                                                                    : null
                                                                }
                                                                isHighlighted={
                                                                  highlightLogId === log.id
                                                                }
                                                                storyId={story.id}
                                                                transcripts={getTranscriptsForLog(
                                                                  log.tc,
                                                                  story.id,
                                                                )}
                                                                expandedTranscriptLogIds={
                                                                  expandedTranscriptLogIds
                                                                }
                                                                toggleTranscriptExpand={
                                                                  toggleTranscriptExpand
                                                                }
                                                                onSaveText={
                                                                  isReadOnly
                                                                    ? undefined
                                                                    : (newText: string) =>
                                                                        handleInlineSaveLogText(
                                                                          story.id,
                                                                          log,
                                                                          newText,
                                                                        )
                                                                }
                                                                onDelete={
                                                                  user?.role === "Superadmin"
                                                                    ? () =>
                                                                        handleDeleteClick(
                                                                          "log",
                                                                          log.id,
                                                                          "Log",
                                                                        )
                                                                    : undefined
                                                                }
                                                                onToggleSelect={() =>
                                                                  toggleItem({
                                                                    id: log.id,
                                                                    ...resolveDragMeta(
                                                                      log.type,
                                                                      log.originType,
                                                                    ),
                                                                    title: log.text || "Log",
                                                                    tc: log.tc,
                                                                    tcIn:
                                                                      log.startAt ||
                                                                      (log.createdAt
                                                                        ? new Date(
                                                                            log.createdAt,
                                                                          ).toISOString()
                                                                        : undefined),
                                                                    tcOut: log.endAt || undefined,
                                                                    author: log.author,
                                                                    tags: log.tags,
                                                                    rating: log.rating,
                                                                    participants: log.participants,
                                                                    sourceId:
                                                                      log.sourceId || log.id,
                                                                    storyId: story.id,
                                                                    duration: log.tc,
                                                                    directorId: log.directorId,
                                                                    storyTags:
                                                                      log.storyTags ||
                                                                      story.tags ||
                                                                      [],
                                                                    storyParticipants:
                                                                      log.storyParticipants ||
                                                                      story.participants ||
                                                                      [],
                                                                    storyRating:
                                                                      log.storyRating ??
                                                                      story.rating ??
                                                                      0,
                                                                    storyName:
                                                                      log.storyName ||
                                                                      story.title ||
                                                                      "",
                                                                    eventName:
                                                                      log.eventName ||
                                                                      event?.title ||
                                                                      "",
                                                                    rundownName:
                                                                      log.rundownName ||
                                                                      rundown?.name ||
                                                                      "",
                                                                    entryTags: log.entryTags || [],
                                                                    streams:
                                                                      log.streams ||
                                                                      story.cameras ||
                                                                      [],
                                                                    cameras: log.cameras || [],
                                                                    endAt: log.endAt || "",
                                                                    words: log.words || [],
                                                                    storyDirectorId:
                                                                      story?.directorId ||
                                                                      log.directorId,
                                                                  })
                                                                }
                                                                showDragHandle={true}
                                                                draggable={true}
                                                                onDragStart={(e: any) => {
                                                                  const dragItem = {
                                                                    id: log.id,
                                                                    ...resolveDragMeta(
                                                                      log.type,
                                                                      log.originType,
                                                                    ),
                                                                    title: log.text || "Log",
                                                                    tc: log.tc,
                                                                    tcIn:
                                                                      log.startAt ||
                                                                      (log.createdAt
                                                                        ? new Date(
                                                                            log.createdAt,
                                                                          ).toISOString()
                                                                        : undefined),
                                                                    tcOut: log.endAt || undefined,
                                                                    author: log.author,
                                                                    tags: log.tags,
                                                                    rating: log.rating,
                                                                    participants: log.participants,
                                                                    sourceId:
                                                                      log.sourceId || log.id,
                                                                    storyId: story.id,
                                                                    duration: log.duration || null,
                                                                    directorId: log.directorId,
                                                                    storyTags:
                                                                      (log as any).storyTags || [],
                                                                    storyParticipants:
                                                                      (log as any)
                                                                        .storyParticipants || [],
                                                                    storyRating:
                                                                      (log as any).storyRating || 0,
                                                                    storyName:
                                                                      (log as any).storyName ||
                                                                      story?.title ||
                                                                      "",
                                                                    eventName:
                                                                      (log as any).eventName ||
                                                                      event?.title ||
                                                                      "",
                                                                    rundownName:
                                                                      (log as any).rundownName ||
                                                                      rundown?.name ||
                                                                      "",
                                                                    entryTags:
                                                                      (log as any).entryTags || [],
                                                                    cameras: log.cameras || [],
                                                                    streams:
                                                                      (log as any).streams || [],
                                                                    words: log.words || [],
                                                                    endAt: (log as any).endAt || "",
                                                                    storyDirectorId:
                                                                      story?.directorId ||
                                                                      log.directorId,
                                                                  };

                                                                  const itemsToDrag =
                                                                    isSelected(log.id) &&
                                                                    selectedItems.length > 1
                                                                      ? selectedItems
                                                                      : [dragItem];

                                                                  e.dataTransfer.setData(
                                                                    "application/json",
                                                                    JSON.stringify(
                                                                      itemsToDrag.length === 1
                                                                        ? itemsToDrag[0]
                                                                        : itemsToDrag,
                                                                    ),
                                                                  );
                                                                  if (itemsToDrag.length > 1) {
                                                                    e.dataTransfer.setData(
                                                                      "application/x-braindog-multi",
                                                                      JSON.stringify(itemsToDrag),
                                                                    );
                                                                  }
                                                                }}
                                                              />
                                                            ))
                                                        )}
                                                      </>
                                                    )}

                                                    {/* Transcripts View */}
                                                    {listStoryContentView[story.id] ===
                                                      "transcripts" &&
                                                      (() => {
                                                        const allTrans =
                                                          storyTranscripts[story.id] || [];
                                                        if (allTrans.length === 0) {
                                                          return (
                                                            <div
                                                              style={{
                                                                color: "#555",
                                                                fontSize: "0.8rem",
                                                                fontStyle: "italic",
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              {t(
                                                                "storyproducer.explore.noTranscriptsAvailable",
                                                                "No transcripts available",
                                                              )}
                                                            </div>
                                                          );
                                                        }

                                                        const sCols: Record<string, any> = {};
                                                        let cIdx = 0;
                                                        const uSpeakers = new Set<string>();
                                                        allTrans.forEach((t) =>
                                                          uSpeakers.add(
                                                            t.speaker || UNKNOWN_SPEAKER,
                                                          ),
                                                        );
                                                        const pOrder = new Map<string, number>();
                                                        participants.forEach(
                                                          (p: any, idx: number) => {
                                                            if (p.name) pOrder.set(p.name, idx);
                                                            if (p.alias) pOrder.set(p.alias, idx);
                                                          },
                                                        );
                                                        [...uSpeakers]
                                                          .sort(
                                                            (a, b) =>
                                                              (pOrder.get(a) ?? 9999) -
                                                              (pOrder.get(b) ?? 9999),
                                                          )
                                                          .forEach((n) => {
                                                            const p = participants.find(
                                                              (p: any) =>
                                                                p.name === n || p.alias === n,
                                                            );
                                                            sCols[n] = getParticipantColor(
                                                              p?.metadata?.colorId,
                                                              p?.metadata?.customColor,
                                                            );
                                                            cIdx++;
                                                          });

                                                        const selectedSp =
                                                          listExploreSelectedSpeakers[story.id] ||
                                                          new Set();
                                                        const filtered =
                                                          selectedSp.size > 0
                                                            ? allTrans.filter((t) =>
                                                                selectedSp.has(
                                                                  t.speaker || UNKNOWN_SPEAKER,
                                                                ),
                                                              )
                                                            : allTrans;
                                                        const expCount =
                                                          listExploreExpandCount[story.id] || 20;
                                                        const visible = filtered.slice(0, expCount);
                                                        const rem =
                                                          filtered.length - visible.length;

                                                        return (
                                                          <div
                                                            style={{
                                                              background: "rgba(30,30,30,0.5)",
                                                              borderRadius: "8px",
                                                              border:
                                                                "1px solid rgba(139,92,246,0.12)",
                                                              overflow: "hidden",
                                                            }}
                                                          >
                                                            {/* Speaker Tags */}
                                                            <div
                                                              style={{
                                                                display: "flex",
                                                                gap: "4px",
                                                                flexWrap: "wrap",
                                                                padding: "8px 10px",
                                                                borderBottom:
                                                                  "1px solid rgba(255,255,255,0.04)",
                                                              }}
                                                            >
                                                              {Object.entries(sCols).map(
                                                                ([name, _color]) => {
                                                                  const isAct =
                                                                    selectedSp.size === 0 ||
                                                                    selectedSp.has(name);
                                                                  return (
                                                                    <ParticipantBadge
                                                                      key={name}
                                                                      participant={
                                                                        participants.find(
                                                                          (p: any) =>
                                                                            p.name === name ||
                                                                            p.alias === name,
                                                                        ) || {
                                                                          id: name,
                                                                          name: name,
                                                                        }
                                                                      }
                                                                      variant="pill"
                                                                      size="sm"
                                                                      isActive={isAct}
                                                                      onClick={() => {
                                                                        setListExploreSelectedSpeakers(
                                                                          (prev) => {
                                                                            const cur =
                                                                              prev[story.id] ||
                                                                              new Set();
                                                                            const next = new Set(
                                                                              cur,
                                                                            );
                                                                            if (next.has(name))
                                                                              next.delete(name);
                                                                            else next.add(name);
                                                                            return {
                                                                              ...prev,
                                                                              [story.id]: next,
                                                                            };
                                                                          },
                                                                        );
                                                                        setListExploreExpandCount(
                                                                          (prev) => ({
                                                                            ...prev,
                                                                            [story.id]: 20,
                                                                          }),
                                                                        );
                                                                      }}
                                                                    />
                                                                  );
                                                                },
                                                              )}
                                                            </div>

                                                            {/* Segments */}
                                                            {visible.map((t) => {
                                                              const sp =
                                                                t.speaker || UNKNOWN_SPEAKER;
                                                              const sc = sCols[sp];
                                                              return (
                                                                <div
                                                                  key={t.id}
                                                                  draggable={showAssembly}
                                                                  onDragStart={(e) => {
                                                                    e.stopPropagation();
                                                                    const dragData: any = {
                                                                      id: t.id,
                                                                      type: "transcript",
                                                                      title: t.text || "",
                                                                      tc: t.tc,
                                                                      tcIn:
                                                                        typeof t.startAt ===
                                                                        "number"
                                                                          ? new Date(
                                                                              t.startAt > 1e12
                                                                                ? t.startAt
                                                                                : t.startAt * 1000,
                                                                            ).toISOString()
                                                                          : t.startAt,
                                                                      tcOut:
                                                                        typeof t.endAt === "number"
                                                                          ? new Date(
                                                                              t.endAt > 1e12
                                                                                ? t.endAt
                                                                                : t.endAt * 1000,
                                                                            ).toISOString()
                                                                          : t.endAt || null,
                                                                      author: sp,
                                                                      tags: t.tags || [],
                                                                      rating: t.rating || 0,
                                                                      participants:
                                                                        t.participants || [],
                                                                      sourceId: t.sourceId || t.id,
                                                                      duration: t.tc,
                                                                      storyTags: story?.tags || [],
                                                                      storyParticipants:
                                                                        story?.participants || [],
                                                                      storyRating:
                                                                        story?.rating || 0,
                                                                      storyName: story?.title || "",
                                                                      eventName: event?.title || "",
                                                                      rundownName:
                                                                        rundown?.name || "",
                                                                      entryTags: [],
                                                                      streams: t.streams?.length
                                                                        ? t.streams
                                                                        : story?.cameras || [],
                                                                      cameras: t.cameras || [],
                                                                      originType: "transcript",
                                                                      endAt:
                                                                        typeof t.endAt === "number"
                                                                          ? new Date(
                                                                              t.endAt > 1e12
                                                                                ? t.endAt
                                                                                : t.endAt * 1000,
                                                                            ).toISOString()
                                                                          : t.endAt || "",
                                                                    };

                                                                    if (
                                                                      dragData.tcIn &&
                                                                      dragData.tcOut
                                                                    ) {
                                                                      const inTime = new Date(
                                                                        dragData.tcIn,
                                                                      ).getTime();
                                                                      const outTime = new Date(
                                                                        dragData.tcOut,
                                                                      ).getTime();
                                                                      if (
                                                                        !isNaN(inTime) &&
                                                                        !isNaN(outTime)
                                                                      ) {
                                                                        const diffMs =
                                                                          outTime - inTime;
                                                                        const hours = Math.floor(
                                                                          diffMs / 3600000,
                                                                        );
                                                                        const mins = Math.floor(
                                                                          (diffMs % 3600000) /
                                                                            60000,
                                                                        );
                                                                        const secs = Math.floor(
                                                                          (diffMs % 60000) / 1000,
                                                                        );
                                                                        dragData.duration = `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
                                                                      }
                                                                    }

                                                                    const isItemCurrentlySelected =
                                                                      isSelected(t.id);
                                                                    const itemsToDrag =
                                                                      isItemCurrentlySelected &&
                                                                      selectedItems.length > 1
                                                                        ? selectedItems
                                                                        : [dragData as any];

                                                                    e.dataTransfer.setData(
                                                                      "application/json",
                                                                      JSON.stringify(
                                                                        itemsToDrag[0],
                                                                      ),
                                                                    );
                                                                    if (itemsToDrag.length > 1) {
                                                                      e.dataTransfer.setData(
                                                                        "application/x-braindog-multi",
                                                                        JSON.stringify(itemsToDrag),
                                                                      );
                                                                    }
                                                                    e.dataTransfer.effectAllowed =
                                                                      "copy";
                                                                  }}
                                                                  onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (
                                                                      isSelectionMode ||
                                                                      showAssembly
                                                                    ) {
                                                                      toggleItem({
                                                                        id: t.id,
                                                                        type: "transcript",
                                                                        title: t.text || "",
                                                                        tc: t.tc,
                                                                        tcIn:
                                                                          typeof t.startAt ===
                                                                          "number"
                                                                            ? new Date(
                                                                                t.startAt > 1e12
                                                                                  ? t.startAt
                                                                                  : t.startAt *
                                                                                      1000,
                                                                              ).toISOString()
                                                                            : t.startAt,
                                                                        tcOut:
                                                                          typeof t.endAt ===
                                                                          "number"
                                                                            ? new Date(
                                                                                t.endAt > 1e12
                                                                                  ? t.endAt
                                                                                  : t.endAt * 1000,
                                                                              ).toISOString()
                                                                            : t.endAt || undefined,
                                                                        author: sp,
                                                                        tags: t.tags || [],
                                                                        rating: t.rating || 0,
                                                                        participants:
                                                                          t.participants || [],
                                                                        sourceId:
                                                                          t.sourceId || t.id,
                                                                        duration: t.tc,
                                                                        storyTags: story.tags || [],
                                                                        storyParticipants:
                                                                          story.participants || [],
                                                                        storyRating:
                                                                          story.rating || 0,
                                                                        storyName:
                                                                          story.title || "",
                                                                        eventName: "",
                                                                        rundownName: "",
                                                                        entryTags: [],
                                                                        streams: t.streams?.length
                                                                          ? t.streams
                                                                          : story.cameras || [],
                                                                        cameras: t.cameras || [],
                                                                        originType: "transcript",
                                                                        endAt:
                                                                          typeof t.endAt ===
                                                                          "number"
                                                                            ? new Date(
                                                                                t.endAt > 1e12
                                                                                  ? t.endAt
                                                                                  : t.endAt * 1000,
                                                                              ).toISOString()
                                                                            : t.endAt || "",
                                                                      });
                                                                    }
                                                                  }}
                                                                  style={{
                                                                    display: "flex",
                                                                    alignItems: "stretch",
                                                                    borderBottom:
                                                                      "1px solid rgba(255,255,255,0.02)",
                                                                    background: isSelected(t.id)
                                                                      ? "rgba(139,92,246,0.1)"
                                                                      : "transparent",
                                                                    cursor: showAssembly
                                                                      ? "grab"
                                                                      : isSelectionMode
                                                                        ? "pointer"
                                                                        : "default",
                                                                    transition: "background 0.15s",
                                                                  }}
                                                                  onMouseOver={(e) => {
                                                                    (
                                                                      e.currentTarget as HTMLDivElement
                                                                    ).style.background =
                                                                      "rgba(255,255,255,0.03)";
                                                                  }}
                                                                  onMouseOut={(e) => {
                                                                    (
                                                                      e.currentTarget as HTMLDivElement
                                                                    ).style.background = isSelected(
                                                                      t.id,
                                                                    )
                                                                      ? "rgba(139,92,246,0.1)"
                                                                      : "transparent";
                                                                  }}
                                                                >
                                                                  <div
                                                                    style={{
                                                                      width: "3px",
                                                                      flexShrink: 0,
                                                                      background: sc,
                                                                    }}
                                                                  />
                                                                  <div
                                                                    style={{
                                                                      flex: 1,
                                                                      padding: "4px 10px",
                                                                    }}
                                                                  >
                                                                    <div
                                                                      style={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: "5px",
                                                                        marginBottom: "1px",
                                                                      }}
                                                                    >
                                                                      <span
                                                                        style={{
                                                                          fontSize: "0.6rem",
                                                                          color: sc,
                                                                          fontWeight: 700,
                                                                        }}
                                                                      >
                                                                        {sp}
                                                                      </span>
                                                                      {t.tc && (
                                                                        <span
                                                                          style={{
                                                                            fontSize: "0.55rem",
                                                                            color: "#444",
                                                                          }}
                                                                        >
                                                                          {t.tc}
                                                                        </span>
                                                                      )}
                                                                      {(isSelectionMode ||
                                                                        showAssembly) &&
                                                                        isSelected(t.id) && (
                                                                          <i
                                                                            className="bx bx-check-circle"
                                                                            style={{
                                                                              fontSize: "0.7rem",
                                                                              color: "#8B5CF6",
                                                                              marginLeft: "auto",
                                                                            }}
                                                                          ></i>
                                                                        )}
                                                                    </div>
                                                                    <div
                                                                      style={{
                                                                        fontSize: "0.75rem",
                                                                        color: "#bbb",
                                                                        lineHeight: 1.3,
                                                                      }}
                                                                    >
                                                                      {t.text}
                                                                    </div>
                                                                  </div>
                                                                </div>
                                                              );
                                                            })}

                                                            {/* Show more */}
                                                            {rem > 0 && (
                                                              <div
                                                                onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  setListExploreExpandCount(
                                                                    (prev) => ({
                                                                      ...prev,
                                                                      [story.id]: expCount + 20,
                                                                    }),
                                                                  );
                                                                }}
                                                                style={{
                                                                  textAlign: "center",
                                                                  padding: "6px",
                                                                  fontSize: "0.7rem",
                                                                  color: "#8B5CF6",
                                                                  cursor: "pointer",
                                                                  fontWeight: 600,
                                                                  background:
                                                                    "rgba(139,92,246,0.05)",
                                                                }}
                                                              >
                                                                {t("storyproducer.showMoreBelow")} (
                                                                {rem})
                                                              </div>
                                                            )}
                                                          </div>
                                                        );
                                                      })()}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Producer Notes Modal */}
                {editingStory && (
                  <div
                    style={{
                      position: "fixed",
                      inset: 0,
                      background: "rgba(0,0,0,0.7)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1000,
                    }}
                    onClick={() => setEditingStory(null)}
                  >
                    <div
                      style={{
                        background: "var(--color-panel-bg)",
                        borderRadius: "12px",
                        border: "1px solid var(--color-accent-success)",
                        width: "90%",
                        maxWidth: "650px",
                        maxHeight: "85vh",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div style={{ padding: "24px", overflowY: "auto", flex: 1 }}>
                        <h2
                          style={{
                            margin: "0 0 20px 0",
                            color: "#fff",
                            fontSize: "1.1rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <i
                            className="bx bx-note"
                            style={{ color: "var(--color-accent-success)" }}
                          ></i>
                          {NOTES_LABEL}
                        </h2>

                        {/* Story Title (read-only) */}
                        <div style={{ marginBottom: "16px" }}>
                          <label
                            style={{
                              display: "block",
                              color: "#666",
                              fontSize: "0.7rem",
                              marginBottom: "4px",
                              textTransform: "uppercase",
                              letterSpacing: "1px",
                            }}
                          >
                            Story
                          </label>
                          <div
                            style={{
                              color: "#fff",
                              fontSize: "1rem",
                              fontWeight: 600,
                            }}
                          >
                            {editingStory.title}
                          </div>
                        </div>

                        {/* AI Summary (read-only) */}
                        {(editingStory.summary || editingStory.aiSummary) && (
                          <div
                            style={{
                              marginBottom: "20px",
                              padding: "14px",
                              background: `rgba(${isShowrunner ? "245, 158, 11" : "247, 45, 96"}, 0.08)`,
                              borderRadius: "8px",
                              border: `1px solid rgba(${isShowrunner ? "245, 158, 11" : "247, 45, 96"}, 0.2)`,
                            }}
                          >
                            <label
                              style={{
                                display: "block",
                                color: ACCENT_COLOR,
                                fontSize: "0.7rem",
                                marginBottom: "8px",
                                textTransform: "uppercase",
                                letterSpacing: "1px",
                                fontWeight: 700,
                              }}
                            >
                              <i className="bx bx-brain" style={{ marginRight: "4px" }}></i> AI
                              Summary
                            </label>
                            <div
                              style={{
                                color: "#aaa",
                                fontSize: "0.9rem",
                                lineHeight: 1.6,
                              }}
                            >
                              {formatSummary(editingStory.summary || editingStory.aiSummary || "")}
                            </div>
                          </div>
                        )}

                        {/* Producer Notes (editable) */}
                        <div style={{ marginBottom: "20px" }}>
                          <label
                            style={{
                              display: "block",
                              color: ACCENT_COLOR,
                              fontSize: "0.7rem",
                              marginBottom: "8px",
                              textTransform: "uppercase",
                              letterSpacing: "1px",
                              fontWeight: 700,
                            }}
                          >
                            <i className="bx bx-edit" style={{ marginRight: "4px" }}></i> Shared
                            Notes
                          </label>
                          {editingStory.producerNotes && (
                            <div
                              style={{
                                marginBottom: "16px",
                                padding: "12px",
                                background: "rgba(255,255,255,0.05)",
                                borderRadius: "8px",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "0.75rem",
                                  color: "#888",
                                  marginBottom: "8px",
                                }}
                              >
                                <strong style={{ color: "#ccc" }}>Legacy Notes</strong> (
                                {editingStory.notesAuthor || UNKNOWN_SPEAKER})
                              </div>
                              <div
                                style={{
                                  color: "#ddd",
                                  whiteSpace: "pre-wrap",
                                  fontSize: "0.9rem",
                                }}
                              >
                                {editingStory.producerNotes}
                              </div>
                            </div>
                          )}
                          <SharedNotesEditor
                            notes={editingStory.sharedNotes || []}
                            onChange={(newNotes) =>
                              setEditingStory({
                                ...editingStory,
                                sharedNotes: newNotes,
                              })
                            }
                            currentUser={
                              user
                                ? {
                                    id: user.id,
                                    name: user.name || "Unknown",
                                    role: user.role || "Producer",
                                  }
                                : null
                            }
                            accentColor={ACCENT_COLOR}
                          />
                        </div>

                        <div
                          style={{
                            display: "flex",
                            gap: "12px",
                            justifyContent: "flex-end",
                            marginBottom: "24px",
                          }}
                        >
                          <button
                            onClick={() => setEditingStory(null)}
                            style={{
                              padding: "10px 20px",
                              background: "transparent",
                              border: "1px solid #444",
                              borderRadius: "6px",
                              color: "#888",
                              cursor: "pointer",
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleUpdateStory(editingStory)}
                            style={{
                              padding: "10px 20px",
                              background: ACCENT_COLOR,
                              border: "none",
                              borderRadius: "6px",
                              color: "#000",
                              cursor: "pointer",
                              fontWeight: 600,
                            }}
                          >
                            <i className="bx bx-check"></i> Save Notes
                          </button>
                        </div>

                        {/* Logs List in Modal */}
                        {editingStory.logs && editingStory.logs.length > 0 && (
                          <div
                            style={{
                              marginBottom: "24px",
                              borderTop: "1px solid rgba(255,255,255,0.1)",
                              paddingTop: "20px",
                            }}
                          >
                            <h3
                              style={{
                                color: "#888",
                                fontSize: "0.8rem",
                                marginBottom: "12px",
                                textTransform: "uppercase",
                                letterSpacing: "1px",
                              }}
                            >
                              <i className="bx bx-list-ul" style={{ marginRight: "8px" }}></i>
                              Logs ({editingStory.logs.length})
                            </h3>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "8px",
                              }}
                            >
                              {[...editingStory.logs]
                                .sort((a: any, b: any) => a.tc.localeCompare(b.tc))
                                .map((log) => (
                                  <LogCard
                                    key={log.id}
                                    log={{
                                      ...log,
                                      storyTags: editingStory.tags,
                                      storyParticipants: editingStory.participants,
                                      storyRating: editingStory.rating,
                                      streams: log.streams?.length
                                        ? log.streams
                                        : editingStory.cameras,
                                      storyDirectorId: editingStory.directorId,
                                      storyName: editingStory.title,
                                    }}
                                    participants={participants}
                                    directors={contextDirectors}
                                    accentColor={ACCENT_COLOR}
                                    getCameraName={getCameraName}
                                    getTagColor={getTagColor}
                                    getTagLabel={getTagLabel}
                                    selectable={false}
                                    selected={false}
                                    onNavigateSource={(sourceId, itemId, itemType) => {
                                      handleNavigateToLog(
                                        itemId,
                                        sourceId,
                                        undefined,
                                        itemType || "log",
                                      );
                                    }}
                                    highlightText={highlightLogId === log.id ? highlightText : null}
                                    isHighlighted={highlightLogId === log.id}
                                    storyId={editingStory.id}
                                    transcripts={getTranscriptsForLog(log.tc, editingStory.id)}
                                    expandedTranscriptLogIds={expandedTranscriptLogIds}
                                    toggleTranscriptExpand={toggleTranscriptExpand}
                                    onSaveText={
                                      isReadOnly
                                        ? undefined
                                        : (newText: string) =>
                                            handleInlineSaveLogText(editingStory.id, log, newText)
                                    }
                                    onDelete={
                                      user?.role === "Superadmin"
                                        ? () => handleDeleteClick("log", log.id, "Log")
                                        : undefined
                                    }
                                    onToggleSelect={() =>
                                      toggleItem({
                                        id: log.id,
                                        ...resolveDragMeta(log.type, log.originType),
                                        title: log.text || "Log",
                                        sourceId: log.sourceId || log.id,
                                        storyId: editingStory.id,
                                        duration: log.tc,
                                        tc: log.tc,
                                        author: log.author,
                                        tags: log.tags,
                                        rating: log.rating,
                                        participants: log.participants,
                                      })
                                    }
                                  />
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Event Notes Modal */}
                {editingEvent && (
                  <div
                    style={{
                      position: "fixed",
                      inset: 0,
                      background: "rgba(0,0,0,0.7)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1000,
                    }}
                    onClick={() => setEditingEvent(null)}
                  >
                    <div
                      style={{
                        background: "var(--color-panel-bg)",
                        borderRadius: "12px",
                        border: `1px solid ${ACCENT_COLOR}`,
                        padding: "24px",
                        width: "90%",
                        maxWidth: "650px",
                        maxHeight: "85vh",
                        overflow: "auto",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <h2
                        style={{
                          margin: "0 0 20px 0",
                          color: "#fff",
                          fontSize: "1.1rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <i className="bx bx-note" style={{ color: ACCENT_COLOR }}></i>
                        {t("storyproducer.explore.eventWord", "Event")} {NOTES_LABEL}
                      </h2>

                      {/* Event Title (read-only) */}
                      <div style={{ marginBottom: "16px" }}>
                        <label
                          style={{
                            display: "block",
                            color: "#666",
                            fontSize: "0.7rem",
                            marginBottom: "4px",
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                          }}
                        >
                          Event
                        </label>
                        <div
                          style={{
                            color: "#fff",
                            fontSize: "1rem",
                            fontWeight: 600,
                          }}
                        >
                          {editingEvent.title}
                        </div>
                      </div>

                      {/* Event Summary (read-only) */}
                      {editingEvent.summary && (
                        <div
                          style={{
                            marginBottom: "20px",
                            padding: "14px",
                            background: `rgba(${isShowrunner ? "245, 158, 11" : "247, 45, 96"}, 0.08)`,
                            borderRadius: "8px",
                            border: `1px solid rgba(${isShowrunner ? "245, 158, 11" : "247, 45, 96"}, 0.2)`,
                          }}
                        >
                          <label
                            style={{
                              display: "block",
                              color: ACCENT_COLOR,
                              fontSize: "0.7rem",
                              marginBottom: "8px",
                              textTransform: "uppercase",
                              letterSpacing: "1px",
                              fontWeight: 700,
                            }}
                          >
                            <i className="bx bx-layer" style={{ marginRight: "4px" }}></i> Summary
                          </label>
                          <div
                            style={{
                              color: "#aaa",
                              fontSize: "0.9rem",
                              lineHeight: 1.6,
                            }}
                          >
                            {formatSummary(editingEvent.summary)}
                          </div>
                        </div>
                      )}

                      {/* Producer Notes (editable) */}
                      <div style={{ marginBottom: "20px" }}>
                        <label
                          style={{
                            display: "block",
                            color: ACCENT_COLOR,
                            fontSize: "0.7rem",
                            marginBottom: "8px",
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                            fontWeight: 700,
                          }}
                        >
                          <i className="bx bx-edit" style={{ marginRight: "4px" }}></i> Shared Notes
                        </label>
                        {editingEvent.producerNotes && (
                          <div
                            style={{
                              marginBottom: "16px",
                              padding: "12px",
                              background: "rgba(255,255,255,0.05)",
                              borderRadius: "8px",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "0.75rem",
                                color: "#888",
                                marginBottom: "8px",
                              }}
                            >
                              <strong style={{ color: "#ccc" }}>Legacy Notes</strong> (
                              {editingEvent.notesAuthor || UNKNOWN_SPEAKER})
                            </div>
                            <div
                              style={{
                                color: "#ddd",
                                whiteSpace: "pre-wrap",
                                fontSize: "0.9rem",
                              }}
                            >
                              {editingEvent.producerNotes}
                            </div>
                          </div>
                        )}
                        <SharedNotesEditor
                          notes={editingEvent.sharedNotes || []}
                          onChange={(newNotes) =>
                            setEditingEvent({
                              ...editingEvent,
                              sharedNotes: newNotes,
                            })
                          }
                          currentUser={
                            user
                              ? {
                                  id: user.id,
                                  name: user.name || "Unknown",
                                  role: user.role || "Producer",
                                }
                              : null
                          }
                          accentColor={ACCENT_COLOR}
                        />
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          justifyContent: "flex-end",
                          marginBottom: "8px",
                        }}
                      >
                        <button
                          onClick={() => setEditingEvent(null)}
                          style={{
                            padding: "10px 20px",
                            background: "transparent",
                            border: "1px solid #444",
                            borderRadius: "6px",
                            color: "#888",
                            cursor: "pointer",
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleUpdateEvent(editingEvent)}
                          style={{
                            padding: "10px 20px",
                            background: ACCENT_COLOR,
                            border: "none",
                            borderRadius: "6px",
                            color: "#000",
                            cursor: "pointer",
                            fontWeight: 600,
                          }}
                        >
                          <i className="bx bx-check"></i> Save Notes
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* End of Main Content (Explorar) */}
              </main>
            </>
          )}
        </div>

        {/* Panel RIGHT: ENSAMBLAJE (colapsable, flex responsive) */}
        {!isEmbedded && (
          <div
            data-testid="explore-assembly-panel"
            onMouseEnter={() => setActiveFocus("program")}
            style={{
              flex: showAssembly ? 1 : 0,
              width: showAssembly ? "auto" : 0,
              minWidth: showAssembly ? "380px" : 0,
              height: "100%",
              overflow: "hidden",
              background: "var(--color-panel-bg)",
              border: showAssembly ? "1px solid var(--color-panel-border)" : "none",
              borderRadius: "var(--border-radius-lg)",
              display: "flex",
              flexDirection: "column",
              opacity: showAssembly ? 1 : 0,
              transition: "flex 0.3s ease, opacity 0.3s ease, width 0.3s ease",
              pointerEvents: showAssembly ? "auto" : "none",
            }}
          >
            {showAssembly && (
              <AssemblyManager
                ref={assemblyRef}
                onVtrPlayStarted={handleVtrPlayStarted}
                showOnlyApproved={assemblyFilter === "approved"}
                isOnlyCutMode={
                  showAssembly &&
                  !showExplore &&
                  !showDiscover &&
                  !showPulse &&
                  !showSummaries &&
                  !showFeed
                }
                readOnly={
                  assemblyFilter === "approved" &&
                  ![
                    "Showrunner",
                    "Content Director",
                    "Content Controller",
                    "Admin",
                    "Superadmin",
                  ].includes(user?.role ?? "")
                }
              />
            )}
          </div>
        )}
      </div>

      <DeleteCascadeModal
        isOpen={deleteCascadeModal.isOpen}
        onClose={() => setDeleteCascadeModal((p) => ({ ...p, isOpen: false }))}
        onConfirm={handleDeleteConfirm}
        type={deleteCascadeModal.type}
        name={deleteCascadeModal.name}
        counts={deleteCascadeModal.counts}
        isLoading={deleteCascadeModal.isLoading}
        isDeleting={deleteCascadeModal.isDeleting}
      />
      <CustomDialog
        isOpen={!!globalAlert?.isOpen}
        type="alert"
        title={globalAlert?.title || t("discovery.attention")}
        message={globalAlert?.message || ""}
        onConfirm={() => setGlobalAlert(null)}
        onCancel={() => setGlobalAlert(null)}
      />
      <ExportMarkersDialog
        open={isExportMarkersOpen}
        projectId={activeProject?.id ?? null}
        projectName={activeProject?.name ?? ""}
        onClose={() => setIsExportMarkersOpen(false)}
      />
      {/* SUMMARIES FULL-SCREEN OVERLAY */}
      {showSummaries && activeProject?.id && !isEmbedded && (
        <div
          data-testid="sp-summaries-fullscreen"
          style={{
            position: "fixed",
            top: 88,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 90,
            background: "#000",
            overflow: "auto",
            padding: "24px 28px",
          }}
        >
          <SummaryFullView projectId={activeProject.id} accentColor={ACCENT_COLOR} />
        </div>
      )}
    </div>
  );
};

export default StoryProducer;
